import { __decorate, __metadata, __param } from 'tslib';
import { injectable, inject, named, tagged, multiInject, optional, Container } from 'inversify';
import { openDB } from 'idb';
import 'reflect-metadata';
import { isBefore, addSeconds } from 'date-fns';
import { WebStorageStateStore, UserManager } from 'oidc-client-ts';
import i18next from 'i18next';
import * as signalR from '@microsoft/signalr';
import { init } from '@elastic/apm-rum';
import axios from 'axios';

/**
 * Gets an object containing the parts of a given URL.
 */
function extractUrlParts(url) {
    const a = document.createElement('a');
    a.href = url;
    return {
        url: a.href,
        host: a.host,
        hostname: a.hostname,
        port: a.port,
        pathname: a.pathname,
        protocol: a.protocol,
        hash: a.hash,
        search: a.search,
        // NOTE in IE the anchor gives undefined on origin, so we use a custom method
        origin: extractUrlOrigin(url)
    };
}
// https://stackoverflow.com/a/1420902/2174621
/**
 * Extracts the origin from a given URL.
 * @param url
 */
function extractUrlOrigin(url) {
    if (url == null || url.length == 0)
        return null;
    const pathArray = url.split('/');
    const protocol = pathArray[0];
    const host = pathArray[2];
    return protocol + '//' + host;
}
/**
 * Checks whether a given URL is absolute.
 * @param url
 */
// https://stackoverflow.com/questions/10687099/how-to-test-if-a-url-string-is-absolute-or-relative
function checkUrlAbsolute(url) {
    return new RegExp('^(?:[a-z]+:)?//', 'i').test(url);
}
// https://stackoverflow.com/a/901144/2174621
/**
 * Gets a query string value.
 * @param name Query string key name.
 * @param url Optional URL.
 */
function parseQueryStringValue(name, url = null) {
    if (url == null)
        url = window.location.href;
    name = name.replace(/[\[\]]/g, '\\$&');
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'), results = regex.exec(url);
    if (!results)
        return null;
    if (!results[2])
        return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
}
/**
 * Resolves an url from the origin (domain) of the application.
 * @param url
 */
function resolveUrlFromOrigin(url) {
    return checkUrlAbsolute(url) ? url : document.location.origin + url;
}
/**
 * Opens an URI.
 * @param uri
 */
function openUrl(url, target = null) {
    const a = document.createElement('a');
    a.href = url;
    if (target != null)
        a.target = target;
    document.body.appendChild(a);
    a.click();
    a.parentNode.removeChild(a);
}
/** Checks whether we are in localhost modus. */
function checkLocalhost() {
    return window.location.hostname === 'localhost';
}
function checkSsl() {
    return window.location.protocol === 'https:';
}
/** Concats different pieces of an url with slashes. */
function concatUrl(...urlSegments) {
    let result = null;
    if (urlSegments && urlSegments.length) {
        result = '';
        urlSegments.forEach((urlSegment) => {
            if (urlSegment && urlSegment.length) {
                if (result.length && !result.endsWith('/') && !urlSegment.startsWith('/'))
                    result += '/';
                else if (result.endsWith('/') && urlSegment.startsWith('/'))
                    result = result.substring(0, result.length - 1);
                result += urlSegment;
            }
        });
    }
    return result;
}
function _checkCompatibleOrigin(url, compatibleOrigins) {
    if (compatibleOrigins?.length) {
        const origin = new URL(url).origin.toLowerCase();
        return compatibleOrigins.some((compatibleOrigin) => {
            let value = compatibleOrigin.value.toLowerCase();
            if (value.endsWith('/'))
                value = value.substring(0, value.length - 1);
            if (compatibleOrigin.strategy === 'exact' && origin === value) {
                return true;
            }
            else if (compatibleOrigin.strategy === 'startsWith' && origin.startsWith(value)) {
                return true;
            }
        });
    }
    return false;
}
/** Clears query string params from a given url. */
function clearQueryStringParams(uri, condition) {
    const url = new URL(uri);
    const keysToDelete = [];
    // Collect the keys that start with 'idp-' in an array
    url.searchParams.forEach((value, key) => {
        if (condition(key))
            keysToDelete.push(key);
    });
    // Delete the keys after collecting them
    keysToDelete.forEach((key) => url.searchParams.delete(key));
    return url.toString();
}

/** Public types. */
const BAAS_TYPES = {
    AxiosInstance: Symbol('AxiosInstance'),
    BaasAuthStrategy: Symbol('BaasAuthStrategy'),
    BaasContextProvider: Symbol('BaasContextProvider'),
    BaasLogOffHook: Symbol('BaasLogOffHook'),
    BaasLoggingApiClient: Symbol('BaasLoggingApiClient'),
    BaasMeldingApiClient: Symbol('BaasMeldingApiClient'),
    BaasSecurityApiClient: Symbol('BaasSecurityApiClient'),
    BaasAuditApiClient: Symbol('BaasAuditApiClient'),
    BaasConfigurationApiClient: Symbol('BaasConfigurationApiClient'),
    BaasMeetpuntApiClient: Symbol('BaasMeetpuntApiClient'),
    BaasCommonApiClient: Symbol('BaasCommonApiClient'),
    BarGvkRemoteStoreProvider: Symbol('BarGvkRemoteStoreProvider'),
    BarErrorDelegate: Symbol('BarErrorDelegate'),
    BaasTelemetry: Symbol('BaasTelemetry'),
    /** @internal @experimental Symbol for future LV OIDC auth service */
    _BaasLvOicdAuthService: Symbol('BaasLvOicdAuthService')
};
const BAAS_AXIOS_INSTANCE_TYPES = {
    /** Default axios instance with interception. */
    Baas: Symbol('Baas'),
    _BaasSilent: Symbol('BaasSilent'),
    /** Plain axios instance (barebone). */
    Plain: Symbol('Plain')
};

/** Reads a cookie. */
function readCookie(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ')
            c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0)
            return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
    return null;
}
/** Deletes all cookies which can be deleted. */
function deleteAllCookies() {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i];
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
        deleteCookie(name);
    }
}
/** Deletes a cookie. */
function deleteCookie(name) {
    document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
}

function checkDateObject(value) {
    return Object.prototype.toString.call(value) === '[object Date]';
}
// NOTE alternatief voor toISOString() omdat hierbij telkens een convertie gedaan wordt naar UTC + 0.
/** Converts a given date as it to an ISO string without converting to UTC +0. */
function convertToISOString(value) {
    if (!value)
        return value;
    return new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate(), value.getHours(), value.getMinutes(), value.getSeconds(), value.getMilliseconds())).toISOString();
}

/** Checks whether a given element has a given parent based on a selector. */
function checkHasParentDomElement(element, parentSelector) {
    while (element) {
        if (element.matches(parentSelector)) {
            return true;
        }
        element = element.parentElement;
    }
    return false;
}

/**
 * Encode non-ASCII characters to unicode escape sequences.
 * @param input The string to encode
 * @returns The encoded string with unicode escape sequences
 */
function asciiEncode(input) {
    return input.replace(/[\u0080-\uFFFF]/g, (char) => {
        return `\\u${char.charCodeAt(0).toString(16).padStart(4, '0')}`;
    });
}
/**
 * Encode header values for BaaS backend compatibility.
 * Replaces single quotes with unicode escapes and encodes non-ASCII characters.
 * @param headers The headers object to encode
 * @returns A new headers object with encoded values
 */
function encodeHeaders(headers) {
    const encoded = {};
    for (const key in headers) {
        // -- Make it valid JSON (if not already) --
        let value = headers[key];
        // Baas backend does not even allow single quotes in values (bug). Make sure to replace them with unicode encoded in case they would still occur here.
        value = value.replace(/'/g, '\\u0027');
        // HTTP headers need to encode non-ASCII (name van user kan bijvoorbeeld een accent hebben)
        value = asciiEncode(value);
        encoded[key] = value;
    }
    return encoded;
}

/** Deletes all indexed db databases. */
function deleteAllIndexedDb() {
    return new Promise((resolve, reject) => {
        window.indexedDB
            .databases()
            .then((r) => {
            for (let i = 0; i < r.length; i++) {
                const req = window.indexedDB.deleteDatabase(r[i].name);
                req.onerror = (event) => {
                    console.error(`[IndexedDB] Error deleting database '${r[i].name}':`, event.target?.error);
                };
            }
        })
            .then(() => {
            resolve();
        })
            .catch((error) => {
            console.error('[IndexedDB] Error enumerating databases for deleteAllIndexedDb:', error);
            reject(error);
        });
    });
}
/** Deletes an entire indexed db. */
function deleteIndexedDb(name) {
    const request = window.indexedDB.deleteDatabase(name);
    request.onerror = (event) => {
        console.error(`[IndexedDB] Error deleting database '${name}':`, event.target?.error);
    };
}
/** Gets the version of an indexed db. */
function getIndexedDbVersion(name) {
    return new Promise((resolve, reject) => {
        const request = window.indexedDB.open(name);
        request.onsuccess = (event) => {
            const db = event.target.result;
            // close db because we need to do an upgrade
            db.close();
            resolve(event.target.result.version);
        };
        request.onerror = (event) => {
            console.error(`[IndexedDB] Error opening database '${name}' in getIndexedDbVersion:`, event.target?.error);
            reject(event.target?.error);
        };
    });
}
/*** Gets information about a given indexed db. */
function getIndexedDbInfo(name) {
    return new Promise((resolve, reject) => {
        const request = window.indexedDB.open(name);
        request.onsuccess = (event) => {
            const db = event.target.result;
            // close db because we need to do an upgrade
            db.close();
            resolve({
                version: event.target.result.version,
                objectStoreNames: [...db.objectStoreNames]
            });
        };
        request.onerror = (event) => {
            console.error(`[IndexedDB] Error opening database '${name}' in getIndexedDbInfo:`, event.target?.error);
            reject(event.target?.error);
        };
    });
}
/** Internal method to delete all object stores in a given indexed db with the actual version. */
function _deleteIndexedDbObjectStores(name, version, storeNames) {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(name, version);
        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            storeNames.forEach((storeName) => {
                db.deleteObjectStore(storeName);
            });
            db.close();
            resolve();
        };
        request.onsuccess = (event) => {
            // no upgrade was needed (e.g. version already matched), close and resolve
            event.target.result.close();
            resolve();
        };
        request.onerror = (event) => {
            console.error(`[IndexedDB] Error in _deleteIndexedDbObjectStores (db='${name}', version=${version}, stores=[${storeNames.join(', ')}]):`, event.target?.error);
            reject(event.target?.error);
        };
    });
}

/**
 * Storage Access API utilities for cross-origin iframe support.
 * Required for Safari/WebKit which blocks IndexedDB in cross-origin iframes.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Storage_Access_API
 */
// Cache the result so we only check once per page load (set by bootstrap)
// This is internal and used before BaasAppState is fully initialized
let cachedResult;
/** Checks if the current context is a cross-origin iframe. */
function isCrossOriginIframe() {
    try {
        // If we can access parent.location.origin, we're same-origin
        return window.self !== window.top && window.location.origin !== window.parent.location.origin;
    }
    catch {
        // SecurityError means we're in a cross-origin iframe
        return true;
    }
}
/** Checks if the Storage Access API is supported. */
function isStorageAccessApiSupported() {
    return 'requestStorageAccess' in document && typeof document.requestStorageAccess === 'function';
}
/**
 * Gets the storage access result that was determined during bootstrap.
 * This is a read-only check - the actual permission request is done by BaasBootstrap.
 *
 * Note: Prefer accessing this via BaasAppState.storageAccess when available.
 * This function is for early access before DI/Angular is initialized.
 *
 * @returns The storage access result, or undefined if bootstrap hasn't run yet
 * @example
 * ```typescript
 * const storageAccess = getStorageAccess();
 * if (storageAccess?.hasAccess === false) {
 *   console.warn('IndexedDB may not work in this iframe');
 * }
 * ```
 */
function getStorageAccess() {
    return cachedResult ? { ...cachedResult } : undefined;
}
/**
 * Requests storage access for IndexedDB in cross-origin iframes.
 * This is required for Safari/WebKit which partitions storage by default.
 *
 * @internal This should only be called once by BaasBootstrap during initialization.
 * Consumers should use BaasAppState.storageAccess or getStorageAccess() to check the result.
 */
async function requestStorageAccess() {
    // Initialize result if not cached yet
    if (!cachedResult) {
        cachedResult = {
            hasAccess: true, // Assume true until proven otherwise
            isCrossOriginIframe: isCrossOriginIframe(),
            isApiSupported: isStorageAccessApiSupported()
        };
    }
    // Not in a cross-origin iframe - access is granted by default
    if (!cachedResult.isCrossOriginIframe) {
        return;
    }
    // Storage Access API not supported - assume older browser without partitioning
    if (!cachedResult.isApiSupported) {
        console.info('[BAR] Storage Access API not supported. Assuming storage access is available.');
        return;
    }
    try {
        // Check if we already have access
        const hasFullAccess = await document.hasStorageAccess();
        if (hasFullAccess) {
            cachedResult.hasAccess = true;
            return;
        }
        // Request access for indexedDB, localStorage, etc.
        // Note: The extended API with options is newer and may not be available in all browsers
        await document.requestStorageAccess({
            indexedDB: true,
            localStorage: true,
            sessionStorage: true
        });
        cachedResult.hasAccess = true;
        console.info('[BAR] Storage access granted for cross-origin iframe.');
    }
    catch (error) {
        cachedResult.hasAccess = false;
        console.warn('[BAR] Storage access denied for cross-origin iframe. IndexedDB may not work correctly.', error);
    }
}

// http://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript
/**
 * Generate universally unique identifier.
 */
function generateUuid() {
    let d = Date.now(); // support from IE9 onwards
    if (window.performance && typeof window.performance.now === 'function') {
        d += performance.now(); //use high-precision timer if available
    }
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (d + Math.random() * 16) % 16 | 0;
        d = Math.floor(d / 16);
        return (c == 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
    return uuid;
}
/**
 * Will get the value or execute the function to get the value.
 * @param value
 */
function resolveValue(value) {
    if (typeof value === 'function') {
        return value();
    }
    else {
        return value;
    }
}
/**
 * Ensures the creation of a global variable with nesting and gives the reference to the last property.
 * @param nestedObject
 */
function ensureGlobal(nestedObject) {
    let result = null;
    if (nestedObject != null && nestedObject.length > 0) {
        let global = window;
        const pieces = nestedObject.split('.');
        pieces.forEach((piece) => {
            if (global[piece] == null) {
                global[piece] = {};
            }
            global = global[piece];
        });
        result = global;
    }
    return result;
}
// https://www.geeksforgeeks.org/how-to-get-the-javascript-function-parameter-names-values-dynamically/
/** Gets the parameters of a function. */
function getParamNames(func) {
    // String representaation of the function code
    let str = func.toString();
    // Remove comments of the form /* ... */
    // Removing comments of the form //
    // Remove body of the function { ... }
    // removing '=>' if func is arrow function
    str = str
        .replace(/\/\*[\s\S]*?\*\//g, '')
        .replace(/\/\/(.)*/g, '')
        .replace(/{[\s\S]*}/, '')
        .replace(/=>/g, '')
        .trim();
    // Start parameter names after first '('
    const start = str.indexOf('(') + 1;
    // End parameter names is just before last ')'
    const end = str.length - 1;
    const result = str.substring(start, end).split(', ');
    const params = [];
    result.forEach((element) => {
        // Removing any default value
        element = element.replace(/=[\s\S]*/g, '').trim();
        if (element.length > 0)
            params.push(element);
    });
    return params;
}
function getStack() {
    try {
        throw new Error();
    }
    catch (e) {
        return e.stack.replace('Error\n', '').trim();
    }
}
/** Compares 2 versions end returns 0 if equal, 1 if version 1 is higher than 2 and -1 if version 1 is lower than 2. */
function versionCompare(v1, v2, options = undefined) {
    const lexicographical = options && options.lexicographical;
    const zeroExtend = options && options.zeroExtend;
    let v1parts = v1.split('.');
    let v2parts = v2.split('.');
    function isValidPart(x) {
        return (lexicographical ? /^\d+[A-Za-z]*$/ : /^\d+$/).test(x);
    }
    if (!v1parts.every(isValidPart) || !v2parts.every(isValidPart)) {
        return NaN;
    }
    if (zeroExtend) {
        while (v1parts.length < v2parts.length)
            v1parts.push('0');
        while (v2parts.length < v1parts.length)
            v2parts.push('0');
    }
    if (!lexicographical) {
        v1parts = v1parts.map(Number);
        v2parts = v2parts.map(Number);
    }
    for (let i = 0; i < v1parts.length; ++i) {
        if (v2parts.length == i) {
            return 1;
        }
        if (v1parts[i] == v2parts[i]) {
            continue;
        }
        else if (v1parts[i] > v2parts[i]) {
            return 1;
        }
        else {
            return -1;
        }
    }
    if (v1parts.length != v2parts.length) {
        return -1;
    }
    return 0;
}
// NOTE from BARVUY
function generateRandomNumber(min, max) {
    // find diff
    const difference = max - min;
    // generate random number
    let rand = Math.random();
    // multiply with difference
    rand = Math.floor(rand * difference);
    // add with min value
    return rand + min;
}

class BaasAuthOptions {
    constructor() {
        /** Whether authentication is enabled */
        this.enabled = true;
        /** Whether IDP validation is enabled. */
        this.validation = true;
        /** @experimental Whether to send the voorwie and hoedanigheid through separate headers. */
        this.withActingScopeHeaders = false;
        /** Whether the basiscontext needs to be synced after an API call. */
        this.syncBasiscontextAfterRequestCompletion = true;
    }
}

var BaasBackendType;
(function (BaasBackendType) {
    /** BaaS.NET in the backend. */
    BaasBackendType["Baas"] = "Baas";
    /** BAR.NET in the backend. */
    BaasBackendType["Bar"] = "Bar";
    /** Custom backend. */
    BaasBackendType["Custom"] = "Custom";
})(BaasBackendType || (BaasBackendType = {}));

let BaasBootstrapOptions = class BaasBootstrapOptions {
    constructor() {
        /** The authentication options */
        this.auth = new BaasAuthOptions();
        /** Backend type. */
        this.backendType = BaasBackendType.Baas;
        /** i18n options for translations/text resources. */
        this.i18n = {};
    }
};
BaasBootstrapOptions = __decorate([
    injectable()
], BaasBootstrapOptions);
/** Outcome of bootstrapping. */
var BaasBootstrapOutcome;
(function (BaasBootstrapOutcome) {
    /** Bootstrap completed. */
    BaasBootstrapOutcome["Initialized"] = "Initialized";
    /** Authentication failed. */
    BaasBootstrapOutcome["AuthFailed"] = "AuthFailed";
    /** Still authenticating */
    BaasBootstrapOutcome["Authenticating"] = "Authenticating";
    /** Bootstrap has stopped and further processing should be halted. */
    BaasBootstrapOutcome["Halted"] = "Halted";
})(BaasBootstrapOutcome || (BaasBootstrapOutcome = {}));

/** Generic internal logger to start log based on a flag in localstorage. */
class BarLogger {
    #enabled;
    #format;
    #checkEnabled() {
        if (this.#enabled == null) {
            if (this.options?.category) {
                // if we have a flag to check by default it is not enabled.
                this.#enabled = false;
                const item = window.localStorage.getItem(`lvflanders.bar.shared.log.enabled.${this.options.category}`);
                if (item != null && item.length > 0) {
                    // parse the string value
                    this.#enabled = JSON.parse(item);
                }
                // if we don't pass on a flag to check in storage we just assume it is enabled
            }
            else
                this.#enabled = true;
        }
        return this.#enabled;
    }
    #getFormat() {
        if (this.#format == null) {
            const item = window.localStorage.getItem('lvflanders.bar.shared.log.format');
            if (item != null && item.length > 0) {
                switch (item.toLowerCase()) {
                    case 'json':
                        this.#format = 'json';
                        break;
                    default:
                        this.#format = 'object';
                        break;
                }
            }
        }
        return this.#format;
    }
    constructor(options) {
        this.options = options;
    }
    /** Logs a message. */
    log(message) {
        if (this.#checkEnabled()) {
            this.#enrich(message);
            this.#output(console.log, message);
        }
    }
    /** Logs an error message. Always fires regardless of the logging enabled flag. */
    error(message) {
        this.#enrich(message);
        this.#output(console.error, message);
    }
    #enrich(message) {
        if (!message.category)
            message.category = this.options?.category;
        if (!message.context)
            message.context = this.options?.context;
        if (!message.stack)
            message.stack = getStack();
        if (this.options?.correlation) {
            // if we gave a correlation as global option we mark it as being the parent
            if (message.correlation)
                message.correlation.parent = this.options.correlation;
            // otherwise we just use it as the leaf correlation for the message
            else
                message.correlation = this.options.correlation;
        }
    }
    #output(fn, message) {
        switch (this.#getFormat()) {
            case 'json':
                fn(JSON.stringify(message));
                break;
            default:
                fn(message);
                break;
        }
    }
}

let BaasAppState = class BaasAppState {
};
BaasAppState = __decorate([
    injectable()
], BaasAppState);

// NOTE doesn't keep open connections
/** BAR store using atomic indexed db operations. */
class BarStore {
    #logger;
    #dbName;
    #storeName;
    constructor(name, storeName) {
        this.#dbName = name;
        this.#storeName = storeName;
        this.#logger = new BarLogger({
            category: 'cacheStorage',
            context: { name, storeName, type: 'BarStore (IndexedDB)' }
        });
    }
    async getItem(key) {
        const correlation = { value: generateUuid() };
        this.#logger.log({ message: `Begin getItem with key '${key}'`, correlation });
        let result = undefined;
        const db = await this.#getDB(false);
        if (db) {
            try {
                if (db.objectStoreNames.contains(this.#storeName)) {
                    const tx = db.transaction(this.#storeName, 'readonly');
                    result = await tx.objectStore(this.#storeName).get(key);
                    await tx.done;
                }
            }
            catch (error) {
                this.#logger.error({ message: `Error in getItem (db='${this.#dbName}', store='${this.#storeName}', key='${key}'): ${error}`, correlation });
                throw error;
            }
            finally {
                db.close();
            }
        }
        this.#logger.log({ message: `End getItem with key '${key}'`, correlation });
        return result;
    }
    async setItem(key, value) {
        const correlation = { value: generateUuid() };
        this.#logger.log({ message: `Begin setItem with key '${key}'`, correlation });
        const db = await this.#getDB(true);
        try {
            const tx = db.transaction(this.#storeName, 'readwrite');
            await tx.objectStore(this.#storeName).put(value, key);
            await tx.done;
        }
        catch (error) {
            this.#logger.error({ message: `Error in setItem (db='${this.#dbName}', store='${this.#storeName}', key='${key}'): ${error}`, correlation });
            throw error;
        }
        finally {
            db.close();
        }
        this.#logger.log({ message: `End setItem with key '${key}'`, correlation });
    }
    async removeItem(key) {
        const correlation = { value: generateUuid() };
        this.#logger.log({ message: `Begin removeItem with key '${key}'`, correlation });
        const db = await this.#getDB(false);
        if (db) {
            try {
                if (db.objectStoreNames.contains(this.#storeName)) {
                    const tx = db.transaction(this.#storeName, 'readwrite');
                    await tx.objectStore(this.#storeName).delete(key);
                    await tx.done;
                }
            }
            catch (error) {
                this.#logger.error({ message: `Error in removeItem (db='${this.#dbName}', store='${this.#storeName}', key='${key}'): ${error}`, correlation });
                throw error;
            }
            finally {
                db.close();
            }
        }
        this.#logger.log({ message: `End removeItem with key '${key}'`, correlation });
    }
    async clear() {
        const correlation = { value: generateUuid() };
        this.#logger.log({ message: 'Begin clearObjectStore', correlation });
        const db = await this.#getDB(false);
        // only clear if db exists
        if (db) {
            try {
                if (db.objectStoreNames.contains(this.#storeName)) {
                    const tx = db.transaction(this.#storeName, 'readwrite');
                    await tx.objectStore(this.#storeName).clear();
                    await tx.done;
                }
            }
            catch (error) {
                this.#logger.error({ message: `Error in clear (db='${this.#dbName}', store='${this.#storeName}'): ${error}`, correlation });
                throw error;
            }
            finally {
                db.close();
            }
        }
        this.#logger.log({ message: 'End clearObjectStore', correlation });
    }
    async drop() {
        const correlation = { value: generateUuid() };
        this.#logger.log({ message: 'Begin dropObjectStore', correlation });
        let db = await this.#getDB(false);
        if (db) {
            try {
                if (db.objectStoreNames.contains(this.#storeName)) {
                    // object store exists, open new db connection with increased version to be able to delete the object store
                    db.close();
                    db = await openDB(this.#dbName, db.version + 1, {
                        upgrade: (db) => {
                            if (db.objectStoreNames.contains(this.#storeName)) {
                                db.deleteObjectStore(this.#storeName);
                            }
                        }
                    });
                }
            }
            catch (error) {
                this.#logger.error({ message: `Error in drop (db='${this.#dbName}', store='${this.#storeName}'): ${error}`, correlation });
                throw error;
            }
            finally {
                db.close();
            }
        }
        this.#logger.log({ message: 'End dropObjectStore', correlation });
    }
    async #getDB(allowCreate) {
        const dbExists = (await indexedDB.databases()).some((db) => db.name === this.#dbName);
        // db doesn't exist and this is a read operation then just give back undefined
        if (!dbExists && !allowCreate)
            return undefined;
        let db;
        try {
            // version if db doesn't exist yet then we use version 1
            // if db doesn't exist yet we know we need to create the object store immediately
            const options = dbExists
                ? undefined
                : {
                    upgrade: (upgradeDb) => {
                        if (!upgradeDb.objectStoreNames.contains(this.#storeName)) {
                            upgradeDb.createObjectStore(this.#storeName);
                        }
                    }
                };
            // open db and pass options
            db = await openDB(this.#dbName, undefined, options);
            if (allowCreate && dbExists && !db.objectStoreNames.contains(this.#storeName)) {
                db.close();
                db = await openDB(this.#dbName, db.version + 1, {
                    upgrade: (upgradeDb) => {
                        if (!upgradeDb.objectStoreNames.contains(this.#storeName)) {
                            upgradeDb.createObjectStore(this.#storeName);
                        }
                    }
                });
            }
        }
        catch (error) {
            this.#logger.error({ message: `Error opening database (db='${this.#dbName}', store='${this.#storeName}'): ${error}` });
            throw error;
        }
        return db;
    }
}

/** Gets the default cache entry options */
function _getDefaultCacheEntryOptions() {
    return { session: true };
}

/** Key-value wrapper around object store with caching. */
class BarCacheStore {
    #name;
    #storeName;
    #store;
    #logger;
    #getContextData;
    constructor(
    /** DB name */
    name, 
    /** Object store name */
    storeName, 
    /** Optional callback for metadata retrieval */
    getContextData = () => {
        return {};
    }, 
    /** Optional correlation used for logging purposes */
    correlation = undefined) {
        this.#name = name;
        this.#storeName = storeName;
        this.#getContextData = getContextData;
        this.#logger = new BarLogger({
            category: 'cacheStorage',
            context: {
                type: 'BarCacheStore',
                name: name,
                storeName: storeName
            },
            correlation
        });
        // store doesn't keep state beside db name and store name
        this.#store = new BarStore(name, storeName);
    }
    /**
     * Gets an item from the cache or executes a callback to set it if not in the cache.
     * @param key Key.
     * @param callback Callback to get the value in case the value is not in the cache.
     */
    async getOrSetItem(key, callback, options = _getDefaultCacheEntryOptions()) {
        const correlation = { value: generateUuid() };
        this.#logger.log({ message: `Begin getOrSetItem with key '${key}'`, correlation: correlation });
        try {
            const value = await this.#getItemInternal(key);
            if (value != null)
                return value;
            // execute the callback
            const data = await Promise.resolve(callback());
            // put it in the cache
            await this.#setItemInternal(key, data, options);
            return data;
        }
        catch (error) {
            this.#logger.error({
                message: `Error in getOrSetItem (db='${this.#name}', store='${this.#storeName}', key='${key}'): ${error}`,
                correlation: correlation
            });
            throw error;
        }
        finally {
            this.#logger.log({ message: `End getOrSetItem with key '${key}'`, correlation: correlation });
        }
    }
    /** Gets an item from the cache. */
    async getItem(key) {
        const correlation = { value: generateUuid() };
        this.#logger.log({ message: `Begin getItem with key '${key}'`, correlation: correlation });
        try {
            return await this.#getItemInternal(key);
        }
        catch (error) {
            this.#logger.error({
                message: `Error in getItem (db='${this.#name}', store='${this.#storeName}', key='${key}'): ${error}`,
                correlation: correlation
            });
            throw error;
        }
        finally {
            this.#logger.log({ message: `End getItem with key '${key}'`, correlation: correlation });
        }
    }
    /** Sets an item in the cache with options. */
    async setItem(key, value, options = _getDefaultCacheEntryOptions()) {
        const correlation = { value: generateUuid() };
        this.#logger.log({ message: `Begin setItem with key '${key}'`, correlation: correlation });
        try {
            await this.#setItemInternal(key, value, options);
        }
        catch (error) {
            this.#logger.error({
                message: `Error in setItem (db='${this.#name}', store='${this.#storeName}', key='${key}'): ${error}`,
                correlation: correlation
            });
            throw error;
        }
        finally {
            this.#logger.log({ message: `End setItem with key '${key}'`, correlation: correlation });
        }
    }
    /** Removes an item from the cache. */
    async removeItem(key) {
        const correlation = { value: generateUuid() };
        this.#logger.log({ message: `Begin removeItem with key '${key}'`, correlation: correlation });
        try {
            await this.#store.removeItem(key);
        }
        catch (error) {
            this.#logger.error({
                message: `Error in removeItem (db='${this.#name}', store='${this.#storeName}', key='${key}'): ${error}`,
                correlation: correlation
            });
            throw error;
        }
        finally {
            this.#logger.log({ message: `End removeItem with key '${key}'`, correlation: correlation });
        }
    }
    /** Clears all items from the cache. */
    async clear() {
        const correlation = { value: generateUuid() };
        this.#logger.log({ message: 'Begin clear', correlation: correlation });
        try {
            await this.#store.clear();
        }
        catch (error) {
            this.#logger.error({ message: `Error in clear (db='${this.#name}', store='${this.#storeName}'): ${error}`, correlation: correlation });
            throw error;
        }
        finally {
            this.#logger.log({ message: 'End clear', correlation: correlation });
        }
    }
    /** Drops the cache. */
    async drop() {
        const correlation = { value: generateUuid() };
        this.#logger.log({ message: 'Begin drop', correlation: correlation });
        try {
            await this.#store.drop();
        }
        catch (error) {
            this.#logger.error({ message: `Error in drop (db='${this.#name}', store='${this.#storeName}'): ${error}`, correlation: correlation });
            throw error;
        }
        finally {
            this.#logger.log({ message: 'End drop', correlation: correlation });
        }
    }
    /**
     * Invalidates the store by removing expired cache entries.
     * @param clearSession Whether to clear the session cache entries.
     */
    async invalidate(clearSession = false) {
        const correlation = { value: generateUuid() };
        this.#logger.log({ message: `Begin invalidate with clearSession = ${clearSession}`, correlation: correlation });
        try {
            let keys;
            let db = await openDB(this.#name);
            try {
                const tx = db.transaction(this.#storeName, 'readwrite');
                const store = tx.objectStore(this.#storeName);
                keys = await store.getAllKeys();
                for (const key of keys) {
                    const entry = await tx.objectStore(this.#storeName).get(key);
                    // now we check if the entry is expired or if the consumer wants to clear session entries
                    // NOTE 16-05-2025 added extra check if entry is defined (JERGAL issue)
                    if ((entry && this.#checkExpired(entry)) || (clearSession && entry.options != null && entry.options.session)) {
                        await tx.objectStore(this.#storeName).delete(key);
                    }
                }
                keys = await store.getAllKeys();
                await tx.done;
            }
            finally {
                db.close();
            }
            if (!keys.length) {
                // we must drop the object store because it is empty (no keys left)
                db = await openDB(this.#name, db.version + 1, {
                    upgrade: (db) => {
                        if (db.objectStoreNames.contains(this.#storeName)) {
                            db.deleteObjectStore(this.#storeName);
                        }
                    }
                });
                db.close();
            }
        }
        catch (error) {
            this.#logger.error({
                message: `Error in invalidate (db='${this.#name}', store='${this.#storeName}', clearSession=${clearSession}): ${error}`,
                correlation: correlation
            });
            throw error;
        }
        this.#logger.log({ message: `End invalidate with clearSession = ${clearSession}`, correlation: correlation });
    }
    #checkExpired(entry) {
        const contextData = this.#getContextData() || {};
        return (
        // expiration expired
        (entry.expiration != null && isBefore(entry.expiration, new Date())) ||
            // or session expired (entry from previous session) in case we can check this
            (entry.options?.session && contextData.sessionId && entry.sessionId != contextData.sessionId));
    }
    async #getItemInternal(key) {
        let value = null;
        const db = await openDB(this.#name);
        try {
            if (db.objectStoreNames.contains(this.#storeName)) {
                const tx = db.transaction(this.#storeName, 'readwrite');
                const store = tx.objectStore(this.#storeName);
                const entry = (await store.get(key));
                if (entry) {
                    if (this.#checkExpired(entry)) {
                        await store.delete(key);
                    }
                    else {
                        entry.lastAccess = new Date();
                        await store.put(entry, key);
                        value = entry.value;
                    }
                }
                await tx.done;
            }
        }
        catch (error) {
            this.#logger.error({ message: `Error in getItemInternal (db='${this.#name}', store='${this.#storeName}', key='${key}'): ${error}` });
            throw error;
        }
        finally {
            db.close();
        }
        return value;
    }
    async #setItemInternal(key, value, options) {
        const timestamp = new Date();
        const contextData = this.#getContextData() || {};
        const entry = {
            value,
            timestamp,
            // compute the expiration based on ttl
            expiration: options != null && options.ttl != null ? addSeconds(timestamp, options.ttl) : null,
            sessionId: contextData.sessionId,
            options
        };
        await this.#store.setItem(key, entry);
    }
}

/** No-op cache store used when cacheStorage is set to 'none'.
 * All operations are bypassed — no IndexedDB is opened or written. */
class BaasNullCacheStore extends BarCacheStore {
    constructor() {
        super('_null', '_null');
    }
    async getOrSetItem(_key, callback, _options) {
        return Promise.resolve(callback());
    }
    async getItem() {
        return undefined;
    }
    async setItem() { }
    async removeItem() { }
    async clear() { }
    async drop() { }
    async invalidate() { }
}

/** Defines how to store a given item. */
var BarCacheStoreType;
(function (BarCacheStoreType) {
    /** Cache store on application level */
    BarCacheStoreType["Application"] = "Application";
    /** Cache store on user level. */
    BarCacheStoreType["User"] = "User";
    /** Cache store on global level. */
    BarCacheStoreType["Global"] = "Global";
    /** Cache store on application and user level. */
    BarCacheStoreType["ApplicationUser"] = "ApplicationUser";
})(BarCacheStoreType || (BarCacheStoreType = {}));

const DB_NAME_PREFIX = 'domain.lv.cache.';
const DB_NAME_APP = `${DB_NAME_PREFIX}app`;
const DB_NAME_USER = `${DB_NAME_PREFIX}user`;
const DB_NAME_APP_USER = `${DB_NAME_PREFIX}appUser`;
const DB_NAME_GLOBAL = `${DB_NAME_PREFIX}global`;
// GVK gets its own database to avoid conflicts, but invalidation logic will be integrated
const DB_NAME_GVK = `${DB_NAME_PREFIX}gvk`;
let BarCacheManager = class BarCacheManager {
    #logger = new BarLogger({ category: 'cacheStorage', context: 'BarCacheManager' });
    #appState;
    #options;
    constructor(appState, options) {
        this.#appState = appState;
        this.#options = options;
    }
    getStore(options) {
        let name = null;
        let storeName = null;
        switch (options.storeType) {
            case BarCacheStoreType.Application: {
                name = DB_NAME_APP;
                storeName = options.appCode;
                break;
            }
            case BarCacheStoreType.User: {
                name = DB_NAME_USER;
                storeName = options.userId;
                break;
            }
            case BarCacheStoreType.ApplicationUser: {
                name = DB_NAME_APP_USER;
                storeName = `${options.appCode}_${options.userId}`;
                break;
            }
            case BarCacheStoreType.Global: {
                name = DB_NAME_GLOBAL;
                storeName = 'main';
                break;
            }
        }
        if (options.store != null) {
            // check if consumer wants separate store in the database
            storeName += `_${options.store}`;
        }
        return this.#getStore(name, storeName);
    }
    /**
     * Gets the stores matching the options (with pattern matching).
     */
    async getStores(options) {
        if (!options)
            throw new Error('Options are required.');
        if (!options.storeType)
            throw new Error('Store type is required.');
        if (this.#options.cacheStorage === 'none')
            return [];
        let dbName;
        let filterFn;
        switch (options.storeType) {
            case BarCacheStoreType.Application: {
                dbName = DB_NAME_APP;
                filterFn = (storeName) => {
                    const { appCode, store } = options;
                    if (appCode === '*')
                        return true;
                    if (appCode && store === '*')
                        return storeName.startsWith(`${appCode}_`);
                    if (appCode && store)
                        return storeName === `${appCode}_${store}`;
                    if (appCode && !store)
                        return storeName === appCode;
                    return false;
                };
                break;
            }
            case BarCacheStoreType.User: {
                dbName = DB_NAME_USER;
                filterFn = (storeName) => {
                    const { userId, store } = options;
                    if (userId === '*')
                        return true;
                    if (userId && store === '*')
                        return storeName.startsWith(`${userId}_`);
                    if (userId && store)
                        return storeName === `${userId}_${store}`;
                    if (userId && !store)
                        return storeName === userId;
                    return false;
                };
                break;
            }
            case BarCacheStoreType.ApplicationUser:
                dbName = DB_NAME_APP_USER;
                filterFn = (storeName) => {
                    const { appCode, userId, store } = options;
                    // All wildcard
                    if (appCode === '*' && userId === '*')
                        return true;
                    // Wildcard appCode, specific userId
                    if (appCode === '*' && userId && userId !== '*') {
                        const suffix = `_${userId}`;
                        if (store === '*')
                            return storeName.endsWith(`${suffix}_${store}`);
                        if (store != null)
                            return storeName.endsWith(`${suffix}_${store}`);
                        return storeName.endsWith(suffix) && !storeName.endsWith(`${suffix}_`);
                    }
                    // Wildcard userId, specific appCode
                    if (userId === '*' && appCode && appCode !== '*') {
                        const prefix = `${appCode}_`;
                        if (store === '*')
                            return storeName.startsWith(`${prefix}`);
                        if (store != null)
                            return storeName.startsWith(`${prefix}`) && storeName.endsWith(`_${store}`);
                        return storeName.startsWith(prefix) && !storeName.substring(prefix.length).includes('_');
                    }
                    // Fully specified appCode and userId
                    const base = `${appCode}_${userId}`;
                    if (store === '*')
                        return storeName.startsWith(`${base}_`);
                    if (store != null)
                        return storeName === `${base}_${store}`;
                    return storeName === base;
                };
                break;
            case BarCacheStoreType.Global: {
                dbName = DB_NAME_GLOBAL;
                filterFn = (storeName) => {
                    const { store } = options;
                    if (store === '*')
                        return storeName.startsWith('main_');
                    if (store != null)
                        return storeName === `main_${store}`;
                    return storeName === 'main';
                };
                break;
            }
            default:
                throw new Error(`Unsupported store type: ${options.storeType}`);
        }
        const storeNames = await this.#getObjectStoreNames(dbName);
        const matchingNames = storeNames.filter(filterFn);
        return matchingNames.map((storeName) => this.#getStore(dbName, storeName));
    }
    /**
     *  Invalidates all caches.
     * @param clearSession Whether to clear all session cache entries.
     */
    async invalidate(clearSession = false) {
        if (this.#options.cacheStorage === 'none')
            return;
        // use a guid to correlate all actions together for logging purposes
        const correlation = { value: generateUuid() };
        this.#logger.log({ message: `Starting cache invalidation with argument clearSession = ${clearSession}`, correlation: correlation });
        await this.#invalidateDatabase(DB_NAME_APP, clearSession, correlation);
        await this.#invalidateDatabase(DB_NAME_USER, clearSession, correlation);
        await this.#invalidateDatabase(DB_NAME_APP_USER, clearSession, correlation);
        await this.#invalidateDatabase(DB_NAME_GLOBAL, clearSession, correlation);
        await this.#invalidateDatabase(DB_NAME_GVK, clearSession, correlation);
        this.#logger.log({ message: 'Ending cache invalidation', correlation: correlation });
    }
    /**
     * Drops all stores matching the options (with pattern matching)..
     * @param options
     */
    async dropStores(options) {
        const stores = await this.getStores(options);
        for (const store of stores)
            await store.drop();
    }
    /**
     * Clears all stores matching the options (with pattern matching)..
     * @param options
     */
    async clearStores(options) {
        const stores = await this.getStores(options);
        for (const store of stores)
            await store.clear();
    }
    async #invalidateDatabase(name, clearSession, correlation) {
        this.#logger.log({ message: `Starting database invalidation with name '${name}'`, correlation: correlation });
        this.#logger.log({ message: 'Getting objectStoreNames for invalidation purposes', correlation: correlation });
        const objectStoreNames = await this.#getObjectStoreNames(name);
        this.#logger.log({ message: 'Retrieved objectStoreNames for invalidation purposes', correlation: correlation });
        for (const storeName of objectStoreNames) {
            const store = this.#getStore(name, storeName, correlation);
            await store.invalidate(clearSession);
        }
        this.#logger.log({ message: 'Getting objectStoreNames for dropping purposes', correlation: correlation });
        const remainingObjectStoreNames = await this.#getObjectStoreNames(name);
        this.#logger.log({ message: 'Retrieved objectStoreNames for dropping purposes', correlation: correlation });
        if (remainingObjectStoreNames.length === 0) {
            // drop database in case empty
            try {
                await indexedDB.deleteDatabase(name);
            }
            catch (error) {
                this.#logger.error({ message: `Error deleting database (db='${name}'): ${error}` });
                throw error;
            }
        }
        else {
            this.#logger.log({ message: `Ending database invalidation with name '${name}'`, correlation: correlation });
        }
    }
    async #getObjectStoreNames(name) {
        let db;
        try {
            db = await openDB(name);
            const objectStoreNames = Array.from(db.objectStoreNames);
            return objectStoreNames;
        }
        catch (error) {
            this.#logger.error({ message: `Error opening database to read objectStoreNames (db='${name}'): ${error}` });
            throw error;
        }
        finally {
            db?.close();
        }
    }
    #getStore(name, storeName, correlation = undefined) {
        if (this.#options.cacheStorage === 'none')
            return new BaasNullCacheStore();
        const getContextData = () => {
            return {
                sessionId: this.#appState.sessionId
            };
        };
        return new BarCacheStore(name, storeName, getContextData, correlation);
    }
};
BarCacheManager = __decorate([
    injectable(),
    __metadata("design:paramtypes", [BaasAppState, BaasBootstrapOptions])
], BarCacheManager);

/**
 * Represents a mechanism to add items to a queue and to read them out to execute something.
 */
class BarQueueProcessor {
    /**
     * Constructor.
     * @param storage Storage to use.
     * @param storageKey Key to use in the storage.
     */
    constructor(storage, storageKey) {
        this.storage = storage;
        this.storageKey = storageKey;
        this.started = false;
    }
    /**
     * Starts listening to the queue.
     * @param interval Interval in milliseconds to get items from the queue.
     * @param dequeueCount Number of items to get from the queue.
     */
    start(interval, dequeueCount) {
        this.intervalId = window.setInterval(() => this.dequeue(dequeueCount), interval);
        this.started = true;
    }
    /**
     * Stops listening to the queue.
     */
    stop() {
        if (this.started) {
            this.started = false;
            window.clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }
    /**
     * Adds an item to the queue.
     * @param item
     */
    enqueue(item) {
        let queueStr = this.storage.getItem(this.storageKey);
        let queue = null;
        if (queueStr == null) {
            queue = [];
        }
        else {
            queue = JSON.parse(queueStr);
        }
        queue.push(item);
        queueStr = JSON.stringify(queue);
        this.storage.setItem(this.storageKey, queueStr);
    }
    dequeue(dequeueCount) {
        let queueStr = this.storage.getItem(this.storageKey);
        if (queueStr != null) {
            let queue = JSON.parse(queueStr);
            if (queue.length > 0) {
                const itemsToSend = queue.slice(0, dequeueCount);
                queue = queue.slice(dequeueCount);
                if (queue.length == 0) {
                    this.storage.removeItem(this.storageKey);
                }
                else {
                    queueStr = JSON.stringify(queue);
                    this.storage.setItem(this.storageKey, queueStr);
                }
                this.onExecute(itemsToSend);
            }
        }
    }
}

const APP_INFO_COOKIE_KEY = 'domain.basisarchitectuur.appinfo';
const PAGES_ROOT_PATH = 'bui/';
const STORAGE_KEY_PREFIX = 'lvflanders.bar.shared.';
const OIDC_CALLBACK_PATH = '/auth/oidc/callback/';
const _BAAS_SHARED_CONSTANTS = {
    STORAGE_KEY_PREFIX,
    TEMP_STORAGE_STATE_KEY: STORAGE_KEY_PREFIX + 'temp-state',
    DEFAULT_MEETPUNT_TYPE: 'NG',
    STORAGE_KEY: {
        SESSION_ID: 'sessionId',
        IDP_IMPERSONATE: STORAGE_KEY_PREFIX + 'idpImpersonate',
        BAAS_STATE_CONTEXT: STORAGE_KEY_PREFIX + 'baas-state-context',
        BAR_APPINFO_CONTEXT: STORAGE_KEY_PREFIX + 'bar-appinfo-context'
    },
    URL_SEGMENT: {
        BAR_WEB: '/basisarchitectuur/domain.basisarchitectuur.web/',
        BAAS_STATIC: '/{segment}/baas-static/web/',
        BAAS_WEB: '/{segment}/baasweb/web/',
        IDP_OIDC: '/{segment}/idp-oidc/web/',
        STARTSCHERM: '/a/startscherm/v2/web/home'
    },
    AUDIT: {
        HTML_ATTRIBUTE_AUDIT_ID: 'data-bar-audit-id',
        HTML_ATTRIBUTE_AUDIT_TYPE: 'data-bar-audit-type'
    },
    COOKIE: {
        APP_INFO: {
            MAIN: APP_INFO_COOKIE_KEY,
            SESSION: APP_INFO_COOKIE_KEY + '.session',
            SECURE: APP_INFO_COOKIE_KEY + '.secure'
        }
    },
    HEADER: {
        BAAS_BACKEND_HEADER: 'baas.context.backend',
        BAAS_BACKEND_HEADER_V2: 'baas-context-backend', // task 335295 (dotless headers)
        BAAS_BROWSER_CALLSTACK: 'baas.browser.callstack',
        BAAS_BROWSER_CALLSTACK_V2: 'baas-browser-callstack', // task 335295 (dotless headers),
        BAAS_HOEDANIGHEID_TOKEN: 'baas-hoedanigheid-token',
        BAAS_VOORWIE_TOKEN: 'baas-voorwie-token'
    },
    PAGE: {
        LOG_OFF: PAGES_ROOT_PATH + 'log-off',
        INSUFFICIENT_PRIVILEGES: PAGES_ROOT_PATH + 'insufficient-privileges'
    },
    AUTH: {
        OIDC: {
            CALLBACK: {
                LV: OIDC_CALLBACK_PATH + 'lv',
                SPOTFIRE: OIDC_CALLBACK_PATH + 'spotfire'
            }
        }
    }
};

var BaasLogLevel;
(function (BaasLogLevel) {
    BaasLogLevel[BaasLogLevel["Debug"] = 0] = "Debug";
    BaasLogLevel[BaasLogLevel["Audit"] = 1] = "Audit";
    BaasLogLevel[BaasLogLevel["Info"] = 2] = "Info";
    BaasLogLevel[BaasLogLevel["Warn"] = 3] = "Warn";
    BaasLogLevel[BaasLogLevel["Error"] = 4] = "Error";
    BaasLogLevel[BaasLogLevel["Critical"] = 5] = "Critical";
})(BaasLogLevel || (BaasLogLevel = {}));

let BaasLoggingService = class BaasLoggingService {
    #loggingApiClient;
    #options;
    #state;
    #queueProcessor;
    constructor(loggingApiClient, options, state) {
        this.#loggingApiClient = loggingApiClient;
        this.#options = options;
        this.#state = state;
        // instantiate the queue with session storage
        this.#queueProcessor = new BarQueueProcessor(window.sessionStorage, `${_BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX}logQueue`);
    }
    error(log) {
        this.#post(log, BaasLogLevel.Error);
    }
    warn(log) {
        this.#post(log, BaasLogLevel.Warn);
    }
    critical(log) {
        this.#post(log, BaasLogLevel.Critical);
    }
    info(log) {
        this.#post(log, BaasLogLevel.Info);
    }
    /**
     * Inits logging logic (init != start).
     */
    init() {
        const global = ensureGlobal('lvflanders.bar.shared.logging');
        Object.defineProperty(global, 'failedLogs', {
            get() {
                const failedLogsStr = window.localStorage.getItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + 'failedLogs');
                return failedLogsStr != null ? JSON.parse(failedLogsStr) : null;
            },
            configurable: true
        });
    }
    /**
     * Start to listen to the log queue.
     */
    async start() {
        // post items to back-end with interval
        this.#queueProcessor.onExecute = async (logs) => {
            try {
                await this.#loggingApiClient.save(logs);
            }
            catch (error) {
                console.error(error);
                // In case of failure, store the logs in local storage with a timestamp
                const failedLogsStr = window.localStorage.getItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + 'failedLogs');
                const failedLogs = failedLogsStr != null ? JSON.parse(failedLogsStr) : [];
                logs.forEach((log) => {
                    log.timestamp = new Date();
                    failedLogs.push(log);
                });
                window.localStorage.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + 'failedLogs', JSON.stringify(failedLogs));
            }
        };
        // start listening to the queue
        this.#queueProcessor.start(this.#state.config.log.interval, this.#state.config.log.itemCount);
    }
    #post(log, level) {
        if (!this.#state.config?.log.enabled)
            return;
        const model = {
            logLevel: level,
            message: log.message,
            appCode: undefined,
            appVersion: undefined
        };
        if (log.error != null) {
            if (typeof log.error === 'string') {
                model.exception = log.error;
                if (model.message == null) {
                    model.message = log.error;
                }
            }
            else {
                model.exception = log.error.message;
                model.stackTrace = log.error.stack;
                if (model.message == null) {
                    model.message = log.error.message;
                }
            }
        }
        // toepassing informatie meegeven om te loggen in context van die toepassing (use config if null)
        if (this.#state.toepassing != null) {
            model.appCode = this.#state.toepassing.code;
            model.appVersion = this.#state.toepassing.versie;
        }
        else {
            model.appCode = this.#options.appCode;
            model.appVersion = this.#options.appVersion;
        }
        if (!model.message)
            model.message = '';
        // append the current URL for extra information purposes
        model.message += `\r\n\r\nURL: ${window.location.href}`;
        this.#queueProcessor.enqueue(model);
    }
};
BaasLoggingService = __decorate([
    injectable(),
    __param(0, inject(BAAS_TYPES.BaasLoggingApiClient)),
    __metadata("design:paramtypes", [Object, BaasBootstrapOptions, BaasAppState])
], BaasLoggingService);

const CACHE_STORE = 'baas-static';
const CONFIGURATION_KEY = 'configuration';
let _BaasStaticConfigurationApiClient = class _BaasStaticConfigurationApiClient {
    constructor(axios, axiosSilent, appState, logging, cacheManager) {
        this.axios = axios;
        this.axiosSilent = axiosSilent;
        this.appState = appState;
        this.logging = logging;
        this.cacheManager = cacheManager;
    }
    /** Gets the BaaS static configuration */
    async getConfiguration() {
        const cacheStore = this.cacheManager.getStore({ storeType: BarCacheStoreType.Global, store: CACHE_STORE });
        return cacheStore.getOrSetItem(CONFIGURATION_KEY, () => this.#executeGetConfiguration());
    }
    /** Checks whether the chatbot is enabled. */
    checkChatbotEnabled() {
        return Promise.resolve(this.appState.config?.baasStatic?.chatbot?.enabled);
    }
    /** Enables or disables the chatbot. */
    async enableChatbot(enabled) {
        const url = concatUrl(this.appState.location.apiRoot.url, 'api/configuration/chat-bot/enabled');
        await this.axios.put(url, enabled, { headers: { 'Content-Type': 'application/json' } });
        if (this.appState.config?.baasStatic?.chatbot) {
            // change in memory value
            this.appState.config.baasStatic.chatbot.enabled = enabled;
            // remove the configuration from the cache store
            await this.#invalidateConfiguration();
        }
    }
    async #executeGetConfiguration() {
        const url = concatUrl(this.appState.location.baasStatic.url, 'api/configuration');
        try {
            // NOTE use silent instance to not cause a crash in case it fails
            const response = await this.axiosSilent.get(url);
            return response.data;
        }
        catch (err) {
            console.error(err);
            this.logging.error(err);
            // NOTE do not crash the application in case something happens here
            return undefined;
        }
    }
    async #invalidateConfiguration() {
        const cacheStore = this.cacheManager.getStore({ storeType: BarCacheStoreType.Global, store: CACHE_STORE });
        // remove the configuration from the cache store
        await cacheStore.removeItem(CONFIGURATION_KEY);
    }
};
_BaasStaticConfigurationApiClient = __decorate([
    injectable(),
    __param(0, inject(BAAS_TYPES.AxiosInstance)),
    __param(0, named(BAAS_AXIOS_INSTANCE_TYPES.Baas)),
    __param(1, inject(BAAS_TYPES.AxiosInstance)),
    __param(1, named(BAAS_AXIOS_INSTANCE_TYPES._BaasSilent)),
    __metadata("design:paramtypes", [Function, Function, BaasAppState,
        BaasLoggingService,
        BarCacheManager])
], _BaasStaticConfigurationApiClient);

class BarStopwatch {
    constructor() {
        this.id = null;
        /** Duration of the watch */
        this.duration = null;
    }
    /** Starts the stopwatch */
    start() {
        this.reset();
        if (window.performance && window.performance.timing) {
            this.id = generateUuid();
            window.performance.mark(this.id + '_start');
        }
    }
    /** Ends the stopwatch and defines the duration */
    stop() {
        if (window.performance && window.performance.timing && this.id != null) {
            window.performance.mark(this.id + '_end');
            window.performance.measure(this.id, this.id + '_start', this.id + '_end');
            const entries = window.performance.getEntriesByName(this.id);
            window.performance.clearMarks(this.id + '_end');
            window.performance.clearMeasures(this.id);
            // cleanup
            this.reset();
            this.duration = entries[0].duration;
        }
    }
    /** Resets the stopwatch */
    reset() {
        if (this.id != null) {
            window.performance.clearMarks(this.id + '_start');
            this.id = null;
            this.duration = null;
        }
    }
}

// Based on https://stackoverflow.com/a/4460624/2174621 + added circular dependency safety check
/**
 * Deep clones an object.
 * @param item The item to clone.
 */
function deepClone(item) {
    // we need to keep track of what we cloned
    const traversed = [];
    return deepCloneInternal(item, traversed);
}
function deepCloneInternal(item, traversed) {
    if (!item) {
        return item;
    } // null, undefined values check
    const types = [Number, String, Boolean];
    let result;
    // normalizing primitives if someone did new String('aaa'), or new Number('444');
    types.forEach(function (type) {
        if (item instanceof type) {
            result = type(item);
        }
    });
    if (typeof result == 'undefined') {
        if (Object.prototype.toString.call(item) === '[object Array]') {
            result = [];
            item.forEach(function (child, index, array) {
                result[index] = deepClone(child);
            });
        }
        else if (typeof item == 'object') {
            // testing that this is DOM
            if (item.nodeType && typeof item.cloneNode == 'function') {
                result = item.cloneNode(true);
            }
            else if (!item.prototype) {
                // check that this is a literal
                if (item instanceof Date) {
                    result = new Date(item);
                }
                else {
                    // check if we already handled this object
                    const traversedItem = traversed.find((t) => t.originalItem == item);
                    if (traversedItem != null) {
                        // return the already cloned item
                        result = traversedItem.clonedItem;
                    }
                    else {
                        // it is an object literal
                        result = {};
                        // keep track of the clone
                        traversed.push({
                            originalItem: item,
                            clonedItem: result
                        });
                        for (const i in item) {
                            result[i] = deepCloneInternal(item[i], traversed);
                        }
                    }
                }
            }
            else {
                // depending what you would like here,
                // just keep the reference, or create new object
                if (item.constructor) {
                    // would not advice to do that, reason? Read below
                    result = new item.constructor();
                }
                else {
                    result = item;
                }
            }
        }
        else {
            result = item;
        }
    }
    return result;
}

function _addAuditInfoOnDocument(audit) {
    if (document && audit) {
        const htmlEl = document.getElementsByTagName('html');
        if (htmlEl.length > 0) {
            htmlEl[0].setAttribute(_BAAS_SHARED_CONSTANTS.AUDIT.HTML_ATTRIBUTE_AUDIT_ID, audit.auditId);
            htmlEl[0].setAttribute(_BAAS_SHARED_CONSTANTS.AUDIT.HTML_ATTRIBUTE_AUDIT_TYPE, audit.auditType);
        }
    }
}

let BaasAuditService = class BaasAuditService {
    constructor(logging, state, auditApiClient) {
        this.logging = logging;
        this.state = state;
        this.auditApiClient = auditApiClient;
        // instantiate the queue with session storage
        this.queueProcessor = new BarQueueProcessor(window.sessionStorage, _BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + 'auditQueue');
    }
    /**
     * Puts an audit record on the queue.
     * @param audit Audit information.
     */
    log(audit) {
        // do not log if disabled or if audit type is excluded
        if (this.state.config.audit.enabled !== true ||
            (this.state.config.audit.excludedAuditTypes != null && this.state.config.audit.excludedAuditTypes.indexOf(audit.auditType) > -1)) {
            return;
        }
        if (audit.timestamp == null) {
            audit.timestamp = new Date();
        }
        if (audit.sessionId == null) {
            audit.sessionId = this.state.sessionId;
        }
        if (audit.appCode == null && this.state.toepassing != null) {
            audit.appCode = this.state.toepassing.code;
        }
        if (this.state.basiscontext != null) {
            audit.hoedanigheid = this.state.basiscontext.hoedanigheid;
            audit.hoedanigheidRolcode = this.state.basiscontext.hoedanigheidRolcode;
            audit.voorwie = this.state.basiscontext.voorwie;
            audit.voorwieRolcode = this.state.basiscontext.voorwieRolcode;
            audit.userId = this.state.basiscontext.userId;
        }
        audit.hostName = location.hostname;
        if (!audit.details)
            audit.details = {};
        if (!audit.details['PGID']) {
            audit.details['PGID'] = this.state.pageId;
        }
        this.queueProcessor.enqueue(audit);
    }
    /**
     * Start to listen to the queue.
     */
    start() {
        // post items to back-end with interval
        this.queueProcessor.onExecute = async (auditItems) => {
            try {
                await this.auditApiClient.save(auditItems);
            }
            catch (error) {
                // in case an error occurs just handle in background
                console.error(error);
                this.logging.error({ error });
            }
        };
        // start listening to the audit queue
        this.queueProcessor.start(this.state.config.audit.interval, this.state.config.audit.itemCount);
    }
};
BaasAuditService = __decorate([
    injectable(),
    __param(2, inject(BAAS_TYPES.BaasAuditApiClient)),
    __metadata("design:paramtypes", [BaasLoggingService,
        BaasAppState, Object])
], BaasAuditService);

var BaasAuditType;
(function (BaasAuditType) {
    BaasAuditType["Session"] = "BROWSERSESSIONSTART";
    BaasAuditType["Page"] = "BROWSERPAGE";
    BaasAuditType["Route"] = "BROWSERROUTE";
    BaasAuditType["Dependencies"] = "BROWSERDEPENDENCIES";
})(BaasAuditType || (BaasAuditType = {}));

/** Configuration service. */
let BaasConfigurationService = class BaasConfigurationService {
    constructor(state, cacheManager, apiClient) {
        this.state = state;
        this.cacheManager = cacheManager;
        this.apiClient = apiClient;
    }
    get store() {
        return this.cacheManager.getStore({
            storeType: BarCacheStoreType.User,
            store: 'configuration',
            userId: this.state.basiscontext.userId
        });
    }
    /** Reads the value with given options. */
    readValue(options) {
        if (!options)
            throw new Error('The options argument is required.');
        if (!options.pathAndKey)
            throw new Error('The pathAndKey property is required.');
        let key = options.pathAndKey;
        if (key && key.indexOf('/') > -1) {
            console.warn('BaaS configuration keys should contain backslashes (\\) instead of slashes (/). The key was automatically transformed.');
            // apparently replace func replaces only the first occurrence
            do
                key = key.replace('/', '\\');
            while (key.indexOf('/') > -1);
        }
        const httpCallback = () => {
            const model = {
                key: key,
                appCode: options.appCode || this.state.toepassing.code,
                // NOTE that version is only used with BAR backend and not with BaaS backend
                appVersion: options.appVersion || this.state.toepassing.versie
            };
            // for now we just use the existing profiel http service
            return this.apiClient.read(model);
        };
        return this.store.getOrSetItem(key, httpCallback, {
            session: true
        });
    }
    /**
     * Flushes the security browser cache.
     */
    flush() {
        return this.store.clear();
    }
};
BaasConfigurationService = __decorate([
    injectable(),
    __param(2, inject(BAAS_TYPES.BaasConfigurationApiClient)),
    __metadata("design:paramtypes", [BaasAppState,
        BarCacheManager, Object])
], BaasConfigurationService);

const LOCALE = {
    FLEMISH: 'nl-be',
    DUTCH: 'nl'
};
const BAAS_METADATA_KEY = {
    CACHE_STORE_TYPE_TAG: 'cacheStoreType'
};
const BAAS_CUSTOM_EVENT = {
    BASISCONTEXT_CHANGED: 'basisContextChanged',
    CUSTOM_HUB_MESSAGE: 'customHubMessage'
};

function clearFromStorage(storage, condition) {
    for (let i = storage.length - 1; i >= 0; i--) {
        const key = storage.key(i);
        if (key && condition(key)) {
            storage.removeItem(key);
        }
    }
}

class BaasActie {
    constructor(actie) {
        if (actie != null) {
            this.actie = actie.actie;
            this.voorwie = actie.voorwie;
            this.voorwieRolcode = actie.voorwieRolcode;
            this.hoedanigheid = actie.hoedanigheid;
            this.hoedanigheidRolcode = actie.hoedanigheidRolcode;
        }
    }
}

const TEMP_STATE_ROOT_KEY = `${_BAAS_SHARED_CONSTANTS.TEMP_STORAGE_STATE_KEY}.actie-allowed`;
/**
 * Security service to perform BAR security tasks.
 */
let BaasSecurityService = class BaasSecurityService {
    get #tempStateKey() {
        let result = TEMP_STATE_ROOT_KEY;
        if (this.state.basiscontext?.userId)
            result += `.${this.state.basiscontext.userId}`;
        return result;
    }
    constructor(securityApiClient, state) {
        this.securityApiClient = securityApiClient;
        this.state = state;
    }
    /**
     * Checks whether the actie is allowed and returns an observable with the result.
     */
    async checkActieAllowed(actie) {
        let storageObj = this.#getActieAllowedStorageObj();
        const key = JSON.stringify(actie);
        if (storageObj[key] != null) {
            return storageObj[key];
        }
        else {
            // make call only once
            const result = await this.securityApiClient.checkActieAllowed(actie);
            // get object again in case request was still going on
            storageObj = this.#getActieAllowedStorageObj();
            storageObj[key] = result;
            // store it into session storage
            localStorage.setItem(this.#tempStateKey, JSON.stringify(storageObj));
            return result;
        }
    }
    // NOTE equivalent of isActieAllowedFromSession but using basiscontext from page state
    /**
     * Checks whether the actie is allowed by checking the basiscontext and returns an observable with the result.
     * @param actie
     * @param includeVoorwie
     * @param includeHoedanigheid
     */
    checkActieAllowedFromContext(actie) {
        const actieObj = new BaasActie();
        actieObj.actie = actie.actie;
        if (actie.includeVoorwie) {
            actieObj.voorwie = this.state.basiscontext.voorwie;
            actieObj.voorwieRolcode = this.state.basiscontext.voorwieRolcode;
        }
        if (actie.includeHoedanigheid) {
            actieObj.hoedanigheid = this.state.basiscontext.hoedanigheid;
            actieObj.hoedanigheidRolcode = this.state.basiscontext.hoedanigheidRolcode;
        }
        return this.checkActieAllowed(actieObj);
    }
    /**
     * Flushes the security browser cache.
     */
    flush() {
        clearFromStorage(localStorage, (key) => key.startsWith(TEMP_STATE_ROOT_KEY));
        return Promise.resolve();
    }
    #getActieAllowedStorageObj() {
        const storageObjStr = localStorage.getItem(this.#tempStateKey);
        return storageObjStr ? JSON.parse(storageObjStr) : {};
    }
};
BaasSecurityService = __decorate([
    injectable(),
    __param(0, inject(BAAS_TYPES.BaasSecurityApiClient)),
    __metadata("design:paramtypes", [Object, BaasAppState])
], BaasSecurityService);

const STORAGE_KEY = _BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + 'requestLogOff';
/** Internal log off utility. */
let _BaasLogOffUtility = class _BaasLogOffUtility {
    constructor(options, state, configuration, security, globalStore, logOffHooks) {
        this.options = options;
        this.state = state;
        this.configuration = configuration;
        this.security = security;
        this.globalStore = globalStore;
        this.logOffHooks = logOffHooks;
    }
    /** Start to listen for log off request. */
    startListening() {
        window.onstorage = () => {
            if (this.requestLogOff)
                this.navigateLogOff();
        };
    }
    get requestLogOff() {
        return localStorage.getItem(STORAGE_KEY) === 'true';
    }
    set requestLogOff(value) {
        if (value == null)
            localStorage.removeItem(STORAGE_KEY);
        else
            localStorage.setItem(STORAGE_KEY, value.toString());
    }
    async invokePreLogOffHooks() {
        if (!this.logOffHooks || this.logOffHooks.length == 0)
            return;
        this.logOffHooks.forEach(async (logOffHook) => {
            await logOffHook.run();
        });
    }
    /** Performs some pre-log off logic. */
    async cleanup() {
        await this.globalStore.clear();
        if (this.state.basiscontext?.userId) {
            await this.configuration.flush();
            await this.security.flush();
        }
    }
    navigateLogOff() {
        // redirect to log off page
        window.location.href = this.options.auth?.logOffUrl || this.state.location.baasStatic.url + _BAAS_SHARED_CONSTANTS.PAGE.LOG_OFF;
    }
};
_BaasLogOffUtility = __decorate([
    injectable(),
    __param(4, inject(BarCacheStore)),
    __param(4, tagged(BAAS_METADATA_KEY.CACHE_STORE_TYPE_TAG, BarCacheStoreType.Global)),
    __param(5, multiInject(BAAS_TYPES.BaasLogOffHook)),
    __param(5, optional()),
    __metadata("design:paramtypes", [BaasBootstrapOptions,
        BaasAppState,
        BaasConfigurationService,
        BaasSecurityService,
        BarCacheStore, Array])
], _BaasLogOffUtility);

/** BaaS authentication service.*/
let BaasAuthService = class BaasAuthService {
    /** Gets whether authenticated. */
    get authenticated() {
        return this.strategy.authenticated;
    }
    /** Gets whether we are in the process of authenticating. */
    get authenticating() {
        return this.strategy.authenticating === true;
    }
    constructor(logOffService, strategy) {
        this.logOffService = logOffService;
        this.strategy = strategy;
    }
    /** Performs authentication.  */
    authenticate() {
        return this.strategy.authenticate();
    }
    /** Processing that occurs after the authentication. Meaning we are authenticated. */
    async postProcessing() {
        if (this.strategy.postProcessing)
            await this.strategy.postProcessing();
    }
    /** Performs the log off. */
    async logOff() {
        // first do some cleanup (state)
        await this.logOffService.cleanup();
        // invoke external hookds
        await this.logOffService.invokePreLogOffHooks();
        // notify other tabs about fact that we are logging off
        this.logOffService.requestLogOff = true;
        // do the log off (e.g. in idp this is a redirect)
        await this.strategy.performLogOff();
    }
};
BaasAuthService = __decorate([
    injectable(),
    __param(1, inject(BAAS_TYPES.BaasAuthStrategy)),
    __metadata("design:paramtypes", [_BaasLogOffUtility, Object])
], BaasAuthService);

var TokenType;
(function (TokenType) {
    TokenType[TokenType["BarSecTicket"] = 0] = "BarSecTicket";
    TokenType[TokenType["OidcLinkId"] = 1] = "OidcLinkId";
})(TokenType || (TokenType = {}));

class BarAppInfo {
}

/** Stores/retrieves baaS context header to window.sessionStorage
 *  This header is being used as the alternative for cookies
 */
let _BaasContextManager = class _BaasContextManager {
    /** saves the baas state context as a structure in sessiondata (for re-use in headers). Will only save when key does not exist */
    saveBaasContextToStorage(baasPersistentStateObject) {
        // Q&D enums from backend can become strings due to following setting:
        // o.SerializerSettings.Converters.Add(new Newtonsoft.Json.Converters.StringEnumConverter());
        // here we parse it back to a numeric enum (which is expected by the backend header parsing logic)
        if (typeof baasPersistentStateObject.tokenType === 'string') {
            const strTokenType = baasPersistentStateObject.tokenType;
            switch (strTokenType.toUpperCase()) {
                case 'BARSECTICKET':
                    baasPersistentStateObject.tokenType = TokenType.BarSecTicket;
                    break;
                case 'OIDCLINKID':
                    baasPersistentStateObject.tokenType = TokenType.OidcLinkId;
                    break;
                default:
                    baasPersistentStateObject.tokenType = undefined;
                    break;
            }
        }
        const jsonObj = JSON.stringify(baasPersistentStateObject);
        window.sessionStorage.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT, jsonObj);
        /*Session storage is boss here, but localstorage also serves as a "backup" for other apps that run on different browser windows (in Hybrid and Headeronly modus without cookies)*/
        window.localStorage.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT, jsonObj);
    }
    /** Updates the baas context (when it exists in storage) with altered voorwie + hoedanigheid */
    updateAppInfoToStorage(appInfo) {
        if (!this.checkBaasContextInStorage())
            return;
        const data = this.getBaasContextFromStorage();
        //only certain fields are allowed to be altered
        const vw = appInfo.ToepVoorwie ?? '';
        const vwrc = appInfo.ToepVoorwieRolcode ?? '';
        const hd = appInfo.ToepHoedanigheid ?? '';
        const hdrc = appInfo.ToepHoedanigheidRolcode ?? '';
        data.userVoorwie = `${vw};${vwrc}`;
        data.userHoedanigheid = `${hd};${hdrc}`;
        //choosing a(nother) testprofiel can also alter the user here!!
        data.userID = appInfo?.ToepUser ?? data.userID; //do not alter this when no other guid available (in case of external auth provider)
        data.userTicketGuid = appInfo?.SecurityTicketGuid ?? data.userTicketGuid;
        if (data.userTicketInfo == null)
            data.userTicketInfo = {};
        data.userTicketInfo.userCbiUserID = appInfo.CbiUserId;
        //save changed object in session state
        window.sessionStorage.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT, JSON.stringify(data));
        window.sessionStorage.removeItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAR_APPINFO_CONTEXT); //(cache cleared) needs to be recreated next time appinfo is retrieved
        window.localStorage.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT, JSON.stringify(data)); //keep localstorage in sync
    }
    /** detects if baaS context state is available in storage (alternative for cookies)*/
    checkBaasContextInStorage() {
        return this.#getBaasContextString() !== null;
    }
    /** retrieves baaS context from storage and converts it into appinfo object */
    getAppInfoFromStorage() {
        //this method is requested many times, so needs to be fast!
        const appInfoDataFromStorage = window.sessionStorage.getItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAR_APPINFO_CONTEXT);
        if (appInfoDataFromStorage !== null)
            return JSON.parse(appInfoDataFromStorage);
        //no appinfo in session store.. retrieve baas state insead, then convert it to appinfo, and store + return appinfo
        const data = this.getBaasContextFromStorage();
        const appinfo = this.#convertFromBaasState(data);
        window.sessionStorage.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAR_APPINFO_CONTEXT, JSON.stringify(appinfo)); //save for fast retrieval
        return appinfo;
    }
    removeContextFromSession() {
        window.sessionStorage.removeItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT);
        window.sessionStorage.removeItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAR_APPINFO_CONTEXT);
        window.localStorage.removeItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT); //keep localstore in sync
    }
    /** Retrieves the baasPersistentStateObject from storage if it exists */
    getBaasContextFromStorage() {
        const data = this.#getBaasContextString();
        if (data === null)
            return null;
        return JSON.parse(data);
    }
    #convertFromBaasState(baas) {
        const resp = new BarAppInfo();
        resp.AuditId = baas.auditID;
        resp.CbiUserId = baas.userTicketInfo?.userCbiUserID;
        resp.SecurityTicketGuid = baas.userTicketGuid;
        resp.UserZone = baas.userTicketInfo?.userZone;
        //old stuff that does not exist in BaaS
        resp.ForcePerformanceLog = false;
        resp.AllowCookieAuth = false;
        //old stuff ends
        resp.ExpireTicks = this.#generateDatum2DaysInFutureInticks(); //set dotnet ticks, 48 hrs in future
        resp.Timestamp = new Date().toString();
        resp.ToepVoorwie = this.#getPart(true, baas?.userVoorwie);
        resp.ToepVoorwieRolcode = this.#getPart(false, baas?.userVoorwie);
        resp.ToepHoedanigheid = this.#getPart(true, baas?.userHoedanigheid);
        resp.ToepHoedanigheidRolcode = this.#getPart(false, baas?.userHoedanigheid);
        resp.ToepUser = baas?.userID;
        return resp;
    }
    #generateDatum2DaysInFutureInticks() {
        const date = new Date();
        date.setTime(date.getTime() + 50 * 60 * 60 * 1000); //add 2 days + 2 hours time-zone compensation
        const ticks = date.getTime() * 10000 + 621355968000000000; //ticks from epoch (1970) to this date
        return ticks;
    }
    #getPart(getfirstPart, input) {
        if (input == null)
            return '';
        if (input.indexOf(';') == -1)
            return '';
        const segments = input.split(';');
        if (segments.length != 2)
            return '';
        return getfirstPart ? segments[0]?.trim() : segments[1]?.trim();
    }
    /** get raw string from session or local storage */
    #getBaasContextString() {
        let data = window.sessionStorage.getItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT);
        if (data === null) {
            data = window.localStorage.getItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT); //get it from local storage backup (and copy in sessionstorage) instead!
            if (data !== null)
                window.sessionStorage.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT, data);
        }
        return data;
    }
};
_BaasContextManager = __decorate([
    injectable()
], _BaasContextManager);

function toLowerCamelCaseKey(key) {
    if (!key)
        return key;
    const firstChar = key.charAt(0);
    if (firstChar === firstChar.toUpperCase() && firstChar !== firstChar.toLowerCase()) {
        // Only change if first character is an uppercase letter
        return firstChar.toLowerCase() + key.slice(1);
    }
    return key;
}
function transformToLowerCamelCase(obj) {
    if (Array.isArray(obj)) {
        return obj.map(transformToLowerCamelCase);
    }
    else if (obj !== null && typeof obj === 'object') {
        return Object.keys(obj).reduce((acc, key) => {
            const newKey = toLowerCamelCaseKey(key);
            acc[newKey] = transformToLowerCamelCase(obj[key]);
            return acc;
        }, {});
    }
    return obj;
}

let _BaasWebContextApiClient = class _BaasWebContextApiClient {
    #axios;
    #appState;
    constructor(axios, appState) {
        this.#axios = axios;
        this.#appState = appState;
    }
    async getStateContext() {
        const url = concatUrl(this.#appState.location.baasWeb.url, 'api/script-helper/provide-info');
        const response = await this.#axios.get(url);
        // NOTE backend returns the data in upper camel case, we need to transform it to lower camel case
        return transformToLowerCamelCase(response.data);
    }
    async getStateContextByTicketId(securityTicketId) {
        const url = concatUrl(this.#appState.location.baasWeb.url, 'api/script-helper-with-user/provide-header', securityTicketId);
        const response = await this.#axios.get(url);
        // NOTE backend returns the data in upper camel case, we need to transform it to lower camel case
        return transformToLowerCamelCase(response.data);
    }
};
_BaasWebContextApiClient = __decorate([
    injectable(),
    __param(0, inject(BAAS_TYPES.AxiosInstance)),
    __param(0, named(BAAS_AXIOS_INSTANCE_TYPES.Baas)),
    __metadata("design:paramtypes", [Function, BaasAppState])
], _BaasWebContextApiClient);

var BaasSecurityLevel;
(function (BaasSecurityLevel) {
    BaasSecurityLevel[BaasSecurityLevel["Development"] = 0] = "Development";
    BaasSecurityLevel[BaasSecurityLevel["Test"] = 1] = "Test";
    BaasSecurityLevel[BaasSecurityLevel["Production"] = 2] = "Production";
    BaasSecurityLevel[BaasSecurityLevel["Unknown"] = 3] = "Unknown";
})(BaasSecurityLevel || (BaasSecurityLevel = {}));

var BaasContextTransportModus;
(function (BaasContextTransportModus) {
    /** App info cookie is used to transport context data. */
    BaasContextTransportModus["Cookie"] = "Cookie";
    /** BaaS Backend header is used to transport data. */
    BaasContextTransportModus["Header"] = "Header";
})(BaasContextTransportModus || (BaasContextTransportModus = {}));

class BarAppInfoSecure {
}

// NOTE this service is not exposed to consumers
// NOTE partially copied from legacy bwcCookieUpdater.ts and util.ts
/**
 * This service will perform manipulations on the app info cookie or BaaS persistentstateobject (wrapper around cookie).
 */
let _BarAppInfoManager = class _BarAppInfoManager {
    #data;
    get sessionId() {
        return this.#readCookie(_BAAS_SHARED_CONSTANTS.COOKIE.APP_INFO.SESSION);
    }
    set sessionId(sessionId) {
        this.#createCookie(_BAAS_SHARED_CONSTANTS.COOKIE.APP_INFO.SESSION, sessionId);
    }
    get #secureCookieMissing() {
        return this.#readCookie(_BAAS_SHARED_CONSTANTS.COOKIE.APP_INFO.SECURE) === null;
    }
    constructor(state, baasContextService, options) {
        this.state = state;
        this.baasContextService = baasContextService;
        this.options = options;
    }
    /**
     * Passes initialization data.
     * @param data
     */
    init(data) {
        this.#data = data;
    }
    /**
     * Gets the app info data from the cookie or from stored BaaS persist object
     */
    get() {
        if (this.options.auth.contextTransportModus === BaasContextTransportModus.Header) {
            //do NOT get state from cookie but from session when that object is available
            if (this.baasContextService.checkBaasContextInStorage())
                return this.baasContextService.getAppInfoFromStorage();
            return null; //WARNING THIS MODUS IGNORES COOKIES
        }
        const koek = this.#readCookie(_BAAS_SHARED_CONSTANTS.COOKIE.APP_INFO.MAIN);
        if (koek == null)
            return null;
        // decode again because it is possible the cookie is double encoded (iframe...)
        const appInfo = JSON.parse(decodeURIComponent(koek));
        // dit zit normaal in secure cookie
        if (!appInfo.SecurityTicketGuid)
            appInfo.SecurityTicketGuid = this.#data?.state?.userTicketGuid;
        return appInfo;
    }
    /** Deletes the app info cookie and the secure cookie (if possible). */
    delete() {
        this.baasContextService.removeContextFromSession(); //equal to logoff
        if (this.options.auth.contextTransportModus === BaasContextTransportModus.Header)
            return; //WARNING THIS MODUS IGNORES COOKIES
        deleteCookie(_BAAS_SHARED_CONSTANTS.COOKIE.APP_INFO.MAIN);
        deleteCookie(_BAAS_SHARED_CONSTANTS.COOKIE.APP_INFO.SECURE);
    }
    /**
     * Saves the app info data
     * @param appInfo
     */
    async save(appInfo, createGuidCookie = false, setTimestamp = true, emit = true, syncContext = null) {
        if (setTimestamp) {
            appInfo.Timestamp = new Date().toISOString();
        }
        if (this.options.auth.contextTransportModus === BaasContextTransportModus.Header) {
            this.baasContextService.updateAppInfoToStorage(appInfo);
            return;
        }
        // save cookie (writes new cookie)
        this.#createCookie(_BAAS_SHARED_CONSTANTS.COOKIE.APP_INFO.MAIN, JSON.stringify(appInfo));
        if (createGuidCookie || this.#secureCookieMissing) {
            //also make guid cookie when missing
            const secureContent = new BarAppInfoSecure();
            secureContent.SecurityTicketGuid = appInfo.SecurityTicketGuid;
            secureContent.ExpireTicks = appInfo.ExpireTicks;
            this.#createCookie(_BAAS_SHARED_CONSTANTS.COOKIE.APP_INFO.SECURE, JSON.stringify(secureContent));
        }
        if (emit) {
            await this.#sync({ caller: 'Save', innerContext: syncContext });
        }
    }
    /**
     * Persists the modifiable data of the basiscontext into the app info cookie.
       This is used when having to make a request to the back-end.
     * @param basiscontext
     */
    async persistBasiscontext(syncContext) {
        if (this.state.basiscontext == null)
            return;
        const context = {
            caller: 'PersistBasiscontext',
            innerContext: syncContext
        };
        // first we sync cookies (or auto resolve in case not localhost)
        await this.#sync(context);
        const appInfo = this.get();
        if (appInfo != null) {
            // make sure not to put value null because this will lead to a null string after uri encode
            appInfo.ToepVoorwie = this.state.basiscontext.voorwie == null ? '' : this.state.basiscontext.voorwie;
            appInfo.ToepVoorwieRolcode = this.state.basiscontext.voorwieRolcode == null ? '' : this.state.basiscontext.voorwieRolcode;
            appInfo.ToepHoedanigheid = this.state.basiscontext.hoedanigheid == null ? '' : this.state.basiscontext.hoedanigheid;
            appInfo.ToepHoedanigheidRolcode = this.state.basiscontext.hoedanigheidRolcode == null ? '' : this.state.basiscontext.hoedanigheidRolcode;
            // save it
            await this.save(appInfo, false, true, true, context);
        }
    }
    /**
     * Derives the basis context from the app info cookie.
     */
    getBasiscontext() {
        const appInfo = this.get();
        return this.#toBasiscontext(appInfo);
    }
    #sync(context) {
        if (this.options.auth.contextTransportModus === BaasContextTransportModus.Header)
            //no not sync
            return Promise.resolve();
        // emit the event and pass the resolve reject
        return new Promise((resolve, reject) => {
            if (this.onSyncRequired) {
                this.onSyncRequired({ resolve, reject, context });
            }
        });
    }
    #toBasiscontext(appInfo) {
        let result = null;
        if (appInfo != null) {
            result = {};
            // assign values with trick for readonly values
            const hoedanigheidKey = 'hoedanigheid';
            result[hoedanigheidKey] = appInfo.ToepHoedanigheid;
            const hoedanigheidRolcodeKey = 'hoedanigheidRolcode';
            result[hoedanigheidRolcodeKey] = appInfo.ToepHoedanigheidRolcode;
            const voorwieKey = 'voorwie';
            result[voorwieKey] = appInfo.ToepVoorwie;
            const voorwieRolcodeKey = 'voorwieRolcode';
            result[voorwieRolcodeKey] = appInfo.ToepVoorwieRolcode;
            const userIdKey = 'userId';
            result[userIdKey] = appInfo.ToepUser;
            const securityTicketIdKey = 'securityTicketId';
            result[securityTicketIdKey] = appInfo.SecurityTicketGuid || '';
            const zoneKey = 'zone';
            result[zoneKey] = appInfo.UserZone || appInfo.ToepZone;
            // assign extra initialization data which does not exist in the cookie
            if (this.#data != null) {
                const fullUsernameKey = 'fullUsername';
                result[fullUsernameKey] = this.#data.fullUsername;
                const klantnummerKey = 'klantnummer';
                result[klantnummerKey] = this.#data.klantnummer;
                // define test profiel
                // if the user starts with DUMMY we know it is a test profiel
                if (result.userId != null && result.userId.toString().startsWith('DUMMY')) {
                    // now we need to substract the test profiel name
                    const testProfielKey = 'testProfiel';
                    try {
                        result[testProfielKey] = result.fullUsername.substr(result.fullUsername.indexOf('\\') + 1, result.fullUsername.indexOf('#') - 6);
                    }
                    catch (error) {
                        console.warn('BarAppInfoService: ', error);
                    }
                }
            }
            // assign extra data which exists outside the cookie
            const impersonateStrStorage = window.localStorage.getItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.IDP_IMPERSONATE);
            let impersonate = false;
            if (impersonateStrStorage != null && impersonateStrStorage.length) {
                impersonate = impersonateStrStorage.toLowerCase() === true.toString();
            }
            const impersonateKey = 'impersonate';
            result[impersonateKey] = impersonate;
        }
        return result;
    }
    #readCookie(name) {
        const nameEQ = name + '=';
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ')
                c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) {
                const cookieValue = decodeURIComponent(c.substring(nameEQ.length, c.length));
                return cookieValue === 'null' ? null : cookieValue;
            }
        }
        return null;
    }
    #createCookie(name, value) {
        let str = name + '=' + encodeURIComponent(value) + ';path=/;'; //wimfra
        if (this.state.toepassing != null && this.state.toepassing.securityLevel != BaasSecurityLevel.Production)
            str = str + 'SameSite=None;Secure=true;';
        document.cookie = str;
    }
};
_BarAppInfoManager = __decorate([
    injectable(),
    __metadata("design:paramtypes", [BaasAppState,
        _BaasContextManager,
        BaasBootstrapOptions])
], _BarAppInfoManager);

/** @experimental BaaS public context service which provides functionality related to the security context */
let BaasContextService = class BaasContextService {
    #apiClient;
    #contextManager;
    #appInfo;
    constructor(apiClient, contextManager, appInfo) {
        this.#apiClient = apiClient;
        this.#contextManager = contextManager;
        this.#appInfo = appInfo;
    }
    /** Gets the current security context. */
    async getStateContext() {
        const context = this.#contextManager.getBaasContextFromStorage();
        if (context)
            return context;
        const appInfo = this.#appInfo.get();
        if (appInfo) {
            // NOTE on deployed environment: get it from cookie
            return {
                appCode: appInfo.ToepCode,
                appOmgeving: appInfo.ToepSecurityEnvironment,
                appVersion: appInfo.ToepVersion,
                auditID: appInfo.AuditId,
                auditType: null,
                originalUserID: appInfo.OriginalUserID,
                tokenType: TokenType.BarSecTicket,
                userID: appInfo.ToepUser,
                userTicketGuid: appInfo.SecurityTicketGuid,
                userTicketInfo: {
                    userTicketName: appInfo.ToepUser,
                    userCbiUserID: appInfo.CbiUserId
                },
                userVoorwie: `${appInfo.ToepVoorwie};${appInfo.ToepVoorwieRolcode}`,
                userHoedanigheid: `${appInfo.ToepHoedanigheid};${appInfo.ToepHoedanigheidRolcode}`
            };
        }
        return await this.#apiClient.getStateContext();
    }
};
BaasContextService = __decorate([
    injectable(),
    __metadata("design:paramtypes", [_BaasWebContextApiClient, _BaasContextManager, _BarAppInfoManager])
], BaasContextService);

/** Generates the necessary headers for the given security context and current state. */
function generateBaasAuthHeaders(securityContext) {
    const headers = {};
    if (securityContext) {
        const contextValue = JSON.stringify(securityContext);
        // (old BAR apps only accept baas.context.backend headers)
        headers[_BAAS_SHARED_CONSTANTS.HEADER.BAAS_BACKEND_HEADER] = contextValue;
        // task 335295 (dotless headers)
        headers[_BAAS_SHARED_CONSTANTS.HEADER.BAAS_BACKEND_HEADER_V2] = contextValue;
    }
    return encodeHeaders(headers);
}

let BaasOidcAuthService = class BaasOidcAuthService {
    #options;
    #_userManager;
    get #userManager() {
        if (this.#_userManager === undefined)
            throw new Error('Oidc auth service has not been initialized. Call init() first.');
        return this.#_userManager;
    }
    /** Initializes the Oidc auth service with the given options. */
    init(options) {
        if (!options)
            throw new Error('Options are required to initialize the Oidc auth service.');
        if (this.#_userManager !== undefined)
            throw new Error('Oidc auth service is already initialized.');
        this.#options = options;
        const settings = {
            authority: options.authority,
            client_id: options.clientId,
            redirect_uri: options.callbackUri,
            response_type: 'code',
            scope: options.clientScope,
            userStore: new WebStorageStateStore({ store: window.localStorage }),
            fetchRequestCredentials: 'include',
            automaticSilentRenew: true,
            metadataUrl: options.metadataUrl
        };
        this.#_userManager = new UserManager(settings);
        this.#_userManager.events.addUserLoaded((user) => {
            if (options.userAddedCallback) {
                options.userAddedCallback(user);
            }
        });
    }
    getUser() {
        return this.#userManager.getUser();
    }
    login(identityType = 'windows') {
        return this.#userManager.signinRedirect({
            extraQueryParams: {
                identitytype: identityType
            },
            state: { targetUrl: window.location.href }
        });
    }
    logout() {
        return this.#userManager.signoutSilent();
    }
    renewToken(identityType = 'windows') {
        return this.#userManager.signinSilent({
            extraQueryParams: {
                identitytype: identityType
            }
        });
    }
    async callback() {
        const user = (await this.#userManager.signinCallback());
        const state = user?.state;
        if (state?.targetUrl) {
            window.location.href = state.targetUrl; // Redirect to the original URL
        }
        else {
            // Fallback if no targetUrl is available
            window.location.href = this.#options.defaultLandingPage;
        }
    }
};
BaasOidcAuthService = __decorate([
    injectable()
], BaasOidcAuthService);

class BarIdpError extends Error {
    constructor(message) {
        super(message); // (1)
        this.name = this.constructor.name;
        if (arguments.length > 1) {
            this.status = arguments[1]; // we houden de httpStatus bij
        }
    }
}
class BarIdpServerError extends BarIdpError {
}
class BarIdpClientError extends BarIdpError {
}

/** Options for idp client. */
class BarIdpClientOptions {
}
class BarAuthValues {
}
class BarIdentity {
}
class BarKlant {
}
var BarIdentityType;
(function (BarIdentityType) {
    BarIdentityType["Anoniem"] = "ANONIEM";
    BarIdentityType["CID"] = "CID";
    BarIdentityType["Windows"] = "WINDOWS";
    BarIdentityType["Cryptokey"] = "CRYPTOKEY";
    BarIdentityType["Klantnummer"] = "ALVNUMMER";
    BarIdentityType["Testprofiel"] = "TESTPROFIEL";
    BarIdentityType["Fas"] = "FAS";
})(BarIdentityType || (BarIdentityType = {}));

/*
 * De WebWorker Orchestrator zorgt voor het opstarten van de webworker.
 * Deze webworker zal om de 10 minuten (default waarde) de Idp validatie functie oproepen
 * Deze kan een config object ontvangen met de baseUrl en initiele Timeout setting
 */
class BarIdpWebworkerOrchestrator {
    constructor(idpUrl, wwUrl) {
        this.idpUrl = idpUrl;
        this.wwUrl = wwUrl;
        /* afhandelen van binnenkomende webworker messages */
        this.processMessage = function (event) {
            if (event) {
                const parsedData = JSON.parse(event.data);
                switch (Object.keys(parsedData)[0]) {
                    case 'INFO':
                        if (parsedData.INFO) {
                            console.info(`webworker INFO message : ${parsedData.INFO.message}`);
                        }
                        break;
                    case 'WARNING':
                        console.warn(`webworker WARNING message: ${parsedData.WARNING.message}`);
                        break;
                    case 'ERROR':
                        console.error(parsedData.ERROR);
                        break;
                }
            }
        };
        /* afhandelen van binnenkomende webworker errors */
        this.processError = function (errorEvent) {
            console.error(errorEvent);
            // bij catastrophale fout gaan we webworker stoppen (de webworker kan dit ook zelf door self.close() aan te roepen, maar wij doen het in de orchestrator via terminate()
            if (window.Worker && typeof this.idpww != 'undefined' && typeof this.idpww.terminate == 'function')
                this.idpww.terminate();
        };
    }
    // deze functie zorgt voor aanmaak van een idp webworker indien deze nog niet bestaat en initialiseert deze met ticketId, baseUrl, idpUrl, timeout
    init(timeout) {
        try {
            if (window.Worker) {
                if (typeof this.idpww == 'undefined') {
                    // in localhost we just point to the same origin because we cannot add workers from other domains (we can use a proxy)
                    let workerUrl = checkLocalhost() ? window.location.origin : this.wwUrl;
                    if (!workerUrl.endsWith('/'))
                        workerUrl += '/';
                    workerUrl += 'scripts/idp-validation-worker/main.js';
                    this.idpww = new Worker(workerUrl);
                    if (typeof this.idpww.postMessage == 'function') {
                        if (timeout != null) {
                            this.idpww.postMessage(JSON.stringify({ SETTIMEOUT: timeout }));
                        }
                        this.idpww.postMessage(JSON.stringify({
                            INIT: {
                                idpUrl: this.idpUrl,
                                timeout: timeout
                            }
                        }));
                    }
                    else {
                        console.warn('initWebworker : Er kon geen message verstuurd worden naar de webworker');
                    }
                    this.idpww.onmessage = (ev) => this.processMessage(ev);
                    this.idpww.onerror = (ev) => this.processError(ev);
                }
                else {
                    console.warn('initWebworker : initialisatie niet uitgevoerd, we hebben reeds een webworker');
                }
            }
            else {
                document.getElementById('result').innerHTML = 'Sorry, uw browser ondersteunt geen Web Workers...';
                console.error('Sorry, uw browser ondersteunt geen Web Workers...');
            }
        }
        catch (error) {
            console.error(error);
        }
    }
    setTimeout(timeout) {
        try {
            if (window.Worker && typeof this.idpww != 'undefined' && typeof this.idpww.postMessage == 'function')
                this.idpww.postMessage(JSON.stringify({ SETTIMEOUT: timeout }));
            else
                console.warn('setTimeout : Er kon geen message verstuurd worden naar de webworker');
        }
        catch (Error) {
            console.error('er ging iets mis voor setTimeout ' + timeout);
        }
    }
    getTimeout() {
        try {
            if (window.Worker && typeof this.idpww != 'undefined' && typeof this.idpww.postMessage == 'function')
                this.idpww.postMessage(JSON.stringify({ GETTIMEOUT: true }));
            else
                console.warn('getTimeout : Er kon geen message verstuurd worden naar de webworker');
        }
        catch (error) {
            console.error(error);
        }
    }
}

// https://stackoverflow.com/questions/15035473/destroying-webworkers
/* De authProviderclient is een security browser Relying party met volgende functies :
-  Authenticate(authValues);
    - redirect naar IdP (de redirect terug vanuit de IdP moet je niet opvangen, dit wordt afgehandeld door @BAR (ander backlog item))
    - het authValues object bevat alles values om de IdP aan te roepen  (Authenticatie via url met querystring parameters)
- Validate(guid);
    - deze zal web worker starten die om de 10 minuten de IdP validatie functie oproept (web api oproep)
    - een andere timeout kan meegegeven worden (in minuten)

*/
let BarIdpClient = class BarIdpClient {
    #wwMgr;
    #appInfoMgr;
    get authenticated() {
        return this.#appInfoCookie != null;
    }
    get #authUrl() {
        return concatUrl(this.options.idpUrl, '/auth/token');
    }
    get #logoutUrl() {
        return concatUrl(this.options.idpUrl, '/auth/logout');
    }
    get #appInfoCookie() {
        return this.#appInfoMgr.get();
    }
    constructor(options = new BarIdpClientOptions(), appInfoMgr) {
        this.options = options;
        this.appInfoMgr = appInfoMgr;
        this.#appInfoMgr = appInfoMgr;
    }
    /* Authenticatie voert een redirect uit naar de IdP en geeft de via config doorgegeven authenticatie waardes op
    (zoals identitytype, hoedanigheid, voorwie)
    Technische 'demonstratie' pagina : https://alv-dotnet4-d/a/idp/wui/auth/token?target=https://alv-dotnet4-d/a/idp/wui/tech
    Voorbeeld url 1 : https://alv-dotnet4-d/a/idp/wui/auth/token?target=https://alv-dotnet4-d/a/idp/wui/tech&identityType=ALVNUMMER&identityValue=00010001407&vw=00015782102&vwrc=LB
    Voorbeeld url 2 : https://alv-dotnet4-d/a/idp/wui/auth/token?target=https://alv-dotnet4-d/a/idp/wui/tech&identityType=WINDOWS&identityValue=GUNDAM
    */
    async authenticate(config = {
        identity: undefined,
        target: this.options.targetUrl
    }) {
        // Als er geen identity
        if (!config.identity) {
            if (this.options.zone === 'E') {
                config.identity = { type: BarIdentityType.Fas };
            }
            else {
                config.identity = { type: BarIdentityType.Windows };
            }
        }
        if (!this.options.idpUrl)
            throw new BarIdpClientError('De idpUrl is niet gedefinieerd.');
        let authUrl = this.#authUrl;
        /* target of default */
        authUrl = this.#appendQuerystring(authUrl, 'target', config.target || window.location.href);
        /* Identity is required */
        if (!config || !config.identity || !config.identity.type)
            throw Error('Fatale fout : er werd geen identiteitstype meegegeven');
        else
            authUrl = this.#appendQuerystring(authUrl, 'identityType', config.identity.type);
        /* identityvalue */
        if (config && config.identity && config.identity.value)
            authUrl = this.#appendQuerystring(authUrl, 'identityValue', config.identity.value);
        /* voorwie values */
        if (config && config.voorwie && config.voorwie.rolcode)
            authUrl = this.#appendQuerystring(authUrl, 'vw', config.voorwie.klantnummer);
        if (config && config.voorwie && config.voorwie.rolcode)
            authUrl = this.#appendQuerystring(authUrl, 'vwrc', config.voorwie.rolcode);
        /* hoedanigheid values */
        if (config && config.voorwie && config.hoedanigheid.klantnummer)
            authUrl = this.#appendQuerystring(authUrl, 'vw', config.hoedanigheid.klantnummer);
        if (config && config.voorwie && config.hoedanigheid.rolcode)
            authUrl = this.#appendQuerystring(authUrl, 'vwrc', config.hoedanigheid.rolcode);
        if (this.options.formatToken)
            authUrl = this.#appendQuerystring(authUrl, 'formattoken', this.options.formatToken);
        window.sessionStorage?.removeItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT); //idp service is cookie-first, so state in session storage must be cleared
        window.localStorage?.removeItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT); //idp service is cookie-first, so state in session storage must be cleared
        // NOTE use timeout to avoid problems in async pipe line
        window.location.replace(authUrl); // Redirect to the specified URL
    }
    authenticateWithAlvnr(identityValue) {
        return this.authenticate({
            identity: {
                type: BarIdentityType.Klantnummer,
                value: identityValue
            },
            target: this.options.targetUrl
        });
    }
    authenticateWithAnoniem() {
        return this.authenticate({
            identity: { type: BarIdentityType.Anoniem },
            target: this.options.targetUrl
        });
    }
    authenticateWithCBIuserId(identityValue) {
        return this.authenticate({
            identity: { type: BarIdentityType.CID, value: identityValue },
            target: this.options.targetUrl
        });
    }
    authenticateWithCryptoKey(identityValue) {
        return this.authenticate({
            identity: { type: BarIdentityType.Cryptokey, value: identityValue },
            target: this.options.targetUrl
        });
    }
    authenticateWithWindows(identityValue) {
        return this.authenticate({
            identity: { type: BarIdentityType.Windows, value: identityValue },
            target: this.options.targetUrl
        });
    }
    authenticateWithFas() {
        return this.authenticate({
            identity: { type: BarIdentityType.Fas },
            target: this.options.targetUrl
        });
    }
    authenticateWithTestProfiel(identityValue) {
        return this.authenticate({
            identity: {
                type: BarIdentityType.Testprofiel,
                value: identityValue
            },
            target: this.options.targetUrl
        });
    }
    // NOTE ticketid as an argument is obsolete and fixed with ng 18
    /* Deze functie start een webworker op die om de x minuten een validate functie zal aanroepen */
    validate(timeout) {
        if (!this.options.wwUrl)
            throw new BarIdpClientError('De web worker URL is niet gedefinieerd.');
        this.#wwMgr = new BarIdpWebworkerOrchestrator(this.options.idpUrl, this.options.wwUrl);
        if (timeout != null) {
            this.#wwMgr.init(timeout);
        }
        else {
            this.#wwMgr.init();
        }
    }
    logout(target) {
        let logoutUrl = this.#logoutUrl;
        if (target) {
            if (!checkUrlAbsolute(target))
                target = concatUrl(window.location.origin, target);
            logoutUrl += `?target=${target}`;
        }
        // just perform redirect
        window.location.replace(logoutUrl);
        return Promise.resolve();
    }
    #appendQuerystring(url, qsPrm, sqValue) {
        const separator = url.indexOf('?') > 0 ? '&' : '?';
        return url + separator + qsPrm + '=' + sqValue;
    }
};
BarIdpClient = __decorate([
    injectable(),
    __metadata("design:paramtypes", [Object, _BarAppInfoManager])
], BarIdpClient);

/**
 BaaS connectivity modus. In the future extra connectivities could be supported (no boolean).
 */
var BaasConnectivity;
(function (BaasConnectivity) {
    /** The application is online -> connect to remote BAR */
    BaasConnectivity["Online"] = "Online";
    /** The application is offline -> connect to local BAR (in the same process) */
    BaasConnectivity["Offline"] = "Offline";
})(BaasConnectivity || (BaasConnectivity = {}));

// NOTE based on BarI18nService
let BaasI18n = class BaasI18n {
    constructor(options) {
        this.options = options;
        // NOTE injected as singleton
        this.init();
    }
    changeLanguage(lang) {
        return i18next.changeLanguage(lang);
    }
    addTranslations(namespace, translations, language = 'nl') {
        const fullBundle = {};
        fullBundle[namespace] = translations;
        i18next.addResourceBundle(language, 'LV', fullBundle, true, true);
    }
    get(key, params = {}) {
        if (!i18next.exists(key)) {
            console.warn(`Translation key "${key}" does not exist.`);
            return '';
        }
        return i18next.t(key, params);
    }
    init() {
        const options = {
            lng: 'nl',
            defaultNS: 'LV',
            debug: false,
            resources: {
                nl: {
                    LV: {
                        INITIALIZED: 'i18next has been initialized'
                    }
                }
            }
        };
        if (this.options?.interpolation)
            options.interpolation = this.options.interpolation;
        i18next
            .init(options)
            .then(() => {
            console.log('i18next has been initialized');
        })
            .catch((error) => {
            console.error('Failed to initialize i18next:', error);
        });
    }
};
BaasI18n = __decorate([
    injectable(),
    __metadata("design:paramtypes", [Object])
], BaasI18n);

/** Internal enum representing the RAP segment in the URL. */
var BarRapSegment;
(function (BarRapSegment) {
    BarRapSegment["Applicative"] = "a";
    BarRapSegment["ReverseProxy"] = "r";
    BarRapSegment["Public"] = "p";
})(BarRapSegment || (BarRapSegment = {}));
/** Internal url to assist with RAP related stuff. */
let BarRapUrlHelper = class BarRapUrlHelper {
    get baasStaticSegment() {
        return _BAAS_SHARED_CONSTANTS.URL_SEGMENT.BAAS_STATIC.replace('{segment}', this.rapSegment);
    }
    get baasWebSegment() {
        return _BAAS_SHARED_CONSTANTS.URL_SEGMENT.BAAS_WEB.replace('{segment}', this.rapSegment);
    }
    get idpOidcWebSegment() {
        return _BAAS_SHARED_CONSTANTS.URL_SEGMENT.IDP_OIDC.replace('{segment}', this.rapSegment);
    }
    get rapSegment() {
        return this.config.public ? BarRapSegment.Public : BarRapSegment.Applicative;
    }
    constructor(config) {
        this.config = config;
    }
    parseUrl(url) {
        if (this.config.public && url.toLowerCase().indexOf(`/${BarRapSegment.Applicative}/`) > -1) {
            // Q&D for now we just do a replace (short term)
            return url.toLowerCase().replace(`/${BarRapSegment.Applicative}/`, `/${BarRapSegment.Public}/`);
        }
        return url;
    }
};
BarRapUrlHelper = __decorate([
    injectable(),
    __metadata("design:paramtypes", [BaasBootstrapOptions])
], BarRapUrlHelper);

/**
 * @deprecated Will be removed with next major version. Cookie based authentication will not work in favour of header based authenticatoin.
 * Provides cross domain cookie functionality.
 * creëert een iframe (indien toegestaan)
 * voorziet een sendMessage functie voor het versturen van strings naar de iframebron
 * luistert op het message event
 */
let _BarXdc = class _BarXdc {
    get enabled() {
        // enabled in release build with localhost client if security level is not production (if state.toepassing is not set yet we assume XDC might be on -> chicken or egg problem: we don't have this toepassing information yet in the beginning)
        // or enabled in local build if we force it for testing purposes
        // only enabled if online (never in offline or disconnected modus)
        return (this.options.localhost &&
            this.options.connectivity == BaasConnectivity.Online &&
            (!this.options._solution || this.options.auth?.forceRemote) &&
            this.options.auth.contextTransportModus === BaasContextTransportModus.Cookie &&
            (this.state.toepassing == null || this.state.toepassing.securityLevel != BaasSecurityLevel.Production));
    }
    constructor(translate, logging, options, appInfo, state, rapUrlHelper) {
        this.translate = translate;
        this.logging = logging;
        this.options = options;
        this.appInfo = appInfo;
        this.state = state;
        this.rapUrlHelper = rapUrlHelper;
        // holds all resolve and rejects for the synchronization with a guid as a key
        this.syncDefers = {};
        // defines whether sync has been disabled at runtime because of broken XDC, we need to do this to avoid syncen causing endless error loops
        this.syncDisabled = false;
        // assign callback function for syncing
        this.appInfo.onSyncRequired = (arg) => {
            this.sync(arg.context)
                .then(() => {
                arg.resolve();
            })
                .catch(() => {
                arg.reject();
            });
        };
    }
    /** Will sync the local cookie with the server cookie */
    // the sync is a chat between the main thread and an iframe using asynchronous communication
    // the main goal is to sync the local cookie and the server cookie by using a timestamp
    sync(context) {
        if (this.enabled && !this.syncDisabled) {
            return new Promise(async (resolve, reject) => {
                const id = generateUuid();
                this.syncDefers[id] = { resolve, reject };
                // get the server cookie first
                await this.sendMessage({ action: 'get', id: id, context: context });
            });
        }
        else {
            //disabled: auto resolve
            return Promise.resolve();
        }
    }
    /** Will clear the server cookies at the other end (iframe). */
    clear(context) {
        if (this.enabled && !this.syncDisabled) {
            return new Promise(async (resolve, reject) => {
                const id = generateUuid();
                this.syncDefers[id] = { resolve, reject };
                // get the server cookie first
                await this.sendMessage({ action: 'clear', id: id, context: context });
            });
        }
        else {
            //disabled: auto resolve
            return Promise.resolve();
        }
    }
    async handleMessage(e) {
        if (e == null || e.data == null || e.data.action == null)
            return;
        switch (e.data.action) {
            case 'ready': {
                // the iframe is ready
                // resolve the promise so that messages can be send
                this.resolveReady();
                break;
            }
            case 'get-response': {
                // if we don't have a local session we take over the remote session of the iframe
                if (this.appInfo.sessionId == null) {
                    this.appInfo.sessionId = e.data.sessionId;
                }
                // we have the server cookie
                const localCookie = this.appInfo.get();
                if (localCookie == null && e.data.cookie != null) {
                    // save without setting the timestamp + set guid cookie
                    await this.appInfo.save(e.data.cookie, true, false, false);
                    this.resolveSync(e.data.id);
                }
                else if (e.data.cookie == null && localCookie != null) {
                    await this.sendMessage({
                        action: 'set',
                        cookie: localCookie,
                        context: e.data.context,
                        id: e.data.id
                    });
                    // do not resolve yet
                }
                else if (e.data.cookie != null && localCookie != null) {
                    // convert timestamps to date time to be able to compare
                    const localTimestamp = localCookie.Timestamp == null ? null : new Date(localCookie.Timestamp);
                    const serverTimestamp = e.data.cookie.Timestamp == null ? null : new Date(e.data.cookie.Timestamp);
                    if (localTimestamp == null && serverTimestamp != null) {
                        // no timestamp locally -> take sever cookie as is and update main cookie
                        await this.appInfo.save(e.data.cookie, true, false, false);
                        this.resolveSync(e.data.id);
                    }
                    else if (localTimestamp != null && serverTimestamp == null) {
                        // do an update on the local cookie
                        await this.sendMessage({
                            action: 'update',
                            cookie: localCookie,
                            context: e.data.context,
                            id: e.data.id
                        });
                        // do not resolve yet
                    }
                    else if (localTimestamp != null && serverTimestamp != null) {
                        if (localTimestamp > serverTimestamp) {
                            // local cookie time is more recent -> update server cookie
                            await this.sendMessage({
                                action: 'update',
                                cookie: localCookie,
                                context: e.data.context,
                                id: e.data.id
                            });
                            // do not resolve yet
                        }
                        else if (localTimestamp < serverTimestamp) {
                            // server cookie time is more recent -> update local cookie
                            await this.appInfo.save(e.data.cookie, true, false, false);
                            this.resolveSync(e.data.id);
                        }
                        else {
                            // times are equal just resolve
                            this.resolveSync(e.data.id);
                        }
                    }
                    else {
                        // we cannot sync because times are null -> do nothing
                        this.resolveSync(e.data.id);
                    }
                }
                else {
                    // nothing to sync
                    this.resolveSync(e.data.id);
                }
                break;
            }
            case 'set-response':
            case 'update-response':
            case 'clear-response': {
                // end of response -> resolve the sync
                this.resolveSync(e.data.id);
                break;
            }
        }
    }
    ensureIFrame() {
        if (this.iframeEl != null)
            return;
        this.defineGlobal();
        // Create the iframe
        this.iframeEl = document.createElement('iframe');
        // if xdc is enabled in local solution this means we need to point to dev because of remote authentication
        const rootUrl = this.options._solution
            ? `${this.state.location.release.origin}${this.rapUrlHelper.baasStaticSegment}`
            : this.state.location.baasStatic.url;
        this.iframeEl.setAttribute('src', rootUrl + 'xdc');
        this.iframeEl.style.display = 'none';
        document.body.appendChild(this.iframeEl);
        this.bindEvent(window, 'message', async (e) => await this.handleMessage(e));
        // create the promise to be used when sending a message
        this.iframeReady = new Promise((resolve) => {
            // remember the resolve variable
            this.resolveReady = resolve;
        });
    }
    // addEventListener support for IE8
    bindEvent(element, eventName, eventHandler) {
        if (element.addEventListener) {
            element.addEventListener(eventName, eventHandler, false);
        }
        else if (element.attachEvent) {
            element.attachEvent('on' + eventName, eventHandler);
        }
    }
    // Send a message to the child iframe
    // Make sure you are sending a string, and to stringify JSON
    async sendMessage(msg) {
        this.ensureIFrame();
        // when the iframe is ready we send the message
        // we are dynamically adding the iframe to the DOM, this means it is not immediately ready (unless F12 is open)
        await this.iframeReady;
        try {
            this.iframeEl.contentWindow.postMessage(msg, '*');
        }
        catch (error) {
            const xdcErrorMsg = this.translate.get('DOMAIN_BAR_SHARED.XDC_ERROR');
            console.error(xdcErrorMsg);
            console.error(error);
            // keep the promise (avoid promise error cycle by not rejecting)
            this.resolveSync(msg.id);
            // disable xdc sync
            this.syncDisabled = true;
            console.warn(this.translate.get('DOMAIN_BAR_SHARED.XDC_SYNC_DISABLED'));
            this.logging.error({
                error: error,
                message: xdcErrorMsg
            });
        }
    }
    // define some global stuff
    defineGlobal() {
        // make sure the state is available globally for non Angular code
        const xdc = ensureGlobal('lvflanders.bar.shared.xdc');
        Object.defineProperty(xdc, 'loggingEnabled', {
            set: async (value) => {
                await this.sendMessage({
                    action: 'set-logging-enabled',
                    value: value
                });
            },
            configurable: true
        });
    }
    resolveSync(id) {
        if (this.syncDefers[id] != null) {
            this.syncDefers[id].resolve();
            delete this.syncDefers[id];
        }
    }
};
_BarXdc = __decorate([
    injectable(),
    __metadata("design:paramtypes", [BaasI18n,
        BaasLoggingService,
        BaasBootstrapOptions,
        _BarAppInfoManager,
        BaasAppState,
        BarRapUrlHelper])
], _BarXdc);

let _BaasStaticApiClient = class _BaasStaticApiClient {
    #appState;
    #axiosPlain;
    constructor(axiosPlain, appState) {
        this.#axiosPlain = axiosPlain;
        this.#appState = appState;
    }
    /** Resets the server-side state by deleting all cookies. */
    async resetState() {
        const url = concatUrl(this.#appState.location.baasStatic.url, 'api/state/reset');
        await this.#axiosPlain.get(url, { withCredentials: true });
    }
};
_BaasStaticApiClient = __decorate([
    injectable(),
    __param(0, inject(BAAS_TYPES.AxiosInstance)),
    __param(0, named(BAAS_AXIOS_INSTANCE_TYPES.Plain)),
    __metadata("design:paramtypes", [Function, BaasAppState])
], _BaasStaticApiClient);

var _OopsType;
(function (_OopsType) {
    _OopsType[_OopsType["Technical"] = 0] = "Technical";
    _OopsType[_OopsType["Authentication"] = 1] = "Authentication";
    _OopsType[_OopsType["NotFound"] = 2] = "NotFound";
})(_OopsType || (_OopsType = {}));
const ACTION_ICON_REFRESH = 'fa-solid fa-rotate';
const ACTION_CONTENT_REFRESH = 'Herladen';
const ACTION_INFO_REFRESH = 'Klik op herladen om opnieuw aan te melden.';
let _OopsDomService = class _OopsDomService {
    #appState;
    #baasStaticApiClient;
    #appInfoManager;
    #bootstrapOptions;
    #mappings = {
        [_OopsType.Technical]: {
            title: 'Technische fout',
            content: 'Er heeft zich een technische fout voorgedaan.',
            action: this.#oopsActionRefresh.bind(this),
            actionIcon: ACTION_ICON_REFRESH,
            actionContent: ACTION_CONTENT_REFRESH,
            actionInfo: ACTION_INFO_REFRESH
        },
        [_OopsType.Authentication]: {
            title: 'Sessie verlopen',
            content: 'Uw huidige sessie is verlopen.',
            action: this.#oopsActionRefresh.bind(this),
            actionIcon: ACTION_ICON_REFRESH,
            actionContent: ACTION_CONTENT_REFRESH,
            actionInfo: ACTION_INFO_REFRESH
        },
        [_OopsType.NotFound]: {
            title: 'Oeps',
            content: 'Deze pagina is vermist.',
            action: this.#oopsActionHome.bind(this),
            actionIcon: 'fa-regular fa-house',
            actionContent: 'Terug naar Startscherm'
        }
    };
    constructor(appState, baasStaticApiClient, appInfoManager, bootstrapOptions) {
        this.#appState = appState;
        this.#baasStaticApiClient = baasStaticApiClient;
        this.#appInfoManager = appInfoManager;
        this.#bootstrapOptions = bootstrapOptions;
    }
    checkOopsSupported() {
        return document.getElementById('oops') != null;
    }
    checkOopsVisible() {
        const el = document.getElementById('oops');
        return el != null && el.style.display === 'block';
    }
    showOops(settings) {
        if (!settings)
            throw new Error('Oops settings are required.');
        if (!this.checkOopsSupported()) {
            console.warn('Oops DOM is not supported.');
            return;
        }
        const oopsElement = document.getElementById('oops');
        if (this.#appState?.location?.baasStatic?.url) {
            let image;
            let imgElement = document.getElementById('oops-image');
            // @deprecated old img id wrong convention
            if (imgElement == null)
                imgElement = document.getElementById('oopsImg');
            if (settings.type === _OopsType.NotFound)
                image = 'lv-melding-error-404.png';
            else {
                const number = generateRandomNumber(1, 8);
                image = `lv-melding-circle-0${number}.png`;
            }
            imgElement.src = concatUrl(this.#appState.location.baasStatic.url, '/images/all/', image);
        }
        // new mechanism with oops
        oopsElement.style.display = 'block';
        const appRoot = document.getElementById('app-root');
        if (appRoot)
            appRoot.parentElement.removeChild(appRoot);
        const loader = document.getElementById('app-loading');
        loader.style.display = 'none';
        const config = this.#mappings[settings.type];
        const titleElement = document.getElementById('oops-title');
        if (titleElement)
            titleElement.innerText = config.title;
        const contentElement = document.getElementById('oops-content');
        if (contentElement)
            contentElement.innerText = config.content;
        const actionElement = document.getElementById('oops-action');
        if (actionElement)
            actionElement.addEventListener('click', config.action);
        const actionIconElement = document.getElementById('oops-action-icon');
        if (actionIconElement)
            actionIconElement.className = config.actionIcon;
        const actionContentElement = document.getElementById('oops-action-content');
        if (actionContentElement)
            actionContentElement.innerText = config.actionContent;
        const actionInfoElement = document.getElementById('oops-action-info');
        if (actionInfoElement) {
            actionInfoElement.style.display = config.actionInfo ? 'block' : 'none';
            actionInfoElement.innerText = config.actionInfo;
        }
    }
    async #oopsActionRefresh() {
        // get appinfo before we clear out all cookies
        const appInfo = this.#appInfoManager.get();
        window.localStorage.clear();
        window.sessionStorage.clear();
        await deleteAllIndexedDb();
        deleteAllCookies();
        // reset server-side state (cookies) then navigate home
        await this.#baasStaticApiClient.resetState();
        // NOTE very defensive approach where we clear everything and don't assume everything is loaded (e.g. config)
        if (checkLocalhost() || this.#bootstrapOptions.public) {
            const appUrl = this.#appState?.location?.app?.url;
            if (appUrl)
                window.location.href = appUrl;
            else
                window.location.reload();
        }
        else {
            const environment = this.#appState?.toepassing?.omgeving ?? appInfo?.ToepSecurityEnvironment;
            const isDmzetst = environment === 'DMZETST';
            const redirectUrl = isDmzetst
                ? 'https://alv-dotnet4-t/a/auth/web/bui/'
                : (this.#appState?.config?.homeUrl ?? window.location.origin + _BAAS_SHARED_CONSTANTS.URL_SEGMENT.STARTSCHERM);
            window.location.href = redirectUrl;
        }
    }
    #oopsActionHome() {
        if (this.#appState?.config?.homeUrl)
            window.location = this.#appState.config.homeUrl;
        return Promise.resolve();
    }
};
_OopsDomService = __decorate([
    injectable(),
    __param(0, inject(BaasAppState)),
    __param(0, optional()),
    __param(1, inject(_BaasStaticApiClient)),
    __param(2, inject(_BarAppInfoManager)),
    __param(3, inject(BaasBootstrapOptions)),
    __metadata("design:paramtypes", [BaasAppState,
        _BaasStaticApiClient,
        _BarAppInfoManager,
        BaasBootstrapOptions])
], _OopsDomService);

// NOTE never expose this.
/** Internal types. */
const _BAAS_TYPES = {
    BarGvkState: Symbol('BarGvkState'),
    BarHubMessageHandler: Symbol('BarHubMessageHandler'),
    BaasApm: Symbol('BaasApm')
};

let BarHubClient = class BarHubClient {
    #handlers;
    #connection;
    constructor(handlers) {
        this.retryCount = 1; // 1 as initial call always gets fired
        this.#handlers = handlers;
    }
    /** Initializes the connection to the hub and starts it. */
    init(options) {
        const neededHeaders = {
            'communication-app-code': options.appCode,
            'communication-user-token': options.securityTicketId
        };
        const connUrl = options?.hubUrl + '?connectionIdentifier=' + generateUuid();
        this.#connection = new signalR.HubConnectionBuilder()
            .withUrl(connUrl, { headers: neededHeaders })
            .withAutomaticReconnect()
            // NOTE we don't use a truthy check because 0 is a valid value (trace level)
            // by default log level none
            .configureLogging(options.logLevel == null ? signalR.LogLevel.None : options.logLevel)
            .build();
        this.#connection.onclose(async () => {
            await this.#start({ userId: options?.userId, appCode: options.appCode, securityTicketId: options.securityTicketId });
        });
        this.#connection.on('ReceiveMessage', (message) => {
            const handlers = this.#handlers.filter((h) => h.action === message.action);
            if (handlers.length)
                handlers.forEach((handler) => handler.handle(message.detail));
            else
                console.warn(`No handler found for hub messages of type ${message.action}.`, message);
        });
        this.#start({ userId: options?.userId, appCode: options.appCode, securityTicketId: options.securityTicketId });
    }
    async #start(registration) {
        try {
            await this.#connection.start();
            console.info('Connected to Bar SignalR');
        }
        catch (e) {
            console.error(e);
            if (this.retryCount >= 5) {
                console.error('Exceeded maximum retry times for connecting SignalR Hub (5).');
            }
            else {
                this.retryCount++;
                setTimeout(() => this.#start(registration), 2000);
            }
        }
    }
};
BarHubClient = __decorate([
    injectable(),
    __param(0, multiInject(_BAAS_TYPES.BarHubMessageHandler)),
    __metadata("design:paramtypes", [Array])
], BarHubClient);

const BAAS_SHARED_I18N = {
    STARTUP_ERROR: 'Er heeft zich een fout voorgedaan bij het opstarten.',
    INIT_LOCAL_FAILED: 'Local init has failed. The localhost web api server may not be running or a server-side error has occured.',
    PRE_INIT_LOCAL_FAILED: 'Local pre-init has failed. The localhost web api server may not be running or a server-side error has occured.',
    XDC_ERROR: 'Fout bij het synchroniseren (XDC).',
    XDC_SYNC_DISABLED: 'XDC synchronisatie uitgeschakeld.',
    TARGET_ORIGIN_OVERRIDE_WARNING: 'Target/release origin has been overridden by "{{targetOrigin}}". This can lead to undesired behavior.',
    AUTH_ERROR: 'An error occurred while authenticating during the bootstrapping of the application.'
};

let BaasMeetpuntService = class BaasMeetpuntService {
    /** Whether performance logging is enabled:
        - must be enabled in config
        - must be enabled by cookie OR enabled in configuation for the toepassing
    */
    get loggingEnabled() {
        const appinfo = this.appInfo.get();
        // meetpunt must be enabled globally
        return (this.state.config?.meetpunt.enabled &&
            // we must be authenticated
            appinfo &&
            // must be enforced by cookie OR must be set on in configuration for the toepassing
            (appinfo.ForcePerformanceLog || this.state.meetpuntenAutomatischAanmaken) &&
            // must be supported by browser to perform measurements
            window.performance &&
            window.performance.timing != null);
    }
    constructor(state, appInfo, apiClient) {
        this.state = state;
        this.appInfo = appInfo;
        this.apiClient = apiClient;
        // instantiate the queue with session storage
        this.queueProcessor = new BarQueueProcessor(window.sessionStorage, _BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + 'meetpuntQueue');
    }
    /**
     * Logs a meetpunt.
     * @param meetpunt
     */
    log(meetpunt) {
        if (!this.loggingEnabled)
            return;
        if (meetpunt.appCode == null) {
            meetpunt.appCode = this.state.toepassing.code;
        }
        if (meetpunt.meetpuntType == null) {
            meetpunt.meetpuntType = _BAAS_SHARED_CONSTANTS.DEFAULT_MEETPUNT_TYPE;
        }
        this.queueProcessor.enqueue(meetpunt);
    }
    /**
     * Start to listen to the meetpunten queue.
     */
    async start() {
        // post items to back-end with interval
        this.queueProcessor.onExecute = async (meetpunten) => {
            await this.apiClient.save(meetpunten);
        };
        // start listening to the queue
        this.queueProcessor.start(this.state.config.meetpunt.interval, this.state.config.meetpunt.itemCount);
    }
};
BaasMeetpuntService = __decorate([
    injectable(),
    __param(2, inject(BAAS_TYPES.BaasMeetpuntApiClient)),
    __metadata("design:paramtypes", [BaasAppState,
        _BarAppInfoManager, Object])
], BaasMeetpuntService);

/**
 * Browser capabilities detected during bootstrap.
 * Provides read-only access to browser feature support and permissions.
 */
let BaasBrowserCapabilities = class BaasBrowserCapabilities {
    /**
     * Storage Access API status for cross-origin iframe support (iOS Safari/WebKit).
     *
     * **Important:** This is NOT about IndexedDB/localStorage support.
     * This is about the Storage Access API - a browser permission mechanism
     * that allows embedded iframes to request access to unpartitioned cookies and storage.
     *
     * Required for Safari/WebKit which partitions storage in cross-origin iframes by default.
     * Set during bootstrap via requestStorageAccess().
     *
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Storage_Access_API
     */
    get storageAccess() {
        return getStorageAccess();
    }
    /**
     * Browser Notification API support and permission status.
     * Check this before attempting to show notifications.
     */
    get notifications() {
        const supported = typeof Notification !== 'undefined';
        return {
            supported,
            permission: supported ? Notification.permission : undefined
        };
    }
};
BaasBrowserCapabilities = __decorate([
    injectable()
], BaasBrowserCapabilities);

/** Manages browser notifications with service workers. */
let BarNotificationManager = class BarNotificationManager {
    constructor(browserCapabilities) {
        this.browserCapabilities = browserCapabilities;
    }
    /** Will init the notification system and ask for permissions. */
    async init() {
        if (!this.browserCapabilities.notifications.supported) {
            console.warn('Notification API is not supported on this device.');
            return;
        }
        const status = await Notification.requestPermission();
        console.info('Notification permission status:', status);
    }
    /** Shows a notification. */
    async show(notification) {
        if (!this.browserCapabilities.notifications.supported) {
            console.warn('Notification API is not supported on this device.');
            return;
        }
        if (this.browserCapabilities.notifications.permission === 'granted') {
            const reg = await navigator.serviceWorker.getRegistration();
            if (reg)
                await reg.showNotification(notification.title, notification.options);
            else {
                console.warn('No service worker registration retrieved to show notification.');
            }
        }
    }
};
BarNotificationManager = __decorate([
    injectable(),
    __param(0, inject(BaasBrowserCapabilities)),
    __metadata("design:paramtypes", [BaasBrowserCapabilities])
], BarNotificationManager);

const INACTIVE_MESSAGE = 'APM is not active.';
/** APM service. */
let _BaasApm = class _BaasApm {
    #alreadyWarnedInactive = false;
    constructor(bootstrapOptions, state) {
        this.bootstrapOptions = bootstrapOptions;
        this.state = state;
    }
    get active() {
        const serverAllows = this.#serverConfig.rumActive === true;
        switch (this.#clientConfig.enabled) {
            case 'force-on':
                return true;
            //@deprecated boolean value is deprecated
            case false:
            case 'force-off':
                return false;
            case 'allow-local':
                return serverAllows;
            case 'inherit':
            default: {
                // by default not allowed for localhost
                if (checkLocalhost())
                    return false;
                return serverAllows;
            }
        }
    }
    get #apm() {
        if (!this.apm)
            throw new Error('APM is not initialized');
        return this.apm;
    }
    /** Gets the configuration from the client through bootstrapping options. */
    get #clientConfig() {
        return this.bootstrapOptions.telemetry || {};
    }
    /** Get the central configuration on the server. */
    get #serverConfig() {
        return this.state.config?.baasStatic?.telemetry || {};
    }
    /** Inits APM for BaaS. */
    init() {
        if (this.active) {
            let distributedTracing = this.#serverConfig.distributedTracing;
            if (distributedTracing === undefined)
                distributedTracing = false;
            const ignoreTransactions = this.#parseIgnorePatterns(this.#serverConfig.ignoreTransactions);
            const logLevel = this.#serverConfig.logLevel ?? (this.state.toepassing.securityLevel === BaasSecurityLevel.Production ? 'warn' : 'debug');
            this.apm = init({
                serverUrl: this.#serverConfig.rumServerUrl,
                serviceName: this.state.toepassing.code,
                serviceVersion: this.state.toepassing.versie,
                environment: this.state.toepassing.omgeving,
                logLevel,
                distributedTracing,
                pageLoadTransactionName: window.location.pathname,
                transactionSampleRate: this.#serverConfig.transactionSampleRate ?? 1.0,
                ignoreTransactions
            });
            const ignoreUrls = this.#parseIgnorePatterns(this.#serverConfig.ignoreUrls);
            if (ignoreUrls?.length) {
                // ignoreTransactions only matches transaction names (no query string).
                // ignoreUrls filters on span level using the full URL (including query string),
                // which is how OGC/WMS requests appear in Kibana (processor.event=span, url.original).
                this.apm.addFilter((payload) => {
                    const shouldIgnore = (url) => ignoreUrls.some((pattern) => (pattern instanceof RegExp ? pattern.test(url) : url.toLowerCase() === pattern.toLowerCase()));
                    const filterSpans = (spans) => spans.filter((span) => {
                        const url = span.context?.http?.url;
                        return url ? !shouldIgnore(url) : true;
                    });
                    if (payload.spans) {
                        payload.spans = filterSpans(payload.spans);
                    }
                    if (payload.transactions) {
                        for (const t of payload.transactions) {
                            if (t.spans) {
                                t.spans = filterSpans(t.spans);
                            }
                        }
                    }
                    return payload;
                });
            }
            // For page-exit: the transaction is created on beforeunload (after the click), so we
            // track the last clicked anchor href separately to attach it to the transaction.
            let lastAnchorHref = null;
            document.addEventListener('click', (event) => {
                const anchor = event.target?.closest('a');
                lastAnchorHref = anchor?.getAttribute('href') ?? null;
            }, { capture: true });
            this.apm.observe('transaction:end', (tr) => {
                switch (tr.type) {
                    case 'page-load':
                        // NOTE sometimes we could still observe query string in transaction name for page-load
                        tr.name = window.location.pathname;
                        break;
                    case 'user-interaction': {
                        // NOTE more context on clicks directly visible in APM
                        const classes = tr.context?.custom?.classes;
                        if (classes)
                            tr.name += `.${classes.replace(' ', '.')}`;
                        break;
                    }
                    case 'page-exit': {
                        if (lastAnchorHref)
                            tr.addContext({ custom: { href: lastAnchorHref } });
                        lastAnchorHref = null;
                        break;
                    }
                }
            });
            if (this.state.basiscontext) {
                this.apm.setUserContext({
                    username: this.state.basiscontext.fullUsername,
                    id: this.state.basiscontext.userId
                });
                if (this.state.basiscontext.voorwie) {
                    this.apm.addLabels({
                        uservoorwie: `${this.state.basiscontext.voorwie};${this.state.basiscontext.voorwieRolcode}`
                    });
                }
                if (this.state.basiscontext.hoedanigheid) {
                    this.apm.addLabels({
                        userhoed: `${this.state.basiscontext.hoedanigheid};${this.state.basiscontext.hoedanigheidRolcode}`
                    });
                }
            }
            return true;
        }
        this.#notActiveWarning();
        return false;
    }
    startTransaction(name, type) {
        if (!this.active)
            throw new Error(INACTIVE_MESSAGE);
        return this.#apm.startTransaction(name, type);
    }
    handleError(error) {
        if (this.active)
            this.#apm.captureError(error.originalError || error);
        else
            this.#notActiveWarning();
    }
    #notActiveWarning() {
        if (!this.#alreadyWarnedInactive) {
            console.warn(INACTIVE_MESSAGE);
            this.#alreadyWarnedInactive = true;
        }
    }
    #parseIgnorePatterns(value) {
        if (!value)
            return undefined;
        return value
            .map((v) => v.trim())
            .filter(Boolean)
            .map((v) => {
            if (v.startsWith('regex:')) {
                // case insensitive
                return new RegExp(v.slice(6), 'i');
            }
            if (v.startsWith('exact:')) {
                return v.slice(6);
            }
            return v;
        });
    }
};
_BaasApm = __decorate([
    injectable(),
    __metadata("design:paramtypes", [BaasBootstrapOptions,
        BaasAppState])
], _BaasApm);

const STORAGE_KEY_USER_INFO_PREFIX = _BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + 'userInfo_';
let BaasBootstrap = class BaasBootstrap {
    #stopwatch;
    #outcome;
    // holds the target origin
    #targetOrigin;
    // whether the configuration has been handled and assigned
    #configHandled = false;
    get #mustAuthenticate() {
        return this.options.auth != null ? this.options.auth.enabled : false;
    }
    get #authFailedUrl() {
        return this.state.config.auth.failedUrl;
    }
    get #targetOriginOverride() {
        return window.localStorage.getItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + 'targetOrigin');
    }
    set #targetOriginOverride(value) {
        if (value == null) {
            window.localStorage.removeItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + 'targetOrigin');
        }
        else {
            window.localStorage.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + 'targetOrigin', value);
        }
    }
    constructor(state, options, idpClient, logOff, i18n, xdc, cacheManager, baasContextService, globalStore, rapUrlHelper, auth, appInfo, audit, commonApiClient, securityApiClient, _BaasStaticConfigurationApiClient, meetpunt, logging, hubClient, notificationManager, oopsDomService, apm, telemetry, oidcAuthService) {
        this.state = state;
        this.options = options;
        this.idpClient = idpClient;
        this.logOff = logOff;
        this.i18n = i18n;
        this.xdc = xdc;
        this.cacheManager = cacheManager;
        this.baasContextService = baasContextService;
        this.globalStore = globalStore;
        this.rapUrlHelper = rapUrlHelper;
        this.auth = auth;
        this.appInfo = appInfo;
        this.audit = audit;
        this.commonApiClient = commonApiClient;
        this.securityApiClient = securityApiClient;
        this._BaasStaticConfigurationApiClient = _BaasStaticConfigurationApiClient;
        this.meetpunt = meetpunt;
        this.logging = logging;
        this.hubClient = hubClient;
        this.notificationManager = notificationManager;
        this.oopsDomService = oopsDomService;
        this.apm = apm;
        this.telemetry = telemetry;
        this.oidcAuthService = oidcAuthService;
    }
    #startTime;
    async bootstrap(/** @deprecated*/ options) {
        this.#startTime = performance.now();
        this.#outcome = undefined;
        if (this.options.auth.contextTransportModus === BaasContextTransportModus.Cookie) {
            // persist basiscontext whenever tab gets focus or we are leaving the tab
            // basiscontext can be different per tab but there is only one cookie
            document.addEventListener('visibilitychange', (ev) => {
                this.appInfo.persistBasiscontext({
                    caller: 'Bootstrap-VisibilityChange'
                });
            });
        }
        this.i18n.addTranslations('DOMAIN_BAR_SHARED', BAAS_SHARED_I18N);
        this.#configHandled = false;
        // clear out request log off
        this.logOff.requestLogOff = undefined;
        await this.#launchLifeCycle();
        return this.#outcome;
    }
    async #launchLifeCycle() {
        this.#defineLogging();
        // Request storage access early for cross-origin iframe support (iOS Safari)
        // This is required before any IndexedDB operations
        await requestStorageAccess();
        this.idpClient.options.targetUrl = window.location.href;
        // generate the page id
        const pageIdKey = 'pageId';
        this.state[pageIdKey] = generateUuid();
        const connectivityKey = 'connectivity';
        this.state[connectivityKey] = this.options.connectivity || BaasConnectivity.Online;
        const locationKey = 'location';
        this.state[locationKey] = {};
        const appRootKey = 'appRoot';
        this.state.location[appRootKey] = extractUrlParts(this.#defineAppRootUrl());
        const appKey = 'app';
        this.state.location[appKey] = extractUrlParts(this.#defineAppUrl());
        const apiRootKey = 'apiRoot';
        this.state.location[apiRootKey] = extractUrlParts(this.#defineApiRootUrl());
        if (this.options.localhost) {
            //localhost needs pre-init-local call to function
            const core = ensureGlobal('lvflanders.bar.shared');
            // and here we make a hook to overide the targetOrigin through the console
            Object.defineProperty(core, 'targetOrigin', {
                get: () => {
                    return this.#targetOriginOverride;
                },
                set: (value) => {
                    this.#targetOriginOverride = value;
                },
                configurable: true
            });
            // use the target origin override (set via developer tools console if defined)
            this.#targetOrigin = this.#targetOriginOverride;
            // we don't have a target origin override check if we have it in session storage
            if (this.#targetOrigin == null) {
                // if xdc is enabled we need to define the target origin to which to connect to (BAR, basiscomponenten, iframe).
                // we remember this in the session storage to avoid performing the pre-init-local constantly
                this.#targetOrigin = window.sessionStorage.getItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + 'targetOrigin');
            }
            else {
                const msg = this.i18n.get('DOMAIN_BAR_SHARED.PRE_INIT_LOCAL_FAILED', { targetOrigin: this.#targetOrigin });
                console.warn(msg);
            }
            if (!this.#targetOrigin) {
                try {
                    // perform a local api call to get the target origin
                    const data = await this.commonApiClient.preInitLocal();
                    this.#targetOrigin = data.targetOrigin || data.TargetOrigin;
                    if (this.#targetOrigin != null) {
                        window.sessionStorage.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + 'targetOrigin', this.#targetOrigin);
                    }
                }
                catch (error) {
                    const msg = this.i18n.get('DOMAIN_BAR_SHARED.PRE_INIT_LOCAL_FAILED');
                    console.warn(msg);
                }
            }
        }
        // we can define the release state location here because a value depends on the pre-init-local (release domain aka target)
        const releaseKey = 'release';
        this.state.location[releaseKey] = extractUrlParts(this.#defineReleaseOrigin());
        const barWebKey = 'barWeb';
        this.state.location[barWebKey] = extractUrlParts(this.#defineBarWebRootUrl());
        const baasStaticKey = 'baasStatic';
        this.state.location[baasStaticKey] = extractUrlParts(this.#defineBaasStaticRootUrl());
        const baasWebKey = 'baasWeb';
        this.state.location[baasWebKey] = extractUrlParts(this.#defineBaasWebRootUrl());
        this.idpClient.options.wwUrl = this.state.location.baasStatic.url;
        // NOTE already define the idp root url because oops page refresh must go to idp (even if we didnt get the (pre-)init config data)
        // set a default idp url origin before config is loaded (config will override with the full idpRootUrl later)
        this.#defineDefaultIdpRootUrl();
        // different steps (in case not authenticated):
        // 1-a) pre - init local (only in XDC modus)
        // 1-b) pre - init (if not authenticated)
        // 2) authenticate (if not authenticated)
        // 3-a) init
        // 3-b) init local (only in XDC modus)
        // if not authenticated we perform the pre-init to retrieve unauthenticated data
        if (!this.auth.authenticated)
            await this.#launchPreInit();
        // we are still in the process of authenticating (redirecting from idp)
        else if (this.auth.authenticating && this.#mustAuthenticate)
            await this.#launchAuth(true);
        else
            await this.#launchInit(true);
    }
    #getInitRequest(loadConfig) {
        // make request object needed for pre-init or init
        return {
            appCode: this.options.appCode,
            appVersion: this.options.appVersion,
            loadConfig,
            loadToepassing: false,
            meetpuntType: _BAAS_SHARED_CONSTANTS.DEFAULT_MEETPUNT_TYPE
        };
    }
    async #launchAuth(preInitSkipped) {
        // authenticate ! when successfull also initialize
        try {
            const authSuccess = await this.auth.authenticate();
            if (authSuccess === true) {
                await this.#launchInit(preInitSkipped);
            }
            else if (authSuccess === false) {
                window.location.href = this.#authFailedUrl;
                this.#handleAuthError();
                this.#outcome = BaasBootstrapOutcome.AuthFailed;
            }
            else if (authSuccess === undefined || authSuccess === null) {
                this.#outcome = BaasBootstrapOutcome.Authenticating;
            }
            // NOTE authSuccess null or undefined means a redirect has been done, no error should be thrown
        }
        catch (error) {
            console.error(error);
            window.location.href = this.#authFailedUrl;
            this.#handleAuthError(error);
        }
    }
    async #launchPreInit() {
        // try get config from session storage first to avoid having to retrieve it (can become larger and larger)
        const loadConfig = this.#tryLoadConfigFromStorage();
        const initRequest = this.#getInitRequest(loadConfig);
        try {
            // get the config from web api without authentication
            const data = await this.commonApiClient.preInit(initRequest);
            this.#handleConfig(data.config, true);
            this.#handleToepassing(data.toepassing);
            // load config from BaaS Static at an early stage
            await this.#defineBaasStaticConfiguration();
            // in case xdc is enabled we do a sync to see if we are already authenticated on the remote domain
            if (this.xdc.enabled)
                await this.xdc.sync({ caller: 'Startup', step: 'Auth' });
            // after the sync we check again if we are not authenticated
            if (this.#mustAuthenticate && !this.auth.authenticated) {
                //when session is filled during (offline modus), IDP call (launchauth) is bypassed!
                await this.#launchAuth(false);
            }
            else {
                // if we succeeded to authenticate we perform the init straight away
                await this.#launchInit(false);
            }
        }
        catch (error) {
            this.#handleStartupError(error);
        }
    }
    #tryLoadConfigFromStorage() {
        let notLoaded = true;
        const configStr = window.sessionStorage.getItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + 'config');
        if (configStr != null && configStr.length > 0) {
            notLoaded = false;
            const config = JSON.parse(configStr);
            this.#handleConfig(config, false);
        }
        return notLoaded;
    }
    // handles the configuration object
    #handleConfig(config, putInStorage) {
        if (this.#configHandled)
            return;
        // configuration data
        const configKey = 'config';
        // trick to have assignment to readonly prop
        this.state[configKey] = config;
        if (putInStorage) {
            // store config in session storage to avoid retrieving it when switching application
            window.sessionStorage.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + 'config', JSON.stringify(config));
        }
        const processIdpRootUrl = (origin) => {
            let idpRootUrl = origin;
            if (!config.idpRootUrl.startsWith('/')) {
                idpRootUrl += '/';
            }
            idpRootUrl += config.idpRootUrl;
            config.idpRootUrl = idpRootUrl;
            config.idpRootUrl = this.rapUrlHelper.parseUrl(config.idpRootUrl);
        };
        if (this.options.localhost) {
            // NOTE overschrijven van urls voor de lokale BAR omgeving (deze solution): localhost heeft geen profielbeheer notie
            if (this.options._solution && !this.options.auth.forceRemote) {
                // indien localhost client EN bundle build voor local : via lokale authprovider/IDP
                config.auth.url = 'https://localhost:44307/auth?noredirect=1';
                config.auth.failedUrl = 'https://localhost:44307/authenticate-failed';
                config.logoutUrl = 'https://localhost:44307/auth/logout';
                config.errorUrl = 'https://alv-dotnet4-d/startscherm/domain.startscherm.Web/probleemrapport/';
                config.idpRootUrl = 'https://alv-dotnet4-d/a/idp/wui';
            }
            else {
                // this will be used in XDC modus
                // this.state.location.release.origin;
                processIdpRootUrl(this.state.location.release.origin);
            }
        }
        else {
            processIdpRootUrl(window.location.origin);
        }
        // pass the idp root url to the idp client if used - never clear it once set
        if (config.idpRootUrl) {
            this.idpClient.options.idpUrl = config.idpRootUrl;
        }
        this.#configHandled = true;
    }
    #defineLogging() {
        const log = ensureGlobal('lvflanders.bar.shared.log');
        log.enabled = {};
        Object.defineProperty(log.enabled, 'cacheStorage', {
            get() {
                const enabledStr = window.localStorage.getItem('lvflanders.bar.shared.log.enabled.cacheStorage');
                return enabledStr != null ? JSON.parse(enabledStr) : false;
            },
            set(newValue) {
                window.localStorage.setItem('lvflanders.bar.shared.log.enabled.cacheStorage', JSON.stringify(newValue));
            },
            configurable: true
        });
        Object.defineProperty(log, 'format', {
            get() {
                const enabledStr = window.localStorage.getItem('lvflanders.bar.shared.log.format');
                return enabledStr || 'object';
            },
            set(newValue) {
                if (newValue)
                    newValue = newValue.toLowerCase();
                if (!newValue || newValue === 'json' || newValue === 'object')
                    window.localStorage.setItem('lvflanders.bar.shared.log.format', newValue);
                else
                    console.warn("can only set value to 'json', 'object' or nothing.");
            },
            configurable: true
        });
    }
    /** The host url of the current application. This is for example used for defining api url's for the current application */
    #defineAppRootUrl() {
        let url = null;
        // if the application url is defined use this
        if (this.options.appRootUrl)
            url = this.options.appRootUrl;
        else if (this.options.appUrl)
            url = this.options.appUrl;
        else {
            // by default use the global base href tag
            const baseTag = document.getElementsByTagName('base');
            // no angular base href defined, use the html base href (set through angular.json baseHref or directly in index.html)
            if (baseTag.length && baseTag[0].href)
                url = baseTag[0].href;
            // if no base href just take the domain
            else
                url = location.origin;
        }
        if (!checkUrlAbsolute(url)) {
            if (!url.startsWith('/'))
                url = '/' + url;
            // we make the url absolute
            url = location.origin + url;
        }
        // make it end with a slash
        if (!url.endsWith('/'))
            url += '/';
        return url.toLowerCase();
    }
    #defineApiRootUrl() {
        if (this.options.apiRootUrl)
            return this.options.apiRootUrl;
        return this.#defineAppRootUrl();
    }
    #defineAppUrl() {
        if (this.options.appUrl) {
            if (checkUrlAbsolute(this.options.appUrl))
                return this.options.appUrl;
            const appUrl = this.options.appUrl.startsWith('/') ? this.options.appUrl : `/${this.options.appUrl}`;
            return `${this.state.location.appRoot.origin}${appUrl}`;
        }
        return this.state.location.appRoot.url;
    }
    // handles startup errors specially because there is no user interface yet
    #handleStartupError(err) {
        console.error(err);
        if (this.oopsDomService.checkOopsVisible())
            return;
        this.oopsDomService.showOops({ type: _OopsType.Technical });
    }
    #handleAuthError(error = undefined) {
        if (error) {
            console.error(error);
        }
        throw new Error(this.i18n.get('DOMAIN_BAR_SHARED.AUTH_ERROR'));
    }
    // following method is invoked after authenticating
    async #defineBrowserSession() {
        // get session id from cookie
        let cookieSessionId = this.appInfo.sessionId;
        if (!cookieSessionId) {
            // no session cookie, create session cookie and use new uuid
            cookieSessionId = generateUuid();
            this.appInfo.sessionId = cookieSessionId;
        }
        // get actual session id being used
        let sessionId = await this.globalStore.getItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.SESSION_ID);
        // this routing is called at the end
        const proceed = async (clearSession) => {
            // use session id in the temp storage generated after successfull authentication
            const sessionIdKey = 'sessionId';
            // save it in state
            this.state[sessionIdKey] = sessionId;
            if (!this.options.disableCacheInvalidation) {
                // invalidate the cache (if not disabled) and pass flag determining whether we must clear out all session cache entries
                await this.cacheManager.invalidate(clearSession);
            }
            if (clearSession)
                this.#clearTempState();
        };
        // if no session id yet or if there is a session but the app info cookie isn't equal we start a new browser session
        if (sessionId !== cookieSessionId) {
            sessionId = cookieSessionId;
            // put the new session id in the global cache store (and keep it there)
            await this.globalStore.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.SESSION_ID, sessionId, { session: false });
            await proceed(true);
        }
        // re-use session
        else {
            await proceed(false);
        }
    }
    #tryRunIdpValidation() {
        if (this.#mustAuthenticate && this.options.auth.validation !== false && this.options.auth.contextTransportModus === BaasContextTransportModus.Cookie) {
            // here we launch a web worker asynchronously bound to the idp client
            this.idpClient.validate();
        }
    }
    // following method is always invoked (after authenticating or if already authenticated)
    async #launchInit(preInitSkipped) {
        if (preInitSkipped && this.xdc.enabled) {
            await this.xdc.sync({ caller: 'Startup', step: 'Before Init' });
        }
        // check if we need to bypass the init in case not authenticated and the url being invoked is a no init path
        if (!this.auth.authenticated &&
            this.options._noInitPaths?.some((noInitPath) => {
                const fullPath = concatUrl(this.state.location.app.pathname, noInitPath).toLowerCase();
                return window.location.pathname.toLowerCase() === fullPath;
            })) {
            await this.#initializeFinal(undefined, preInitSkipped);
            return;
        }
        // try get config from session storage first to avoid having to retrieve it (can become larger and larger)
        const loadConfig = !this.#configHandled && this.#tryLoadConfigFromStorage();
        const initRequest = this.#getInitRequest(loadConfig);
        initRequest.loadToepassing = this.state.toepassing == null;
        try {
            const data = await this.commonApiClient.init(initRequest);
            if (data.config != null) {
                this.#handleConfig(data.config, true);
            }
            const baasVersionKey = 'baasVersion';
            this.state[baasVersionKey] = data.baasVersion;
            const requestTokenKey = 'requestToken';
            this.state[requestTokenKey] = data.requestToken;
            if (data.toepassing != null) {
                this.#handleToepassing(data.toepassing);
            }
            if (this.options.auth.contextTransportModus === BaasContextTransportModus.Header && data?.state !== null && typeof data.state !== 'undefined') {
                const currentContext = this.baasContextService.getBaasContextFromStorage();
                const updatedContext = { ...data.state, userTicketGuid: currentContext.userTicketGuid };
                //will save context of authenticated user in session storage
                this.baasContextService.saveBaasContextToStorage(updatedContext);
            }
            const meetpuntenAutomatischAanmakenKey = 'meetpuntenAutomatischAanmaken';
            this.state[meetpuntenAutomatischAanmakenKey] = data.meetpuntenAutomatischAanmaken == null ? false : data.meetpuntenAutomatischAanmaken;
            if (this.options.localhost) {
                // in case xdc is enabled we do an extra init on the localhost api
                if (this.xdc.enabled)
                    await this.xdc.sync({ caller: 'Startup', step: 'After Init' });
                try {
                    // extra localhost init for antiforgery
                    const localData = await this.commonApiClient.initLocal();
                    const localRequestTokenKey = 'localRequestToken';
                    this.state[localRequestTokenKey] = localData.requestToken;
                }
                catch (error) {
                    const msg = this.i18n.get('DOMAIN_BAR_SHARED.INIT_LOCAL_FAILED');
                    console.error(msg);
                    // NOTE this will throw an error to abort further processing
                    this.#handleStartupError(error);
                }
            }
            // end the initialization
            await this.#initializeFinal(data, preInitSkipped);
        }
        catch (error) {
            this.#handleStartupError(error);
        }
    }
    #handleToepassing(toepassing) {
        if (this.options.appVersion) {
            // NOTE use the version from the config as the toepassing versie
            // ATM (17-01-2024) there is a bug where BaaS.NET gives a wrong version through the init
            const versieKey = 'versie';
            toepassing[versieKey] = this.options.appVersion;
        }
        const toepassingKey = 'toepassing';
        this.state[toepassingKey] = toepassing;
        // set zone for IDP based on state
        this.idpClient.options.zone = this.state.toepassing.zone;
        //NOTE activatie voorwie hoedanigheid headers in dev 26-05-2025, 2 weken hierna: baas-auth-options.ts default values aanpassen en deze code weghalen
        const defaultActingScopeOptions = !this.options.auth.withActingScopeHeaders &&
            (this.options.auth.syncBasiscontextAfterRequestCompletion == null || this.options.auth.syncBasiscontextAfterRequestCompletion === true);
        if (defaultActingScopeOptions && this.state.toepassing.securityLevel !== BaasSecurityLevel.Production) {
            this.options.auth.syncBasiscontextAfterRequestCompletion = false;
            this.options.auth.withActingScopeHeaders = true;
        }
    }
    async #initializeFinal(data, preInitSkipped) {
        await this.#defineBrowserSession();
        // audit page (make sure it is after session audit)
        const audit = {
            auditId: this.state.pageId,
            parentAuditId: this.state.sessionId,
            auditType: BaasAuditType.Page,
            appCode: this.options.appCode,
            details: {
                URL: document.location.href,
                'APP-VERSION': this.options.appVersion
            }
        };
        this.audit.log(audit);
        _addAuditInfoOnDocument(audit);
        this.appInfo.init(data);
        // get basiscontext from cookie (cookie is the master)
        const basiscontext = this.appInfo.getBasiscontext();
        // also set the basiscontext regular variable (non observable)
        // trick to fill in readonly prop (will give compilation error if property name changes)
        // https://stackoverflow.com/questions/48992522/get-name-of-property-or-field-in-object-typed/48992694?noredirect=1#comment84991472_48992694
        const basiscontextKey = 'basiscontext';
        this.state[basiscontextKey] = basiscontext;
        if (this.auth.postProcessing)
            await this.auth.postProcessing();
        if (preInitSkipped)
            await this.#defineBaasStaticConfiguration();
        // apm needs BaaS static configuration so it has been moved
        this.apm.init();
        await this.#initLvOidc();
        if (await this.#applyPreRoutingInterceptions()) {
            this.logOff.startListening();
            this.#tryRunIdpValidation();
            await this.#defineAuditMeetpunten();
            this.#auditDependencies();
            this.#defineHubClient();
            await this.#defineUserInfo();
            await this.#defineNotifications();
            this.#outcome = BaasBootstrapOutcome.Initialized;
        }
        // don't allow further processing in case already intercepted
        else
            this.#outcome = BaasBootstrapOutcome.Halted;
        if (this.telemetry.currentTransaction) {
            // log entire bootstrapping on parent transaction
            const span = this.telemetry.currentTransaction.startSpan('Bootstrap BaaS App', 'bootstrap-baas-app', { startTime: this.#startTime });
            span.end();
        }
    }
    // this is the domain where the @lvflanders/bar-ng script is released and from which it is referenced within the application
    // we guess this domain by checking the script tags (the localhost application has an absolute reference to the script file (cdn)
    // in case a localhost consumer outside BAR is using a script we use the domain of that script to determine where to do api calls or other stuff
    /** The domain where @lvflanders BAR is running */
    #defineReleaseOrigin() {
        let result = null;
        if (this.#targetOrigin != null) {
            // here we are in xdc modus and are using a target origin to define to which domain to connect to
            // for BAR & basiscomponenten and & XDC iframe
            result = this.#targetOrigin;
        }
        else {
            let src = null;
            // note that scripts property is not a regular array
            for (let i = 0; i < document.scripts.length; i++) {
                const script = document.scripts[i];
                // find script tag with bar-ng
                if (script.src != null && script.src.indexOf('lvflanders-bar-ng') > -1) {
                    src = script.src;
                    break;
                }
            }
            if (src != null) {
                result = extractUrlOrigin(src);
            }
            else {
                // no bar-core bundle meaning everything is probably bundled inline -> just take current origin
                result = window.location.origin;
            }
        }
        return result;
    }
    /** Root url to BAR web (api) (this is the domain + the bar web segment) */
    #defineBarWebRootUrl() {
        let url = null;
        if (this.options.connectivity === BaasConnectivity.Offline) {
            // local means BAR web is in process -> we need to point to url of current application
            url = this.state.location.appRoot.url + 'domain.basisarchitectuur.web/';
        }
        // remote modus (default)
        else if (this.options.localhost) {
            // check local: this means we are in the BAR solution: just use the URL of BAR web localhost
            url = this.state.location.release.origin + _BAAS_SHARED_CONSTANTS.URL_SEGMENT.BAR_WEB;
        }
        // we are on the deployed environment: just use current host name
        else {
            url = location.origin + _BAAS_SHARED_CONSTANTS.URL_SEGMENT.BAR_WEB;
        }
        return url;
    }
    #defineBaasWebRootUrl() {
        const origin = this.options.localhost ? this.state.location.release.origin : location.origin;
        return concatUrl(origin, this.rapUrlHelper.baasWebSegment);
    }
    /**
     * Sets an early default idpUrl on the idpClient based on the resolved release origin.
     * This runs before config is loaded. #handleConfig will later override it with the full processed idpRootUrl from config.
     * The origin priority is:
     *   1. state.location.release.origin (if already resolved)
     *   2. 'https://alv-dotnet4-d' (if running on localhost without a release origin)
     *   3. window.location.origin (any other deployed environment)
     */
    #defineDefaultIdpRootUrl() {
        // only set if not already explicitly provided via options
        if (this.idpClient.options.idpUrl)
            return;
        let origin;
        if (this.state.location?.release?.origin) {
            origin = this.state.location.release.origin;
        }
        else if (checkLocalhost()) {
            origin = 'https://alv-dotnet4-d';
        }
        else {
            origin = window.location.origin;
        }
        this.idpClient.options.idpUrl = concatUrl(origin, `/${this.rapUrlHelper.rapSegment}/idp/wui`);
    }
    #defineBaasStaticRootUrl() {
        let url = null;
        if (this.options.connectivity === BaasConnectivity.Offline) {
            // local means BaaS Static is in process -> we need to point to url of current application
            url = this.state.location.appRoot.url + 'domain.baas-static.web/';
        }
        // remote modus (default)
        else if (this.options.localhost) {
            // check local: this means we are in the BAR solution: just use the URL of BAR web localhost
            if (this.options._solution && this.options.staticRootUrl) {
                // NOTE that the bar web process is hosted somewhere else (other localhost) and this is passed through the forRoot options
                url = this.options.staticRootUrl;
                if (!url.endsWith('/')) {
                    url += '/';
                }
            }
            // if localhost client and this bundle is build for DEV
            // this means we need to use the DEV url
            else {
                url = this.state.location.release.origin + this.rapUrlHelper.baasStaticSegment;
            }
        }
        // we are on the deployed environment: just use current host name
        else {
            url = location.origin + this.rapUrlHelper.baasStaticSegment;
        }
        return url;
    }
    async #defineBaasStaticConfiguration() {
        if (this.state.connectivity === BaasConnectivity.Online) {
            const config = await this._BaasStaticConfigurationApiClient.getConfiguration();
            this.#handleBaasStaticConfiguration(config);
        }
    }
    #handleBaasStaticConfiguration(config) {
        config?.appMenuItems?.forEach((ami) => {
            ami.url = this.#processAppMenuUrl(ami.url);
            if (this.options.localhost) {
                ami.urlLocalhost = this.#processAppMenuUrl(ami.urlLocalhost);
            }
        });
        const configKey = 'config';
        // assign it to sub property
        this.state[configKey].baasStatic = config;
        // override config in case public
        if (this.options.public === true && config.public?.homeUrl) {
            this.state.config.homeUrl = config.public.homeUrl;
        }
    }
    #processAppMenuUrl(url) {
        if (!url || checkUrlAbsolute(url))
            return url;
        return `${this.state.location.release.origin}${url}`;
    }
    async #defineAuditMeetpunten() {
        const perflog = parseQueryStringValue('perflog');
        if (perflog != null) {
            const appinfo = this.appInfo.get();
            if (appinfo != null) {
                switch (perflog.toLowerCase()) {
                    case 'off':
                        appinfo.ForcePerformanceLog = false;
                        await this.appInfo.save(appinfo);
                        break;
                    case 'on':
                        appinfo.ForcePerformanceLog = true;
                        await this.appInfo.save(appinfo);
                        break;
                }
            }
        }
        if (this.meetpunt.loggingEnabled) {
            // here we measure the page load time (duration of loading resources and parsing it)
            // https://stackoverflow.com/a/14878493/2174621
            const pageLoadTime = window.performance.timing.domContentLoadedEventEnd - window.performance.timing.navigationStart;
            this.meetpunt.log({
                meetpunt: 'DOM_CONTENT_LOADED_END',
                meetpuntDetail: document.location.pathname,
                waarde: pageLoadTime,
                meetpuntInfo: document.location.href,
                auditId: this.state.pageId
            });
            this.#stopwatch = new BarStopwatch();
            // navigation start event does not occur the first time
            // this means we need to start the stopwatch the frist time
            this.#stopwatch.start();
        }
        if (this.state.config?.audit?.enabled == true) {
            // start listening to the audit queue
            this.audit.start();
        }
        if (this.meetpunt.loggingEnabled) {
            // start listening to the meetpunt queue
            await this.meetpunt.start();
        }
        // intialize logging (global vars...)
        this.logging.init();
        if (this.state.config?.log?.enabled == true) {
            // start listening to the log queue
            await this.logging.start();
        }
    }
    #auditDependencies() {
        const scripts = [];
        const scriptTags = document.getElementsByTagName('script');
        for (let i = 0; i < scriptTags.length; i++) {
            const scriptTag = scriptTags[i];
            if (scriptTag.src != null && scriptTag.src.length > 0) {
                const script = {
                    src: scriptTag.src,
                    type: scriptTag.type
                };
                scripts.push(script);
            }
        }
        const links = [];
        const linkTags = document.getElementsByTagName('link');
        for (let i = 0; i < linkTags.length; i++) {
            const linkTag = linkTags[i];
            if (linkTag.href != null && linkTag.href.length > 0) {
                const link = {
                    href: linkTag.href,
                    rel: linkTag.rel,
                    type: linkTag.type
                };
                links.push(link);
            }
        }
        this.audit.log({
            parentAuditId: this.state.pageId,
            auditType: BaasAuditType.Dependencies,
            appCode: this.options.appCode,
            details: {
                SCRIPTS: JSON.stringify(scripts),
                LINKS: JSON.stringify(links)
            }
        });
    }
    #defineHubClient() {
        if (this.options.hub?.enabled) {
            const hubUrl = concatUrl(this.state.location.baasWeb.url, '/baas/hubs/bui');
            this.hubClient.init({
                hubUrl,
                userId: this.state.basiscontext?.userId,
                appCode: this.state.toepassing.code,
                securityTicketId: this.state.basiscontext?.securityTicketId
            });
        }
    }
    async #defineUserInfo() {
        let userData;
        // don't do anything if not authenticated
        if (!this.auth.authenticated) {
            userData = {
                hoedanigheidInfoSet: []
            };
        }
        else {
            const cacheKey = STORAGE_KEY_USER_INFO_PREFIX + (this.state.basiscontext?.userId || 'UNDEFINED');
            let cachedItem = window.sessionStorage.getItem(cacheKey);
            if (cachedItem) {
                userData = JSON.parse(cachedItem);
            }
            else {
                userData = await this.securityApiClient.getUserInfo();
                cachedItem = JSON.stringify(userData);
                window.sessionStorage.setItem(cacheKey, cachedItem);
            }
        }
        const userInfoKey = 'userInfo';
        this.state[userInfoKey] = userData;
    }
    async #defineNotifications() {
        if (this.options.notifications === true)
            await this.notificationManager.init();
    }
    /** Checks if we need to intercept the current route before the routing.
     * Returns a flag defining whether the bootstrapping can proceed */
    async #applyPreRoutingInterceptions() {
        if (this.#checkRouteMatch(_BAAS_SHARED_CONSTANTS.AUTH.OIDC.CALLBACK.SPOTFIRE)) {
            // clear out page (used inside an spotfire web player which only cares for query string parameters)
            while (document.body.firstChild) {
                document.body.removeChild(document.body.firstChild);
            }
            if (typeof spotfire !== 'undefined')
                spotfire.webPlayer.oauthEnd();
            else
                console.error('Spotfire CDN JS api not loaded.');
            this.#clearDocument('Spotfire callback');
            return false;
        }
        if (this.options.auth?._experimentalOidcEnabled) {
            if (!this.#checkRouteMatch(_BAAS_SHARED_CONSTANTS.AUTH.OIDC.CALLBACK.LV)) {
                const user = await this.oidcAuthService.getUser();
                if (user)
                    await this.oidcAuthService.renewToken();
            }
            else {
                // we are in the callback
                await this.oidcAuthService.callback();
                return false;
            }
        }
        return true;
    }
    /** @experimental Initializes the LV OICD service and hooks up the callback. */
    async #initLvOidc() {
        if (!this.options.auth?._experimentalOidcEnabled)
            return;
        const authority = concatUrl(this.state.location.release.url, this.rapUrlHelper.idpOidcWebSegment);
        const callbackRootUri = checkLocalhost() ? this.state.location.app.url : concatUrl(this.state.location.baasStatic.url, '/bui');
        const callbackUri = concatUrl(callbackRootUri, _BAAS_SHARED_CONSTANTS.AUTH.OIDC.CALLBACK.LV);
        const options = {
            authority,
            clientId: 'oidc_lv_public_test1',
            callbackUri,
            clientScope: 'openid offline_access',
            userAddedCallback: (user) => {
                // NOTE use this to register tokens in state
            }
        };
        this.oidcAuthService.init(options);
    }
    #checkRouteMatch(path) {
        const url = new URL(window.location.href);
        const pathname = url.pathname.split('?')[0]; // Exclude query string
        return pathname.endsWith(path);
    }
    #clearDocument(comment) {
        // Get the <html> element
        const htmlElement = document.documentElement;
        // Remove all attributes
        while (htmlElement.attributes.length > 0) {
            htmlElement.removeAttribute(htmlElement.attributes[0].name);
        }
        const commentElement = document.createComment(comment);
        document.body.appendChild(commentElement);
    }
    #clearTempState() {
        clearFromStorage(localStorage, (key) => key.startsWith(`${_BAAS_SHARED_CONSTANTS.TEMP_STORAGE_STATE_KEY}.`));
    }
};
BaasBootstrap = __decorate([
    injectable(),
    __param(8, inject(BarCacheStore)),
    __param(8, tagged(BAAS_METADATA_KEY.CACHE_STORE_TYPE_TAG, BarCacheStoreType.Global)),
    __param(13, inject(BAAS_TYPES.BaasCommonApiClient)),
    __param(14, inject(BAAS_TYPES.BaasSecurityApiClient)),
    __param(22, inject(BAAS_TYPES.BaasTelemetry)),
    __param(23, inject(BAAS_TYPES._BaasLvOicdAuthService)),
    __metadata("design:paramtypes", [BaasAppState,
        BaasBootstrapOptions,
        BarIdpClient,
        _BaasLogOffUtility,
        BaasI18n,
        _BarXdc,
        BarCacheManager,
        _BaasContextManager,
        BarCacheStore,
        BarRapUrlHelper,
        BaasAuthService,
        _BarAppInfoManager,
        BaasAuditService, Object, Object, _BaasStaticConfigurationApiClient,
        BaasMeetpuntService,
        BaasLoggingService,
        BarHubClient,
        BarNotificationManager,
        _OopsDomService,
        _BaasApm, Object, BaasOidcAuthService])
], BaasBootstrap);

/** Global variable to access BaaS context. */
const baasContext = {
    container: undefined
};

let BaasAuditDefaultApiClient = class BaasAuditDefaultApiClient {
    constructor(axios, state) {
        this.axios = axios;
        this.state = state;
    }
    async save(auditItems) {
        await this.axios.post(this.state.location.apiRoot.url + 'baas/audit/bui/messages', auditItems);
    }
};
BaasAuditDefaultApiClient = __decorate([
    injectable(),
    __param(0, inject(BAAS_TYPES.AxiosInstance)),
    __param(0, named(BAAS_AXIOS_INSTANCE_TYPES.Baas)),
    __metadata("design:paramtypes", [Function, BaasAppState])
], BaasAuditDefaultApiClient);

let BaasCommonDefaultApiClient = class BaasCommonDefaultApiClient {
    constructor(state, axiosBaas, axiosPlain) {
        this.state = state;
        this.axiosBaas = axiosBaas;
        this.axiosPlain = axiosPlain;
    }
    async preInitLocal() {
        const response = await this.axiosPlain.post(this.state.location.apiRoot.url + 'baas/bootloader/pre-init-local', null, {
            // ommit interceptor for performing the pre-init-local
            // this is invoked in the very beginning before pre-init
            withCredentials: true
        });
        return response.data;
    }
    async preInit(request) {
        // this is invoked before authentication
        const response = await this.axiosPlain.post(this.state.location.apiRoot.url + 'baas/bootloader/pre-init', request, {
            withCredentials: true
        });
        if (response.data?.toepassing)
            this.parseToepassing(response.data.toepassing);
        return response.data;
    }
    async init(request) {
        // this is running after authentication
        const response = await this.axiosBaas.post(this.state.location.apiRoot.url + 'baas/bootloader/init', request);
        if (response.data?.toepassing)
            this.parseToepassing(response.data.toepassing);
        return response.data;
    }
    async initLocal() {
        const response = await this.axiosBaas.post(this.state.location.apiRoot.url + 'baas/bootloader/init-local', {});
        return response.data;
    }
    async saveEndpoints(appCode, appVersion, endpoints) {
        const url = `${this.state.location.apiRoot.url}baas/bootloader/endpoints/appcode/${appCode}/version/${appVersion}`;
        try {
            const response = await this.axiosBaas.post(url, endpoints);
            if (!response.data?.returnVal)
                console.error('An error occurred while saving the endpoints', response.data);
        }
        catch (err) {
            // application should not crash because of this
            console.error('An error occurred while saving the endpoints', err);
        }
    }
    parseToepassing(toepassing) {
        if (typeof toepassing.securityLevel === 'string') {
            const securityLevelKey = 'securityLevel';
            toepassing[securityLevelKey] = this.parseSecLevelFromString(toepassing.securityLevel);
        }
    }
    parseSecLevelFromString(securityLevel) {
        if (securityLevel == null)
            return null;
        switch (securityLevel.toUpperCase()) {
            case 'DEV':
                return BaasSecurityLevel.Development;
            case 'TST':
                return BaasSecurityLevel.Test;
            case 'PRD':
                return BaasSecurityLevel.Production;
            default:
                return BaasSecurityLevel.Unknown;
        }
    }
};
BaasCommonDefaultApiClient = __decorate([
    injectable(),
    __param(1, inject(BAAS_TYPES.AxiosInstance)),
    __param(1, named(BAAS_AXIOS_INSTANCE_TYPES.Baas)),
    __param(2, inject(BAAS_TYPES.AxiosInstance)),
    __param(2, named(BAAS_AXIOS_INSTANCE_TYPES.Plain)),
    __metadata("design:paramtypes", [BaasAppState, Function, Function])
], BaasCommonDefaultApiClient);

let BaasConfigurationDefaultApiClient = class BaasConfigurationDefaultApiClient {
    constructor(axios, state) {
        this.axios = axios;
        this.state = state;
    }
    // de BaaS api werkt met GET voor ophalen van values
    async read(model) {
        const url = `${this.state.location.apiRoot.url}baas/configuration/value/appCode/${model.appCode}/path/${encodeURI(model.key)}`;
        const response = await this.axios.get(url);
        return response.data;
    }
};
BaasConfigurationDefaultApiClient = __decorate([
    injectable(),
    __param(0, inject(BAAS_TYPES.AxiosInstance)),
    __param(0, named(BAAS_AXIOS_INSTANCE_TYPES.Baas)),
    __metadata("design:paramtypes", [Function, BaasAppState])
], BaasConfigurationDefaultApiClient);

let BaasLoggingDefaultApiClient = class BaasLoggingDefaultApiClient {
    constructor(axios, state) {
        this.axios = axios;
        this.state = state;
    }
    save(logs) {
        return this.axios.post(this.state.location.apiRoot.url + 'baas/logging/bui/messages', logs);
    }
};
BaasLoggingDefaultApiClient = __decorate([
    injectable(),
    __param(0, inject(BAAS_TYPES.AxiosInstance)),
    __param(0, named(BAAS_AXIOS_INSTANCE_TYPES.Baas)),
    __metadata("design:paramtypes", [Function, BaasAppState])
], BaasLoggingDefaultApiClient);

let BaasMeetpuntDefaultApiClient = class BaasMeetpuntDefaultApiClient {
    constructor() {
        this.warnedNotAvailable = false;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    save(meetpunten) {
        this.warnNotAvailable();
        return Promise.resolve();
    }
    warnNotAvailable() {
        if (!this.warnedNotAvailable) {
            this.warnedNotAvailable = true;
            console.warn('Meetpunten zijn (nog) niet beschikbaar binnen BaaS.Net...');
        }
    }
};
BaasMeetpuntDefaultApiClient = __decorate([
    injectable()
], BaasMeetpuntDefaultApiClient);

let BaasMeldingDefaultApiClient = class BaasMeldingDefaultApiClient {
    constructor(axios, state) {
        this.axios = axios;
        this.state = state;
    }
    /**
     * Get meldingen for requested url
     */
    async getMeldingen(urlPart) {
        const apiVersion = this.state.config?.baasStatic?.meldingen?.apiVersion;
        if (apiVersion === '2')
            return this.#getMeldingenV2(urlPart);
        return this.#getMeldingenV1(urlPart);
    }
    /**
     * Used by Melding Guard to check if a route has a blocking notification, if so it has to redirect to a specific page
     */
    async hasBlockingMelding(urlPart) {
        const url = this.state.location.apiRoot.url + `baas/melding/important-only/for-url?urlPart=${encodeURI(urlPart)}`;
        const response = await this.axios.get(url);
        return response.data;
    }
    async #getMeldingenV2(urlPart) {
        const url = concatUrl(this.state.location.apiRoot.url, `/baas/meldingv2/for-url?pattern=${encodeURI(urlPart)}`);
        const response = await this.axios.get(url);
        return response.data.map((melding) => {
            return {
                ...melding,
                baas: true,
                version: '2'
            };
        });
    }
    async #getMeldingenV1(urlPart) {
        const url = this.state.location.apiRoot.url + `baas/melding/all-types/for-url?urlPart=${encodeURI(urlPart)}`;
        const response = await this.axios.get(url);
        return response.data.map((melding) => {
            return {
                ...melding,
                baas: true,
                version: '1'
            };
        });
    }
};
BaasMeldingDefaultApiClient = __decorate([
    injectable(),
    __param(0, inject(BAAS_TYPES.AxiosInstance)),
    __param(0, named(BAAS_AXIOS_INSTANCE_TYPES.Baas)),
    __metadata("design:paramtypes", [Function, BaasAppState])
], BaasMeldingDefaultApiClient);

let BaasSecurityDefaultApiClient = class BaasSecurityDefaultApiClient {
    constructor(axios, state) {
        this.axios = axios;
        this.state = state;
    }
    async checkActieAllowed(actie) {
        // indien checkActieAllowed vanuit checkActieAllowedFromContext wordt aangeroepen
        // zal voorwie / voorwierolcode en hoedanigheid / hoedanigheidrolcode ingevuld zijn
        // /baas/security/isactieallowed/actie/{actie}
        // /baas/security/isactieallowed/actie/{actie}/voorwie/{voorwie}/rolcode/{rolcode}
        // /baas/security/isactieallowed/actie/{actie}/hoedanigheid/{hoedanigheid}/rolcode/{rolcode}
        // /baas/security/isactieallowed/actie/{actie}/voorwie/{vw}/rolcode/{vwrc}/hoedanigheid/{hoed}/rolcode/{hdrc}
        let voorwieSegment = '';
        if (actie.voorwie && actie.voorwieRolcode)
            voorwieSegment = '/voorwie/' + actie.voorwie + '/rolcode/' + actie.voorwieRolcode;
        let hoedanigheidSegment = '';
        if (actie.hoedanigheid && actie.hoedanigheidRolcode)
            hoedanigheidSegment = '/hoedanigheid/' + actie.hoedanigheid + '/rolcode/' + actie.hoedanigheidRolcode;
        const url = this.state.location.apiRoot.url + 'baas/security/isactieallowed/actie/' + encodeURI(actie.actie) + voorwieSegment + hoedanigheidSegment;
        const response = await this.axios.get(url);
        return response.data;
    }
    async getUserInfo() {
        const rootUrl = this.state.location.apiRoot.url;
        try {
            const response = await this.axios.get(rootUrl + 'baas/security/user-info');
            const data = response.data;
            return {
                cbiUserId: data.cbiUserId,
                hoedanigheidInfoSet: data.hoedList,
                contactInfo: data.contactInfo != null
                    ? {
                        klantnummer: data.contactInfo.alvNr,
                        firstname: data.contactInfo.firstName,
                        lastname: data.contactInfo.lastname,
                        fullName: data.contactInfo.fullname,
                        phone: data.contactInfo.phoneNumber,
                        mobile: data.contactInfo.mobileNumber,
                        email: data.contactInfo.mailAddress
                    }
                    : undefined
            };
        }
        catch (error) {
            console.error(error);
            return {};
        }
    }
};
BaasSecurityDefaultApiClient = __decorate([
    injectable(),
    __param(0, inject(BAAS_TYPES.AxiosInstance)),
    __param(0, named(BAAS_AXIOS_INSTANCE_TYPES.Baas)),
    __metadata("design:paramtypes", [Function, BaasAppState])
], BaasSecurityDefaultApiClient);

let BarAuditApiClient = class BarAuditApiClient {
    constructor(axios, state) {
        this.axios = axios;
        this.state = state;
    }
    async save(auditItems) {
        await this.axios.post(this.state.location.barWeb.url + 'api/audit', auditItems);
    }
};
BarAuditApiClient = __decorate([
    injectable(),
    __param(0, inject(BAAS_TYPES.AxiosInstance)),
    __param(0, named(BAAS_AXIOS_INSTANCE_TYPES.Baas)),
    __metadata("design:paramtypes", [Function, BaasAppState])
], BarAuditApiClient);

let BarCommonApiClient = class BarCommonApiClient {
    constructor(state, axiosBaas, axiosPlain) {
        this.state = state;
        this.axiosBaas = axiosBaas;
        this.axiosPlain = axiosPlain;
    }
    async preInitLocal() {
        const response = await this.axiosPlain.post(this.state.location.apiRoot.url + 'api/pre-init-local', null, {
            // ommit interceptor for performing the pre-init-local
            // this is invoked in the very beginning before pre-init
            withCredentials: true
        });
        return response.data;
    }
    async preInit(request) {
        // this is invoked before authentication
        const response = await this.axiosPlain.post(this.state.location.baasStatic.url + 'baas/bootloader/pre-init', request, {
            withCredentials: true
        });
        if (response.data?.toepassing)
            this.parseToepassing(response.data.toepassing);
        return response.data;
    }
    async init(request) {
        // this is running after authentication
        const response = await this.axiosBaas.post(this.state.location.baasStatic.url + 'baas/bootloader/init', request);
        if (response.data?.toepassing)
            this.parseToepassing(response.data.toepassing);
        return response.data;
    }
    async initLocal() {
        const response = await axios.post(this.state.location.apiRoot.url + 'api/init-local', {}, {
            withCredentials: true,
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        return response.data;
    }
    async saveEndpoints(appCode, appVersion, endpoints) {
        // NOTE just invoke baas static instead of BAR web
        const url = `${this.state.location.baasStatic.url}baas/bootloader/endpoints/appcode/${appCode}/version/${appVersion}`;
        try {
            const response = await this.axiosBaas.post(url, endpoints);
            if (!response.data?.returnVal)
                console.error('An error occurred while saving the endpoints', response.data);
        }
        catch (err) {
            // application should not crash because of this
            console.error('An error occurred while saving the endpoints', err);
        }
    }
    parseToepassing(toepassing) {
        if (typeof toepassing.securityLevel === 'string') {
            const securityLevelKey = 'securityLevel';
            toepassing[securityLevelKey] = this.parseSecLevelFromString(toepassing.securityLevel);
        }
    }
    parseSecLevelFromString(securityLevel) {
        if (securityLevel == null)
            return null;
        switch (securityLevel.toUpperCase()) {
            case 'DEV':
                return BaasSecurityLevel.Development;
            case 'TST':
                return BaasSecurityLevel.Test;
            case 'PRD':
                return BaasSecurityLevel.Production;
            default:
                return BaasSecurityLevel.Unknown;
        }
    }
};
BarCommonApiClient = __decorate([
    injectable(),
    __param(1, inject(BAAS_TYPES.AxiosInstance)),
    __param(2, inject(BAAS_TYPES.AxiosInstance)),
    __param(2, named(BAAS_AXIOS_INSTANCE_TYPES.Plain)),
    __metadata("design:paramtypes", [BaasAppState, Function, Function])
], BarCommonApiClient);

let BarLoggingApiClient = class BarLoggingApiClient {
    constructor(axios, state) {
        this.axios = axios;
        this.state = state;
    }
    async save(logs) {
        await this.axios.post(this.state.location.barWeb.url + 'api/logging', logs);
    }
};
BarLoggingApiClient = __decorate([
    injectable(),
    __param(0, inject(BAAS_TYPES.AxiosInstance)),
    __param(0, named(BAAS_AXIOS_INSTANCE_TYPES.Baas)),
    __metadata("design:paramtypes", [Function, BaasAppState])
], BarLoggingApiClient);

let BarMeetpuntApiClient = class BarMeetpuntApiClient {
    constructor(axios, state) {
        this.axios = axios;
        this.state = state;
    }
    async save(meetpunten) {
        await this.axios.post(this.state.location.barWeb.url + 'api/meetpunt', meetpunten);
    }
};
BarMeetpuntApiClient = __decorate([
    injectable(),
    __param(0, inject(BAAS_TYPES.AxiosInstance)),
    __metadata("design:paramtypes", [Function, BaasAppState])
], BarMeetpuntApiClient);

let BarMeldingApiClient = class BarMeldingApiClient {
    constructor(axios, state) {
        this.axios = axios;
        this.state = state;
    }
    /**
     * Get meldingen for requested url
     */
    async getMeldingen(url) {
        const response = await this.axios.post(this.state.location.barWeb.url + 'api/meldingen', { url });
        return response.data.map((melding) => {
            return {
                ...melding,
                baas: false,
                version: '0'
            };
        });
    }
    /**
     *Used by Melding Guard to check if a route has a blocking notification, if so it has to redirect to a specific page
     */
    async hasBlockingMelding(url) {
        const response = await this.axios.post(this.state.location.barWeb.url + 'api/meldingen/get', { url });
        return response.data;
    }
};
BarMeldingApiClient = __decorate([
    injectable(),
    __param(0, inject(BAAS_TYPES.AxiosInstance)),
    __param(0, named(BAAS_AXIOS_INSTANCE_TYPES.Baas)),
    __metadata("design:paramtypes", [Function, BaasAppState])
], BarMeldingApiClient);

let BarProfielApiClient = class BarProfielApiClient {
    constructor(axios, state) {
        this.axios = axios;
        this.state = state;
    }
    async read(model) {
        const response = await this.axios.post(this.state.location.barWeb.url + 'api/profiel/read', model);
        return response.data;
    }
};
BarProfielApiClient = __decorate([
    injectable(),
    __param(0, inject(BAAS_TYPES.AxiosInstance)),
    __metadata("design:paramtypes", [Function, BaasAppState])
], BarProfielApiClient);

let BarSecurityApiClient = class BarSecurityApiClient {
    constructor(axios, state) {
        this.axios = axios;
        this.state = state;
    }
    async checkActieAllowed(actie) {
        const url = this.state.location.barWeb.url + 'api/security/check-actie-allowed';
        const response = await this.axios.post(url, actie);
        return response.data;
    }
    async getUserInfo() {
        // ask baas static for the user data
        const url = this.state.location.baasStatic.url + 'baas/security/user-info';
        try {
            const response = await this.axios.get(url);
            const data = response.data;
            return {
                cbiUserId: data.cbiUserId,
                hoedanigheidInfoSet: data.hoedList,
                contactInfo: data.contactInfo != null
                    ? {
                        klantnummer: data.contactInfo.alvNr,
                        firstname: data.contactInfo.firstName,
                        lastname: data.contactInfo.lastname,
                        fullName: data.contactInfo.fullname,
                        phone: data.contactInfo.phoneNumber,
                        mobile: data.contactInfo.mobileNumber,
                        email: data.contactInfo.mailAddress
                    }
                    : undefined
            };
        }
        catch (error) {
            console.error(error);
            return {};
        }
    }
};
BarSecurityApiClient = __decorate([
    injectable(),
    __param(0, inject(BAAS_TYPES.AxiosInstance)),
    __metadata("design:paramtypes", [Function, BaasAppState])
], BarSecurityApiClient);

/** Allows for external authentication to set their prefered user & context without using IDP service */
let BaasExternalAuthStrategy = class BaasExternalAuthStrategy {
    get authenticated() {
        return this.contextManager.checkBaasContextInStorage();
    }
    constructor(contextManager, contextProvider) {
        this.contextManager = contextManager;
        this.contextProvider = contextProvider;
    }
    async authenticate() {
        if (!this.contextProvider)
            throw 'BaasExternalAuthStrategy was called but the injected BaasContextProvider has not been defined.';
        try {
            const context = await this.contextProvider.provide();
            if (context) {
                const userVoorwie = context.voorwie && context.voorwieRolcode ? `${context.voorwie};${context.voorwieRolcode.toUpperCase()}` : undefined;
                const userHoedanigheid = context.hoedanigheid && context.hoedanigheidRolcode ? `${context.hoedanigheid};${context.hoedanigheidRolcode.toUpperCase()}` : undefined;
                const state = { userTicketGuid: context.securityTicketId, userVoorwie, userHoedanigheid };
                this.contextManager.saveBaasContextToStorage(state);
                return true;
            }
            else
                return null;
        }
        catch (error) {
            console.error(error);
            return false;
        }
    }
    performLogOff() {
        this.contextManager.removeContextFromSession();
        return Promise.resolve();
    }
};
BaasExternalAuthStrategy = __decorate([
    injectable(),
    __param(1, inject(BAAS_TYPES.BaasContextProvider)),
    __metadata("design:paramtypes", [_BaasContextManager, Object])
], BaasExternalAuthStrategy);

/** Public BAR authentication service using IDP. */
let BaasIdpAuthStrategy = class BaasIdpAuthStrategy {
    get authenticated() {
        // being authenticated means having a bar-cookie (if we have the idp client we use this to check whether we are authenticated)
        return this.idpClient.authenticated;
    }
    constructor(appState, bootstrapOptions, idpClient, appInfo) {
        this.appState = appState;
        this.bootstrapOptions = bootstrapOptions;
        this.idpClient = idpClient;
        this.appInfo = appInfo;
    }
    async authenticate() {
        // make sure local storage value impersonate is removed before authentication
        window.localStorage.removeItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.IDP_IMPERSONATE);
        // NOTE we cannot use the route here because it is before routing takes place
        const idpStatus = parseQueryStringValue('idp-status');
        if (idpStatus === 'fail') {
            // idp returned a fail, just make sure we go to authenticate failed page (to avoid endless loop)
            return false;
        }
        else {
            // if we are using @lvflanders/bar we use the idp client
            await this.idpClient.authenticate(this.bootstrapOptions.auth.config);
            // make sure processing is stopped because we are doing a full page redirect
            return null;
        }
    }
    /** Processing that occurs after the authentication. Meaning we are authenticated. */
    async postProcessing() {
        if (!this.authenticated)
            return;
        const impersonateStrQuery = parseQueryStringValue('idp-impersonate');
        const impersonateStrStorage = window.localStorage.getItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.IDP_IMPERSONATE);
        let impersonate = false;
        if (impersonateStrQuery && (!impersonateStrStorage || impersonateStrStorage === false.toString())) {
            impersonate = impersonateStrQuery.toLowerCase() === true.toString();
            window.localStorage.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.IDP_IMPERSONATE, impersonate.toString());
        }
        else
            impersonate = impersonateStrStorage === true.toString();
        const impersonateKey = 'impersonate';
        this.appState.basiscontext[impersonateKey] = impersonate;
    }
    async performLogOff() {
        if (this.bootstrapOptions.localhost) {
            // NOTE app info localhost xdc stuff is not yet in @lvflanders/bar
            this.appInfo.delete();
        }
        await this.idpClient.logout();
    }
};
BaasIdpAuthStrategy = __decorate([
    injectable(),
    __metadata("design:paramtypes", [BaasAppState,
        BaasBootstrapOptions,
        BarIdpClient,
        _BarAppInfoManager])
], BaasIdpAuthStrategy);

let BaasIdpHeaderAuthStrategy = class BaasIdpHeaderAuthStrategy {
    constructor(idpClient, contextManager, bootstrapOptions, logOff) {
        this.idpClient = idpClient;
        this.contextManager = contextManager;
        this.bootstrapOptions = bootstrapOptions;
        this.logOff = logOff;
    }
    get authenticated() {
        return this.contextManager.checkBaasContextInStorage();
    }
    get authenticating() {
        // we are coming from IDP meaning we still need to authenticate
        return parseQueryStringValue('idp-status') != null;
    }
    async authenticate() {
        const idpStatus = parseQueryStringValue('idp-status');
        switch (idpStatus) {
            case 'success': {
                // authentication success
                const idpUsr = parseQueryStringValue('idp-usr');
                const voorwie = parseQueryStringValue('idp-vw');
                const voorwieRolcode = parseQueryStringValue('idp-vwrc');
                // NOTE voorwie rolcode is niet verplicht
                const userVoorwie = voorwie ? `${voorwie};${voorwieRolcode ? voorwieRolcode.toUpperCase() : 'NULL'}` : undefined;
                const hoedanigheid = parseQueryStringValue('idp-hd');
                const hoedanigheidRolcode = parseQueryStringValue('idp-hdrc');
                // NOTE hoedanigheid rolcode is niet verplicht
                const userHoedanigheid = hoedanigheid ? `${hoedanigheid};${hoedanigheidRolcode ? hoedanigheidRolcode.toUpperCase() : 'NULL'}` : undefined;
                const state = { userTicketGuid: idpUsr, userVoorwie, userHoedanigheid };
                // opkuisen query string
                const url = clearQueryStringParams(window.location.href, (key) => key.startsWith('idp-'));
                window.history.replaceState({}, document.title, url.toString());
                this.contextManager.saveBaasContextToStorage(state);
                return true;
            }
            case 'fail': {
                // authentication failed
                return false;
            }
            default: {
                // authenticating
                // go to idp and pass this url as the target
                await this.idpClient.authenticate(this.bootstrapOptions.auth.config);
                return null;
            }
        }
    }
    async performLogOff() {
        this.contextManager.removeContextFromSession();
        this.logOff.navigateLogOff();
    }
};
BaasIdpHeaderAuthStrategy = __decorate([
    injectable(),
    __metadata("design:paramtypes", [BarIdpClient,
        _BaasContextManager,
        BaasBootstrapOptions,
        _BaasLogOffUtility])
], BaasIdpHeaderAuthStrategy);

/** @deprecated use BaasConfigurationService. */
let _BaasProfielService = class _BaasProfielService {
    get store() {
        return this.cacheManager.getStore({
            storeType: BarCacheStoreType.User,
            store: 'profiel',
            userId: this.state.basiscontext.userId
        });
    }
    constructor(state, cacheManager, apiClient) {
        this.state = state;
        this.cacheManager = cacheManager;
        this.apiClient = apiClient;
    }
    /**
     * Reads a profiel key and returns an observable.
     * @param key
     */
    read(key) {
        const httpCallback = () => {
            const model = {
                key: key,
                appCode: this.state.toepassing.code,
                appVersion: this.state.toepassing.versie
            };
            // NOTE perform post to avoid query string
            return this.apiClient.read(model);
        };
        return this.store.getOrSetItem(key, httpCallback, {
            session: true
        });
    }
    async readNumber(key) {
        const value = await this.read(key);
        return value != null ? parseFloat(value) : null;
    }
    async readBoolean(key) {
        const value = await this.read(key);
        return value != null ? value.toLowerCase() == 'true' || value == '1' : null;
    }
    /**
     * Flushes the security browser cache.
     */
    flush() {
        return this.store.clear();
    }
};
_BaasProfielService = __decorate([
    injectable(),
    __param(2, inject(BAAS_TYPES.BaasConfigurationApiClient)),
    __metadata("design:paramtypes", [BaasAppState,
        BarCacheManager, Object])
], _BaasProfielService);

/** Default state for GVK */
let BarGvkDefaultState = class BarGvkDefaultState {
    constructor(appState) {
        this.appState = appState;
    }
    get appCode() {
        // in case wired get app state from app state
        return this.appState.toepassing?.code;
    }
    get userId() {
        return this.appState.basiscontext?.userId;
    }
    get apiRootUrl() {
        return this.appState.location?.barWeb?.url;
    }
};
BarGvkDefaultState = __decorate([
    injectable(),
    __metadata("design:paramtypes", [BaasAppState])
], BarGvkDefaultState);

var BarGvkCompatibilityMode;
(function (BarGvkCompatibilityMode) {
    // in version 1 we use Application storage and embed the user id in the key.
    /** Compatible with version 1 of Gebruikersvoorkeuren. */
    BarGvkCompatibilityMode[BarGvkCompatibilityMode["Version_1"] = 0] = "Version_1";
    // in version 2 we use Application User storage where the key is kept proper (by default this is used).
    /** Compatible with version 2 of Gebruikersvoorkeuren. */
    BarGvkCompatibilityMode[BarGvkCompatibilityMode["Version_2"] = 1] = "Version_2";
})(BarGvkCompatibilityMode || (BarGvkCompatibilityMode = {}));

/** GVK storage modus defining where state is persisted. */
var BarGvkStorageMode;
(function (BarGvkStorageMode) {
    /** GVK will use browser storage and remote storage (if accessible) to persist the state. */
    BarGvkStorageMode[BarGvkStorageMode["Default"] = 0] = "Default";
    /** GVK will only use browser storage and no remote storage to persist the state. */
    BarGvkStorageMode[BarGvkStorageMode["BrowserOnly"] = 1] = "BrowserOnly";
    /** GVK will only use remote storage and no browser storage to persist the state. */
    BarGvkStorageMode[BarGvkStorageMode["RemoteOnly"] = 2] = "RemoteOnly";
})(BarGvkStorageMode || (BarGvkStorageMode = {}));

class BarGvkDefaultStore {
    constructor(appCode, userId) {
        this.store = new BarCacheStore(DB_NAME_GVK, `${appCode}_${userId}`);
    }
    async getItem(key) {
        return await this.store.getItem(key);
    }
    async setItem(key, value) {
        // by default we set an item for indefinite time in the cache
        await this.store.setItem(key, value, { session: false });
    }
    async removeItem(key) {
        await this.store.removeItem(key);
    }
}

const DB_NAME_GVK_LEGACY = 'domain.gebruikersvoorkeuren';
class BarGvkLegacyStore {
    constructor(appCode, userId) {
        this.appCode = appCode;
        this.userId = userId;
    }
    getItem(key) {
        const realKey = this.generateRealKey(key);
        const store = this.getStoreInstance();
        return store.getItem(realKey);
    }
    setItem(key, value) {
        const realKey = this.generateRealKey(key);
        const store = this.getStoreInstance();
        return store.setItem(realKey, value);
    }
    removeItem(key) {
        const realKey = this.generateRealKey(key);
        const store = this.getStoreInstance();
        return store.removeItem(realKey);
    }
    generateRealKey(key) {
        // in legacy mode we integrate the user id into the key
        return `_${this.userId}_${key}`;
    }
    getStoreInstance() {
        return new BarStore(DB_NAME_GVK_LEGACY, this.appCode);
    }
}

// NOTE this is based on the original GVK and is not exposed to the outside world
// API voor de 'storage' layer.
// De storage layer maakt gebruikt van een lokale storage (BarStorage) en remote storage (ProfielBeheer met laagje er bovenop).
let BarGvkInternalManager = class BarGvkInternalManager {
    constructor(remoteStoreProvider, bootstrapOptions) {
        this.remoteStoreProvider = remoteStoreProvider;
        this.bootstrapOptions = bootstrapOptions;
        this.disabled = false;
    }
    // Zijn de gebruikersvoorkeuren uitgeschakeld (via een 'master' switch in profielbeheer)?
    // Dit is van belang om voorkeuren te kunnen uitschakelen 'op afstand' indien er issues zijn.
    async checkDisabled(loadRemoteSettings, appCode, userId) {
        if (this.disabled) {
            // if globally disabled we just exit
            return true;
        }
        const uniqueKey = this.getUniqueKeyForSessionStorage(appCode, userId, '_disabled'), localSessionValue = window.sessionStorage.getItem(uniqueKey);
        if (localSessionValue === undefined || localSessionValue === null) {
            if (loadRemoteSettings) {
                const disabled = await this.getRemoteStore(appCode).checkDisabled();
                window.sessionStorage.setItem(uniqueKey, disabled.toString());
                return disabled;
            }
            else {
                sessionStorage.setItem(uniqueKey, 'false');
                return false;
            }
        }
        else
            return localSessionValue === 'true';
    }
    // Gets the gebruikersvoorkeuren.
    async getItem(appCode, userId, setName, storageMode, noSettingsAction, sets, forceGetFromRemoteStorage, compatibilityMode) {
        let value = undefined;
        // Remote storage heeft voorrang op local storage.
        // Let wel: remote storage wordt maar één keer bevraagd (bij start van sessie) en wordt dan ook lokaal bewaard.
        // Bij volgende 'loads' zal de resolve dus steeds een 'undefined' geven, maar dat is geen probleem omdat de settings
        // lokaal werden bewaard.
        if (storageMode !== BarGvkStorageMode.BrowserOnly) {
            const uniqueKey = this.getUniqueKeyForSessionStorage(appCode, userId, setName), localSessionValue = sessionStorage.getItem(uniqueKey);
            if (localSessionValue === undefined || localSessionValue === null || forceGetFromRemoteStorage) {
                // Onthouden dat we het al opgehaald hebben in deze sessie (of althans een poging gedaan hebben).
                // userId moet niet opgegeven want daar zorgt BAR voor (sessie).
                sessionStorage.setItem(uniqueKey, new Date().getTime().toString());
                value = await this.getRemoteStore(appCode).getItem(setName);
                if (value != null) {
                    // Als profielbeheer voorkeuren heeft: lokaal bewaren.
                    await this.setItem(appCode, userId, setName, BarGvkStorageMode.BrowserOnly, value, compatibilityMode);
                }
                else {
                    // Profielbeheer heeft geen voorkeuren (meer): lokaal leegmaken.
                    await this.removeItem(appCode, userId, setName, BarGvkStorageMode.BrowserOnly, compatibilityMode);
                }
            }
        }
        // Indien er remote geen settings zijn of werden opgehaald, dan wordt local storage gecontroleerd.
        if (storageMode !== BarGvkStorageMode.RemoteOnly && !value) {
            value = await this.getBrowserStore(appCode, userId, compatibilityMode).getItem(setName);
        }
        // En indien er remote en/of local niets gevonden werd: dan kunnen de voorkeuren eventueel 'berekend' worden door een callback.
        if (noSettingsAction.action === 'callback' && !value) {
            const ctx = {
                appCode: appCode,
                setName: setName,
                // TODO check concept of sets
                settings: sets != null && sets.get(setName) ? sets.get(setName).settings : this.createEmptySettings(setName)
            };
            const voorkeuren = await noSettingsAction.callback.apply(null, [ctx]);
            if (voorkeuren) {
                // Om gebruikers van deze API enigszins 'flexibiliteit' aan te bieden kunnen zij kiezen om een JSON string terug te geven of
                // een gewoon JavaScript object.
                let voorkeurenObject = null, voorkeurenString = null;
                if (typeof voorkeuren === 'string') {
                    voorkeurenObject = JSON.parse(voorkeuren);
                    voorkeurenString = voorkeuren;
                }
                else if (voorkeuren instanceof String) {
                    voorkeurenObject = JSON.parse(voorkeuren.toString());
                    voorkeurenString = voorkeuren;
                }
                else {
                    voorkeurenObject = voorkeuren;
                    voorkeurenString = JSON.stringify(voorkeuren);
                }
                // Controle op 'well-formedness' van het object.
                // Deze check zou nog doorgedrevener kunnen, maar dit is al goed genoeg.
                if (voorkeurenObject._gvk_setName && voorkeurenObject._gvk_timeStamp) {
                    await this.setItem(appCode, userId, setName, storageMode, voorkeurenString, compatibilityMode);
                    value = voorkeurenString;
                }
            }
        }
        return value;
    }
    // Bewaren van voorkeuren
    async setItem(appCode, userId, setName, storageMode, voorkeuren, compatibilityMode) {
        if (storageMode == BarGvkStorageMode.BrowserOnly) {
            await this.getBrowserStore(appCode, userId, compatibilityMode).setItem(setName, voorkeuren);
        }
        else {
            // TODO take into account localStorageMode === 'off' ??
            const browserStore = this.getBrowserStore(appCode, userId, compatibilityMode);
            await browserStore.setItem(setName, voorkeuren);
            const uniqueKey = this.getUniqueKeyForSessionStorage(appCode, userId, setName);
            // Onthouden dat we het opgestuurd hebben (of althans een poging gedaan hebben).
            sessionStorage.setItem(uniqueKey, new Date().getTime().toString());
            const remoteStore = this.getRemoteStore(appCode);
            await remoteStore.setItem(setName, JSON.parse(voorkeuren));
        }
    }
    // Opkuisen (clearen) van voorkeuren
    async removeItem(appCode, userId, setName, storageMode, compatibilityMode) {
        // clearen gebeurt in de 2 storage systemen. Eventueel zou localStorageMode kunnen uitgelezen worden.
        await this.getBrowserStore(appCode, userId, compatibilityMode).removeItem(setName);
        if (storageMode != BarGvkStorageMode.BrowserOnly) {
            // Sessie opkuisen
            const uniqueKey = this.getUniqueKeyForSessionStorage(appCode, userId, setName);
            if (appCode === null || userId === null || setName === null) {
                let i = 0;
                const keysToRemove = [];
                for (i = 0; i < sessionStorage.length; i++) {
                    if (sessionStorage.key(i).indexOf(uniqueKey) == 0) {
                        keysToRemove.push(sessionStorage.key(i));
                    }
                }
                for (i = 0; i < keysToRemove.length; i++) {
                    sessionStorage.removeItem(keysToRemove[i]);
                }
            }
            else {
                sessionStorage.removeItem(uniqueKey);
            }
            await this.getRemoteStore(appCode).removeItem(setName);
        }
    }
    createEmptySettings(setName) {
        const settings = {
            _gvk_setName: setName,
            _gvk_timeStamp: null // Wordt bij het saven opgevuld.
        };
        return settings;
    }
    // Helper functie voor het genereren van unieke key voor de sessionStorage.
    getUniqueKeyForSessionStorage(appCode, userId, setName) {
        let key = 'domain.gvk';
        if (appCode) {
            key = key + '_' + appCode;
            if (userId) {
                key = key + '_' + userId;
                if (setName) {
                    key = key + '_' + setName;
                }
            }
        }
        return key;
    }
    getBrowserStore(appCode, userId, compatibilityMode) {
        if (this.bootstrapOptions.cacheStorage === 'none') {
            return new BaasNullCacheStore();
        }
        if (compatibilityMode == BarGvkCompatibilityMode.Version_1) {
            return new BarGvkLegacyStore(appCode, userId);
        }
        else {
            return new BarGvkDefaultStore(appCode, userId);
        }
    }
    getRemoteStore(appCode) {
        return this.remoteStoreProvider.provide(appCode);
    }
};
BarGvkInternalManager = __decorate([
    injectable(),
    __param(0, inject(BAAS_TYPES.BarGvkRemoteStoreProvider)),
    __metadata("design:paramtypes", [Object, BaasBootstrapOptions])
], BarGvkInternalManager);

/** BAR user storage service to get and set gebruikersvoorkeuren. */
let BarGvkManager = class BarGvkManager {
    /** Sets GVK as being disabled (globally). */
    set disabled(value) {
        this.storage.disabled = value;
    }
    /** Gets whether GVK is (globally). */
    get disabled() {
        return this.storage.disabled;
    }
    constructor(state, storage, bootstrapOptions) {
        this.state = state;
        this.storage = storage;
        this.bootstrapOptions = bootstrapOptions;
        // TODO [NG20] refactor way settings are defined
        // default settings used
        this.settings = {
            storageMode: BarGvkStorageMode.Default
        };
        if (this.bootstrapOptions.public || this.bootstrapOptions.connectivity === BaasConnectivity.Offline) {
            // NOTE in public or offline modus always use browser only
            this.settings.storageMode = BarGvkStorageMode.BrowserOnly;
        }
    }
    /**
     * Checks whether user storage is disabled for the application/user.
     */
    checkDisabled(appCode = this.state.appCode) {
        return this.storage.checkDisabled(true, appCode, this.state.userId);
    }
    /**
     * Loads an object from the storage.
     * @param key Key from the storage.
     */
    async getItem(key, settings = this.settings) {
        const strData = await this.storage.getItem(settings.appCode || this.state.appCode, this.state.userId, key, settings.storageMode, { action: 'none' }, null, settings.storageMode == BarGvkStorageMode.RemoteOnly, settings.compatibilityMode);
        return strData == null ? null : JSON.parse(strData);
    }
    /**
     * Saves an object to the storage.
     * @param key Key from the storage.
     * @param value The value to store in the storage.
     * @param settings Optional settings for the user storage.
     */
    async setItem(key, value, settings = this.settings) {
        if (value && typeof value !== 'object')
            throw new Error('GVK item value must be a JS object.');
        await this.storage.setItem(settings.appCode || this.state.appCode, this.state.userId, key, settings.storageMode, value == null ? null : JSON.stringify(value), settings.compatibilityMode);
    }
    /**
     * Removes an object from the storage.
     * @param key Key from the storage.
     */
    async removeItem(key, settings = this.settings) {
        await this.storage.removeItem(settings.appCode || this.state.appCode, this.state.userId, key, settings.storageMode, settings.compatibilityMode);
    }
};
BarGvkManager = __decorate([
    injectable(),
    __param(0, inject(_BAAS_TYPES.BarGvkState)),
    __metadata("design:paramtypes", [Object, BarGvkInternalManager,
        BaasBootstrapOptions])
], BarGvkManager);

class BarGvkRemoteStore {
    get urlRoot() {
        // we hebben de root URL nodig waarop we onze calls gaan doen (domain + segment)
        let result = this.state.apiRootUrl || '';
        if (!result.endsWith('/'))
            result += '/';
        // hier maken we een assumptie dat de web api er relatief als volgt eruit ziet
        result += 'api/gvk';
        return result;
    }
    constructor(state, axios, appCode) {
        this.state = state;
        this.axios = axios;
        this.appCode = appCode;
    }
    /** Checks whether the remote storage is disabled. */
    async checkDisabled() {
        try {
            // Opgelet: deze promise zal altijd 'resolven' en nooit rejecten.
            // Bij fouten wordt het zeker voor het onzeker genomen: de voorkeuren zullen niet geladen worden.
            const response = await this.axios.get(`${this.urlRoot}/${this.appCode}/settings`);
            return response.data?.disabled == null ? true : response.data.disabled;
        }
        catch (ex) {
            console.error(ex);
            return true;
        }
    }
    async getItem(key) {
        const response = await this.axios.get(`${this.urlRoot}/${this.appCode}?key=${encodeURIComponent(key)}`);
        return response.data.hasVoorkeuren ? response.data.voorkeuren : undefined;
    }
    async setItem(key, value) {
        try {
            await this.axios.put(`${this.urlRoot}/${this.appCode}?key=${encodeURIComponent(key)}`, value);
        }
        catch (error) {
            console.error('Server side fout bij wegschrijven voorkeuren', error);
        }
    }
    async removeItem(key) {
        // TODO: optimalisatie -> meerdere setNames in één keer doorgeven.
        let optionalSetName = '';
        // userId hoeft niet, want dat regelt BAR (sessie)
        if (key)
            optionalSetName = `?key=${encodeURIComponent(key)}`;
        await this.axios.delete(`${this.urlRoot}/${this.appCode}${optionalSetName}`);
    }
}

let BarGvkDefaultRemoteStoreProvider = class BarGvkDefaultRemoteStoreProvider {
    constructor(state, axios) {
        this.state = state;
        this.axios = axios;
    }
    /** Provides a remote store for the given app code. */
    provide(appCode) {
        return new BarGvkRemoteStore(this.state, this.axios, appCode);
    }
};
BarGvkDefaultRemoteStoreProvider = __decorate([
    injectable(),
    __param(0, inject(_BAAS_TYPES.BarGvkState)),
    __param(1, inject(BAAS_TYPES.AxiosInstance)),
    __param(1, named(BAAS_AXIOS_INSTANCE_TYPES._BaasSilent)),
    __metadata("design:paramtypes", [Object, Function])
], BarGvkDefaultRemoteStoreProvider);

var BarClearStateTarget;
(function (BarClearStateTarget) {
    /// <summary>
    /// Clear all known BaaS/BAR caches.
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["Baas"] = 0] = "Baas";
    /// <summary>
    /// Full cache clear.
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["Full"] = 1] = "Full";
    /// <summary>
    /// Only clear everything related to profiel/configuration.
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["Configuration"] = 2] = "Configuration";
    /// <summary>
    /// Clear everything concerning security (actie allowed).
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["Security"] = 3] = "Security";
    /// <summary>
    /// Clear everything regarding caching & storage (indexed db).
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["CacheStorage"] = 4] = "CacheStorage";
    /// <summary>
    /// Clear all application state in caching & storage (indexed db).
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["CacheStorageApp"] = 5] = "CacheStorageApp";
    /// <summary>
    /// Clear all application/user state in caching & storage (indexed db).
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["CacheStorageAppUser"] = 6] = "CacheStorageAppUser";
    /// <summary>
    /// Clear all user state in caching & storage (indexed db).
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["CacheStorageUser"] = 7] = "CacheStorageUser";
    /// <summary>
    /// Clear all global state in caching & storage (indexed db).
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["CacheStorageGlobal"] = 8] = "CacheStorageGlobal";
    /// <summary>
    /// Clear all state regarding gvk.
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["Gvk"] = 9] = "Gvk";
    /// <summary>
    /// Clear all local storage.
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["LocalStorage"] = 10] = "LocalStorage";
    /// <summary>
    /// Clear all session storage.
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["SessionStorage"] = 11] = "SessionStorage";
    // https://gist.github.com/rmehner/b9a41d9f659c9b1c3340
    /// <summary>
    /// Clear all indexed db.
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["IndexedDb"] = 12] = "IndexedDb";
    // https://stackoverflow.com/questions/179355/clearing-all-cookies-with-javascript
    /// <summary>
    /// Clear all cookies (except what cannot be removed due to security).
    /// </summary>
    BarClearStateTarget[BarClearStateTarget["Cookies"] = 13] = "Cookies";
})(BarClearStateTarget || (BarClearStateTarget = {}));

let BarClearStateHubMessageHandler = class BarClearStateHubMessageHandler {
    constructor() {
        this.action = 'ClearState';
    }
    async handle(target) {
        switch (target) {
            case BarClearStateTarget.Baas: {
                this.#clearCacheStorage();
                this.#clearGvk(false);
                this.#clearStorageByPrefix(window.sessionStorage, 'domain.gvk_');
                this.#clearStorageByPrefix(window.localStorage, 'lvflanders.bar.ng.core');
                this.#clearStorageByPrefix(window.sessionStorage, 'lvflanders.bar.ng.core');
                this.#clearStorageByPrefix(window.localStorage, _BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX);
                this.#clearStorageByPrefix(window.sessionStorage, _BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX);
                break;
            }
            case BarClearStateTarget.Cookies: {
                deleteAllCookies();
                break;
            }
            case BarClearStateTarget.SessionStorage: {
                window.sessionStorage.clear();
                break;
            }
            case BarClearStateTarget.LocalStorage: {
                window.localStorage.clear();
                break;
            }
            case BarClearStateTarget.IndexedDb: {
                deleteAllIndexedDb();
                break;
            }
            case BarClearStateTarget.Full: {
                deleteAllCookies();
                window.sessionStorage.clear();
                window.localStorage.clear();
                deleteAllIndexedDb();
                break;
            }
            case BarClearStateTarget.Gvk: {
                this.#clearGvk();
                break;
            }
            case BarClearStateTarget.CacheStorageGlobal: {
                deleteIndexedDb(DB_NAME_GLOBAL);
                break;
            }
            case BarClearStateTarget.CacheStorageUser: {
                deleteIndexedDb(DB_NAME_USER);
                break;
            }
            case BarClearStateTarget.CacheStorageApp: {
                deleteIndexedDb(DB_NAME_APP);
                break;
            }
            case BarClearStateTarget.CacheStorageAppUser: {
                deleteIndexedDb(DB_NAME_APP_USER);
                break;
            }
            case BarClearStateTarget.CacheStorage: {
                this.#clearCacheStorage();
                break;
            }
            case BarClearStateTarget.Configuration: {
                await this.#clearUserObjectStores('profiel', 'configuration');
                break;
            }
            case BarClearStateTarget.Security: {
                await this.#clearUserObjectStores('actieAllowed');
                break;
            }
        }
    }
    async #clearUserObjectStores(...objectStoreNameSuffixes) {
        const info = await getIndexedDbInfo(DB_NAME_USER);
        const objectStoresToDelete = info.objectStoreNames.filter((storeName) => {
            return objectStoreNameSuffixes.some((suffix) => storeName.endsWith(`_${suffix}`));
        });
        if (objectStoresToDelete.length) {
            await _deleteIndexedDbObjectStores(DB_NAME_USER, info.version + 1, objectStoresToDelete);
        }
    }
    #clearCacheStorage() {
        deleteIndexedDb(DB_NAME_APP);
        deleteIndexedDb(DB_NAME_APP_USER);
        deleteIndexedDb(DB_NAME_USER);
        deleteIndexedDb(DB_NAME_GLOBAL);
        deleteIndexedDb(DB_NAME_GVK);
    }
    #clearGvk(clearNonLegacy = true) {
        deleteIndexedDb(DB_NAME_GVK_LEGACY);
        if (clearNonLegacy)
            deleteIndexedDb(DB_NAME_GVK);
        this.#clearStorageByPrefix(window.sessionStorage, 'domain.gvk_');
    }
    #clearStorageByPrefix(storage, keyPrefix) {
        Object.keys(storage)
            .filter((key) => key.startsWith(keyPrefix))
            .forEach((key) => {
            storage.removeItem(key);
        });
    }
};
BarClearStateHubMessageHandler = __decorate([
    injectable()
    /*** Handles hub message to show a push notification. */
], BarClearStateHubMessageHandler);

let BarCustomHubMessageHandler = class BarCustomHubMessageHandler {
    constructor() {
        this.action = 'CustomMessage';
    }
    handle(data) {
        const customEvent = new CustomEvent(BAAS_CUSTOM_EVENT.CUSTOM_HUB_MESSAGE, {
            detail: data
        });
        // Dispatch the custom event on a JavaScript object or DOM element
        window.dispatchEvent(customEvent);
    }
};
BarCustomHubMessageHandler = __decorate([
    injectable()
], BarCustomHubMessageHandler);

let BarLogOffHubMessageHandler = class BarLogOffHubMessageHandler {
    constructor(auth) {
        this.auth = auth;
        this.action = 'LogOff';
    }
    async handle() {
        await this.auth.logOff();
    }
};
BarLogOffHubMessageHandler = __decorate([
    injectable()
    /*** Handles hub message to show a push notification. */
    ,
    __metadata("design:paramtypes", [BaasAuthService])
], BarLogOffHubMessageHandler);

let BarPushNotificationHubMessageHandler = class BarPushNotificationHubMessageHandler {
    constructor(notificationManager) {
        this.notificationManager = notificationManager;
        this.action = 'PushNotification';
    }
    handle(data) {
        this.notificationManager.show(data);
    }
};
BarPushNotificationHubMessageHandler = __decorate([
    injectable()
    /*** Handles hub message to show a push notification. */
    ,
    __metadata("design:paramtypes", [BarNotificationManager])
], BarPushNotificationHubMessageHandler);

let BaasMeldingService = class BaasMeldingService {
    constructor(apiClient) {
        this.apiClient = apiClient;
    }
    /**
     * Get meldingen for requested url
     */
    getMeldingen(url) {
        return this.apiClient.getMeldingen(url);
    }
    /**
     *Used by Melding Guard to check if a route has a blocking notification, if so it has to redirect to a specific page
     */
    hasBlockingMelding(url) {
        return this.apiClient.hasBlockingMelding(url);
    }
};
BaasMeldingService = __decorate([
    injectable(),
    __param(0, inject(BAAS_TYPES.BaasMeldingApiClient)),
    __metadata("design:paramtypes", [Object])
], BaasMeldingService);

let _BaasTransportHelper = class _BaasTransportHelper {
    #options;
    #appState;
    #baasContextManager;
    constructor(options, appState, baasContextManager) {
        this.#options = options;
        this.#appState = appState;
        this.#baasContextManager = baasContextManager;
    }
    /** Internal method which generates the headers */
    generateAppStateHeaders() {
        const result = {};
        // NOTE only set scope headers in case basiscontext is known already
        if (this.#options.auth.withActingScopeHeaders && this.#appState.basiscontext) {
            result[_BAAS_SHARED_CONSTANTS.HEADER.BAAS_VOORWIE_TOKEN] = JSON.stringify({
                value: this.#appState.basiscontext.voorwie || '',
                rolcode: this.#appState.basiscontext.voorwieRolcode || ''
            });
            result[_BAAS_SHARED_CONSTANTS.HEADER.BAAS_HOEDANIGHEID_TOKEN] = JSON.stringify({
                value: this.#appState.basiscontext.hoedanigheid || '',
                rolcode: this.#appState.basiscontext.hoedanigheidRolcode || ''
            });
        }
        const callStack = [
            {
                userId: this.#appState.basiscontext?.userId,
                appCode: this.#appState.toepassing?.code
            }
        ];
        const headerValue = JSON.stringify(callStack);
        result[_BAAS_SHARED_CONSTANTS.HEADER.BAAS_BROWSER_CALLSTACK] = headerValue;
        result[_BAAS_SHARED_CONSTANTS.HEADER.BAAS_BROWSER_CALLSTACK_V2] = headerValue;
        return encodeHeaders(result);
    }
    /** @internal Generates BaaS headers for interception, only including auth headers when context transport modus is Header. */
    generateBaasInterceptionHeaders() {
        const appStateHeaders = this.generateAppStateHeaders();
        if (this.#options.auth.contextTransportModus === BaasContextTransportModus.Header && this.#baasContextManager.checkBaasContextInStorage()) {
            const stateContext = this.#baasContextManager.getBaasContextFromStorage();
            const authHeaders = generateBaasAuthHeaders(stateContext);
            return { ...authHeaders, ...appStateHeaders };
        }
        return { ...appStateHeaders };
    }
};
_BaasTransportHelper = __decorate([
    injectable()
    /** Internal BaaS transport helper. */
    ,
    __metadata("design:paramtypes", [BaasBootstrapOptions, BaasAppState, _BaasContextManager])
], _BaasTransportHelper);
/** BaaS transport helper. */
let BaasTransportHelper = class BaasTransportHelper {
    #contextService;
    #transportHelper;
    constructor(contextService, transportHelper) {
        this.#contextService = contextService;
        this.#transportHelper = transportHelper;
    }
    /** @experimental Generates all BaaS headers which are also used in BaaS interception. */
    async generateBaasHeaders() {
        const context = await this.#contextService.getStateContext();
        const authHeaders = generateBaasAuthHeaders(context);
        const appStateHeaders = this.#transportHelper.generateAppStateHeaders();
        return { ...authHeaders, ...appStateHeaders };
    }
};
BaasTransportHelper = __decorate([
    injectable(),
    __metadata("design:paramtypes", [BaasContextService, _BaasTransportHelper])
], BaasTransportHelper);

class BaasApmTransaction {
    constructor(transaction) {
        this.transaction = transaction;
        if (!transaction)
            throw new Error('Transaction cannot be null or undefined');
    }
    startSpan(name, type, options) {
        const span = this.transaction.startSpan(name, type, { startTime: options?.startTime });
        return span ? new BaasApmSpan(span) : BaasNoopTelemetrySpan.INSTANCE;
    }
    end() {
        this.transaction.end();
    }
}
/*** No-op Transaction (used when telemetry is disabled) */
class BaasNoopTelemetryTransaction {
    static { this.INSTANCE = new BaasNoopTelemetryTransaction(); }
    startSpan(name, type, options) {
        return BaasNoopTelemetrySpan.INSTANCE;
    }
    end() {
        // No-op
    }
}
/*** No-op Span (used when telemetry is disabled) */
class BaasNoopTelemetrySpan {
    static { this.INSTANCE = new BaasNoopTelemetrySpan(); }
    end() {
        // No-op
    }
    addLabels() {
        // No-op
    }
}
class BaasApmSpan {
    constructor(span) {
        this.span = span;
    }
    end() {
        this.span.end();
    }
    addLabels(labels) {
        this.span.addLabels(labels);
    }
}
/*** BaaS Telemetry service. */
let BaasApmTelemetry = class BaasApmTelemetry {
    constructor(baasApm) {
        this.baasApm = baasApm;
    }
    startTransaction(name, type) {
        if (this.#enabled) {
            const transaction = this.baasApm.startTransaction(name, type);
            return new BaasApmTransaction(transaction);
        }
        return BaasNoopTelemetryTransaction.INSTANCE;
    }
    get currentTransaction() {
        if (this.#enabled) {
            const transaction = this.baasApm.apm?.getCurrentTransaction();
            if (transaction)
                return new BaasApmTransaction(transaction);
        }
        return undefined;
    }
    startSpan(name, type) {
        const span = this.baasApm.apm.startSpan(name, type);
        return span ? new BaasApmSpan(span) : BaasNoopTelemetrySpan.INSTANCE;
    }
    get #enabled() {
        return this.baasApm.active && this.baasApm.apm !== undefined;
    }
    handleError(error) {
        this.baasApm.handleError(error);
    }
};
BaasApmTelemetry = __decorate([
    injectable(),
    __metadata("design:paramtypes", [_BaasApm])
], BaasApmTelemetry);
function _registerTelemetryIoc(container) {
    container.bind(BAAS_TYPES.BaasTelemetry).to(BaasApmTelemetry).inSingletonScope();
}

let BaasToepassingService = class BaasToepassingService {
    constructor(appInfo, state) {
        this.appInfo = appInfo;
        this.state = state;
    }
    /**
     * Sets the voorwie.
     * @param voorwie
     * @param voorwieRolcode
     */
    async setVoorwie(voorwie, voorwieRolcode) {
        // do not allow null because it can cause null to be written in de cookie as a string causing trouble in the neighbourhood
        if (voorwie == null)
            voorwie = '';
        if (voorwieRolcode == null)
            voorwieRolcode = '';
        await this.setBasiscontextData((basiscontext) => {
            basiscontext.voorwie = voorwie;
            basiscontext.voorwieRolcode = voorwieRolcode;
        }, 'Set-Voorwie');
    }
    /**
     * Resets the voorwie.
     */
    async resetVoorwie() {
        await this.setVoorwie('', '');
    }
    /**
     * Sets the hoedanigheid.
     * @param hoedanigheid
     * @param hoedanigheidRolcode
     */
    async setHoedanigheid(hoedanigheid, hoedanigheidRolcode) {
        // do not allow null because it can cause null to be written in de cookie as a string causing trouble in the neighbourhood
        if (hoedanigheid == null)
            hoedanigheid = '';
        if (hoedanigheidRolcode == null)
            hoedanigheidRolcode = '';
        await this.setBasiscontextData((basiscontext) => {
            basiscontext.hoedanigheid = hoedanigheid;
            basiscontext.hoedanigheidRolcode = hoedanigheidRolcode;
        }, 'Set-Hoedanigheid');
    }
    /**
     * Resets the hoedanigheid.
     */
    async resetHoedanigheid() {
        await this.setHoedanigheid('', '');
    }
    async setBasiscontextData(handler, method) {
        const basiscontext = this.state.basiscontext;
        if (basiscontext != null) {
            handler(basiscontext);
            // trick to fill in readonly prop (will give compilation error if property name changes)
            // https://stackoverflow.com/questions/48992522/get-name-of-property-or-field-in-object-typed/48992694?noredirect=1#comment84991472_48992694
            const basiscontextKey = 'basiscontext';
            this.state[basiscontextKey] = basiscontext;
            await this.appInfo.persistBasiscontext({ caller: `Toepassing-${method}` });
            const customEvent = new CustomEvent(BAAS_CUSTOM_EVENT.BASISCONTEXT_CHANGED, {
                detail: basiscontext
            });
            // Dispatch the custom event on a JavaScript object or DOM element
            window.dispatchEvent(customEvent);
        }
    }
};
BaasToepassingService = __decorate([
    injectable(),
    __metadata("design:paramtypes", [_BarAppInfoManager,
        BaasAppState])
], BaasToepassingService);

const ANTI_XSSI_TOKEN = ")]}',";
let BaasInterceptor = class BaasInterceptor {
    // defines if we handle the forbidden status (combined means we check different parts of the afschakelplan)
    get handleAuthFailedStatus() {
        // we handle it if defined globally through configuration and if the user didn't turn it off (if we don't have the configuration yet we assume we want to handle: init call)
        return !this.appState.config || this.appState.config.interception?.handleAuthFailed;
    }
    // defines if we handle the forbidden status (combined means we check different parts of the afschakelplan)
    get handleForbiddenStatus() {
        // we handle it if defined globally through configuration and if the user didn't turn it off
        return this.appState.config?.interception != null && this.appState.config.interception?.handleForbidden;
    }
    constructor(options, appState, appInfo, xdc, oopsDomService, telemetry, transportHelper) {
        this.options = options;
        this.appState = appState;
        this.appInfo = appInfo;
        this.xdc = xdc;
        this.oopsDomService = oopsDomService;
        this.telemetry = telemetry;
        this.transportHelper = transportHelper;
        /** Whether the interception should be silent and not cause a crash. */
        this.silent = false;
    }
    async onRequest(config) {
        const stopwatch = new BarStopwatch();
        stopwatch.start();
        config.metadata = {
            stopwatch: stopwatch // Include the stopwatch instance in metadata
        };
        // determine whether this is a domain request
        const domainRequest = this.checkDomainRequest(config.url);
        const crossDomainRequest = this.checkCrossDomainRequest(config.url);
        if (domainRequest || crossDomainRequest) {
            if (this.appState.basiscontext != null) {
                // before we do the call we make sure the cookie is up to date for the request
                await this.appInfo.persistBasiscontext({
                    caller: 'BaasInterceptor',
                    url: config.url
                });
            }
            else if (crossDomainRequest && this.xdc.enabled) {
                await this.xdc.sync({
                    caller: 'BaasInterceptor',
                    url: config.url
                });
            }
            // needed for localhost cookie sharing
            config.withCredentials = true;
        }
        this.telemetryStart(config);
        this.defineHeaders(config, domainRequest, crossDomainRequest);
        // Modify the request config
        // You can access this.axiosInstance and other injected dependencies here
        return Promise.resolve(config);
    }
    async onResponse(response) {
        this.telemetryEnd(response.config);
        if (response.data && typeof response.data === 'string' && response.data.startsWith(ANTI_XSSI_TOKEN)) {
            // Remove the prefix before parsing the JSON
            const cleanData = response.data.substring(ANTI_XSSI_TOKEN.length);
            response.data = JSON.parse(cleanData);
        }
        const domainRequest = this.checkDomainRequest(response.request.url);
        const crossDomainRequest = this.checkCrossDomainRequest(response.request.url);
        if (crossDomainRequest) {
            await this.xdc.sync({
                caller: 'BaasInterceptor-End',
                url: response.request.url
            });
        }
        if (domainRequest && this.options.auth?.syncBasiscontextAfterRequestCompletion) {
            // get the basiscontext based on cookie and update basiscontext in temp state and page state
            // a request can lead to changed basiscontext
            const basiscontext = this.appInfo.getBasiscontext();
            const basiscontextKey = 'basiscontext';
            this.appState[basiscontextKey] = basiscontext;
        }
        // Modify the response or perform actions
        return Promise.resolve(response);
    }
    async onError(error) {
        this.telemetryEnd(error.config);
        if (!this.silent) {
            // 455 custom code authentication engine or authentication handler (in localhost we also want redirect behavior)
            if (this.handleAuthFailedStatus && error.response?.status == 455) {
                await this.handleAuthFailed();
            }
            // 403 forbidden (e.g. actie allowed failed) handling, in localhost we don't want to get kicked out of application
            else if (this.handleForbiddenStatus && !this.options.localhost && error.response?.status == 403) {
                this.handleForbidden();
            }
        }
        // Handle errors or perform actions
        return Promise.reject(error);
    }
    checkDomainRequest(url) {
        return (!checkUrlAbsolute(url) ||
            url.indexOf('localhost') > -1 ||
            url.startsWith(document.location.origin) ||
            _checkCompatibleOrigin(url, this.options.compatibleOrigins));
    }
    /* check if this is a cross domain request
        rules:
        - xdc must be enabled (localhost, not production...)
        - we must be doing a call to the release domain (which is not localhost)
    */
    checkCrossDomainRequest(url) {
        const result = false;
        if (this.options.localhost) {
            // get the parts of the url being invoked by http
            const invokedUrlParts = extractUrlParts(url);
            // do the check if this is really a cross domain check (http call to origin of cross domain && cross domain is not localhost)
            return invokedUrlParts.origin == this.appState.location.release.origin && this.appState.location.release.hostname != 'localhost';
        }
        return result;
    }
    defineHeaders(config, domainRequest, crossDomainRequest) {
        config.headers = {
            ...config.headers,
            'X-Requested-With': 'XMLHttpRequest'
        };
        let requestToken = null;
        if (domainRequest && !crossDomainRequest && this.appState.localRequestToken != null && this.xdc.enabled) {
            // local request cookie in case domain request && cross domain cookeis are enabled
            requestToken = this.appState.localRequestToken;
        }
        else if (this.appState.requestToken != null) {
            requestToken = this.appState.requestToken;
        }
        if (requestToken) {
            // Add headers to the request configuration
            config.headers = {
                ...config.headers, // Preserve existing headers
                'X-XSRF-TOKEN': requestToken // Add your custom header
            };
        }
        let auditId = null;
        let auditType = null;
        if (this.appState.routeId != null) {
            auditId = this.appState.routeId;
            auditType = BaasAuditType.Route;
        }
        else if (this.appState.pageId != null) {
            auditId = this.appState.pageId;
            auditType = BaasAuditType.Page;
        }
        else if (this.appState.sessionId != null) {
            auditId = this.appState.sessionId;
            auditType = BaasAuditType.Session;
        }
        if (auditId) {
            // set audit id so that audit system can chain everything together
            // Add headers to the request configuration
            config.headers = {
                ...config.headers, // Preserve existing headers
                'X-Audit-Id': auditType // Add your custom header
            };
        }
        this.addBaasHeaders(config);
    }
    /**pass headers from session storage to call */
    addBaasHeaders(config) {
        const baasHeaders = this.transportHelper.generateBaasInterceptionHeaders();
        this.assignHeaders(config, baasHeaders);
    }
    assignHeaders(config, headers) {
        for (const [key, value] of Object.entries(headers)) {
            if (!config.headers[key])
                config.headers[key] = value;
        }
    }
    handleAuthFailed() {
        this.oopsDomService.showOops({ type: _OopsType.Authentication });
        return Promise.resolve();
    }
    handleForbidden() {
        window.location.replace(this.appState.location.baasStatic.url + _BAAS_SHARED_CONSTANTS.PAGE.INSUFFICIENT_PRIVILEGES);
    }
    telemetryStart(config) {
        const name = `Axios HTTP ${config.method} ${config.url}`;
        const telemetry = {};
        if (this.telemetry.currentTransaction) {
            // span op op huidige transactie by default
            telemetry.span = this.telemetry.currentTransaction.startSpan(name, 'axios-http-request');
        }
        else {
            // Start telemetry transaction for the request if we don't have a current transaction
            telemetry.transaction = this.telemetry.startTransaction(name, 'axios-http-request');
            telemetry.span = telemetry.transaction.startSpan(name, 'axios-http-request');
        }
        config.telemetry = telemetry;
    }
    telemetryEnd(config) {
        if (config?.telemetry) {
            const span = config.telemetry.span;
            span?.end();
            if (config.telemetry.transaction) {
                const transaction = config.telemetry.transaction;
                transaction.end();
            }
        }
    }
};
BaasInterceptor = __decorate([
    injectable(),
    __param(5, inject(BAAS_TYPES.BaasTelemetry)),
    __metadata("design:paramtypes", [BaasBootstrapOptions,
        BaasAppState,
        _BarAppInfoManager,
        _BarXdc,
        _OopsDomService, Object, _BaasTransportHelper])
], BaasInterceptor);

/** Internal method which bootstraps a IOC container for a BaaS browser application.*/
function bootstrapBaasIoc(options) {
    const container = new Container();
    // global variable
    baasContext.container = container;
    // bindings
    container.bind(Container).toConstantValue(container);
    container.bind(BaasAppState).toSelf().inSingletonScope();
    container.bind(BaasBrowserCapabilities).toSelf().inSingletonScope();
    container.bind(BaasBootstrap).toSelf().inSingletonScope();
    container.bind(BaasInterceptor).toSelf();
    container.bind(BaasToepassingService).toSelf().inSingletonScope();
    container.bind(BaasBootstrapOptions).toConstantValue(options);
    container.bind(BarRapUrlHelper).toSelf().inSingletonScope();
    container.bind(BaasLoggingService).toSelf().inSingletonScope();
    container.bind(_BaasLogOffUtility).toSelf().inSingletonScope();
    container.bind(BaasSecurityService).toSelf().inSingletonScope();
    container.bind(BaasAuditService).toSelf().inSingletonScope();
    container.bind(BaasMeldingService).toSelf().inSingletonScope();
    container.bind(BaasConfigurationService).toSelf().inSingletonScope();
    container.bind(_BaasProfielService).toSelf().inSingletonScope();
    container.bind(BaasMeetpuntService).toSelf().inSingletonScope();
    container.bind(BarNotificationManager).toSelf().inSingletonScope();
    container.bind(_OopsDomService).toSelf().inSingletonScope();
    container.bind(_BaasApm).toSelf().inSingletonScope();
    container.bind(BaasTransportHelper).toSelf().inSingletonScope();
    container.bind(_BaasTransportHelper).toSelf().inSingletonScope();
    _registerTelemetryIoc(container);
    defineGvk(container);
    defineAuth(container, options.auth);
    defineBackendApi(container, options.backendType);
    defineI18n(container, options.i18n);
    defineAxios(container);
    defineCaching(container, options.appCode);
    defineHubStuff(container);
    return container;
}
function defineGvk(container) {
    container.bind(BarGvkManager).toSelf().inSingletonScope();
    container.bind(BarGvkInternalManager).toSelf().inSingletonScope();
    container.bind(_BAAS_TYPES.BarGvkState).to(BarGvkDefaultState).inSingletonScope();
    container.bind(BAAS_TYPES.BarGvkRemoteStoreProvider).to(BarGvkDefaultRemoteStoreProvider).inSingletonScope();
}
function defineAuth(container, auth) {
    container.bind(BaasContextService).toSelf().inSingletonScope();
    container.bind(_BaasContextManager).toSelf().inSingletonScope();
    container.bind(_BarAppInfoManager).toSelf().inSingletonScope();
    container.bind(_BarXdc).toSelf().inSingletonScope();
    container.bind(BaasExternalAuthStrategy).toSelf().inSingletonScope();
    container.bind(BaasIdpHeaderAuthStrategy).toSelf().inSingletonScope();
    container.bind(BaasIdpAuthStrategy).toSelf().inSingletonScope();
    // NOTE do dynamic value because the context provider is injected after the BaaS bootstrap (deferred execution)
    container
        .bind(BAAS_TYPES.BaasAuthStrategy)
        .toDynamicValue((context) => {
        switch (auth.contextTransportModus) {
            case BaasContextTransportModus.Header: {
                const hasContextProvider = context.get(BAAS_TYPES.BaasContextProvider, { optional: true }) != null;
                if (hasContextProvider) {
                    return context.get(BaasExternalAuthStrategy);
                }
                return context.get(BaasIdpHeaderAuthStrategy);
            }
            default:
            case BaasContextTransportModus.Cookie: {
                return context.get(BaasIdpAuthStrategy);
            }
        }
    })
        .inSingletonScope();
    container.bind(BaasAuthService).toSelf().inSingletonScope();
    const appInfoManager = container.get(_BarAppInfoManager);
    const idpClientOptions = new BarIdpClientOptions();
    if (auth.contextTransportModus === BaasContextTransportModus.Header)
        idpClientOptions.formatToken = 'qs';
    const idpClient = new BarIdpClient(idpClientOptions, appInfoManager);
    container.bind(BarIdpClient).toConstantValue(idpClient);
    container.bind(BAAS_TYPES._BaasLvOicdAuthService).to(BaasOidcAuthService).inSingletonScope();
}
function defineCaching(container, appCode) {
    container.bind(BarCacheManager).toSelf().inSingletonScope();
    container
        .bind(BarCacheStore)
        .toDynamicValue((context) => {
        const cacheManager = context.get(BarCacheManager);
        return cacheManager.getStore({ storeType: BarCacheStoreType.Global });
    })
        .whenTagged(BAAS_METADATA_KEY.CACHE_STORE_TYPE_TAG, BarCacheStoreType.Global);
    if (appCode) {
        container
            .bind(BarCacheStore)
            .toDynamicValue((context) => {
            const cacheManager = context.get(BarCacheManager);
            return cacheManager.getStore({ storeType: BarCacheStoreType.Application, appCode: appCode });
        })
            .whenTagged(BAAS_METADATA_KEY.CACHE_STORE_TYPE_TAG, BarCacheStoreType.Application);
    }
}
function defineBackendApi(container, backendType) {
    container.bind(_BaasStaticApiClient).toSelf().inSingletonScope();
    container.bind(_BaasStaticConfigurationApiClient).toSelf().inSingletonScope();
    container.bind(_BaasWebContextApiClient).toSelf().inSingletonScope();
    switch (backendType) {
        case BaasBackendType.Bar: {
            container.bind(BAAS_TYPES.BaasAuditApiClient).to(BarAuditApiClient);
            container.bind(BAAS_TYPES.BaasLoggingApiClient).to(BarLoggingApiClient);
            container.bind(BAAS_TYPES.BaasMeldingApiClient).to(BarMeldingApiClient);
            container.bind(BAAS_TYPES.BaasSecurityApiClient).to(BarSecurityApiClient);
            container.bind(BAAS_TYPES.BaasConfigurationApiClient).to(BarProfielApiClient);
            container.bind(BAAS_TYPES.BaasMeetpuntApiClient).to(BarMeetpuntApiClient);
            container.bind(BAAS_TYPES.BaasCommonApiClient).to(BarCommonApiClient);
            break;
        }
        case BaasBackendType.Baas: {
            container.bind(BAAS_TYPES.BaasAuditApiClient).to(BaasAuditDefaultApiClient);
            container.bind(BAAS_TYPES.BaasLoggingApiClient).to(BaasLoggingDefaultApiClient);
            container.bind(BAAS_TYPES.BaasMeldingApiClient).to(BaasMeldingDefaultApiClient);
            container.bind(BAAS_TYPES.BaasSecurityApiClient).to(BaasSecurityDefaultApiClient);
            container.bind(BAAS_TYPES.BaasConfigurationApiClient).to(BaasConfigurationDefaultApiClient);
            container.bind(BAAS_TYPES.BaasMeetpuntApiClient).to(BaasMeetpuntDefaultApiClient);
            container.bind(BAAS_TYPES.BaasCommonApiClient).to(BaasCommonDefaultApiClient);
            break;
        }
    }
}
function defineAxios(container) {
    // first create all axios instances which have different behavior
    const baasInstance = axios.create();
    const baasSilentInstance = axios.create();
    const plainInstance = axios.create();
    container.bind(BAAS_TYPES.AxiosInstance).toConstantValue(baasInstance).whenNamed(BAAS_AXIOS_INSTANCE_TYPES.Baas);
    container.bind(BAAS_TYPES.AxiosInstance).toConstantValue(baasSilentInstance).whenNamed(BAAS_AXIOS_INSTANCE_TYPES._BaasSilent);
    container.bind(BAAS_TYPES.AxiosInstance).toConstantValue(plainInstance).whenNamed(BAAS_AXIOS_INSTANCE_TYPES.Plain);
    // main interceptor associated to BaaS
    const interceptor = container.get(BaasInterceptor); // should inject the named Baas instance (see above)
    baasInstance.interceptors.request.use((cfg) => interceptor.onRequest(cfg));
    baasInstance.interceptors.response.use((resp) => interceptor.onResponse(resp), (err) => interceptor.onError(err));
    // new instance of interceptor make it silent (differen behavior)
    const silentInterceptor = container.get(BaasInterceptor);
    silentInterceptor.silent = true;
    baasSilentInstance.interceptors.request.use((cfg) => silentInterceptor.onRequest(cfg));
    baasSilentInstance.interceptors.response.use((resp) => silentInterceptor.onResponse(resp), (err) => silentInterceptor.onError(err));
}
function defineI18n(container, options) {
    const i18n = new BaasI18n(options);
    container.bind(BaasI18n).toConstantValue(i18n);
}
function defineHubStuff(container) {
    container.bind(BarHubClient).toSelf().inSingletonScope();
    container.bind(_BAAS_TYPES.BarHubMessageHandler).to(BarClearStateHubMessageHandler).inSingletonScope();
    container.bind(_BAAS_TYPES.BarHubMessageHandler).to(BarCustomHubMessageHandler).inSingletonScope();
    container.bind(_BAAS_TYPES.BarHubMessageHandler).to(BarLogOffHubMessageHandler).inSingletonScope();
    container.bind(_BAAS_TYPES.BarHubMessageHandler).to(BarPushNotificationHubMessageHandler).inSingletonScope();
}

/** Bootstraps a BaaS browser DOM application. */
async function bootstrapBaasApp(options = {}, 
/** Callback to perform extra IOC injection after the container is generated and before the actual bootstrapping */
iocSetup = undefined) {
    if (!options)
        options = {};
    setDefaults(options);
    const container = bootstrapBaasIoc(options);
    if (iocSetup)
        iocSetup(container);
    const bootstrap = container.get(BaasBootstrap);
    const outcome = await bootstrap.bootstrap();
    return {
        success: outcome === BaasBootstrapOutcome.Initialized,
        outcome: outcome,
        appInstance: { container }
    };
}
function setDefaults(options) {
    if (options.localhost == null)
        options.localhost = checkLocalhost();
    if (!options.connectivity)
        options.connectivity = BaasConnectivity.Online;
    if (!options.backendType)
        options.backendType = BaasBackendType.Baas;
    if (!options.auth) {
        options.auth = new BaasAuthOptions();
    }
    options.auth._originalContextTransportModus = options.auth.contextTransportModus;
    if (!options.auth.contextTransportModus) {
        // in localhost use online headers instead of cookies (avoid third party cookie issues)
        options.auth.contextTransportModus = options.localhost ? BaasContextTransportModus.Header : BaasContextTransportModus.Cookie;
    }
    if (options.auth.syncBasiscontextAfterRequestCompletion === undefined)
        options.auth.syncBasiscontextAfterRequestCompletion = true;
    if (!options.cacheStorage)
        options.cacheStorage = 'idb';
}

/** GVK call settings. */
class BarGvkSettings {
    constructor() {
        /** Defines how to store the state. */
        this.storageMode = BarGvkStorageMode.Default;
        /** Defines the compatibility */
        this.compatibilityMode = BarGvkCompatibilityMode.Version_2;
    }
}

/**
 * Actie object to check. This is needed by the BarActieAllowedGuard.
 */
class BaasActieFromContext {
    constructor(actie) {
        this.includeVoorwie = true;
        this.includeHoedanigheid = true;
        if (actie != null) {
            this.actie = actie.actie;
            this.includeVoorwie = actie.includeVoorwie === undefined ? true : actie.includeVoorwie;
            this.includeHoedanigheid = actie.includeHoedanigheid === undefined ? true : actie.includeHoedanigheid;
        }
    }
}

/*
 * Public API Surface of @lvflanders/bar
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BAAS_AXIOS_INSTANCE_TYPES, BAAS_CUSTOM_EVENT, BAAS_METADATA_KEY, BAAS_TYPES, BaasActie, BaasActieFromContext, BaasAppState, BaasAuditService, BaasAuditType, BaasAuthOptions, BaasAuthService, BaasBackendType, BaasBootstrap, BaasBootstrapOptions, BaasBootstrapOutcome, BaasBrowserCapabilities, BaasConfigurationService, BaasConnectivity, BaasContextService, BaasContextTransportModus, BaasI18n, BaasLogLevel, BaasLoggingService, BaasMeetpuntService, BaasMeldingService, BaasOidcAuthService, BaasSecurityLevel, BaasSecurityService, BaasToepassingService, BaasTransportHelper, BarAuthValues, BarCacheManager, BarCacheStore, BarCacheStoreType, BarGvkCompatibilityMode, BarGvkManager, BarGvkSettings, BarGvkStorageMode, BarHubClient, BarIdentity, BarIdentityType, BarIdpClient, BarIdpClientOptions, BarKlant, BarNotificationManager, BarStopwatch, BarStore, DB_NAME_APP, DB_NAME_APP_USER, DB_NAME_GLOBAL, DB_NAME_GVK, DB_NAME_USER, LOCALE, _BAAS_SHARED_CONSTANTS, _BaasApm, _BaasContextManager, _BaasProfielService, _BaasStaticConfigurationApiClient, _BaasTransportHelper, _BarAppInfoManager, _BarXdc, _OopsDomService, _OopsType, _addAuditInfoOnDocument, _checkCompatibleOrigin, _getDefaultCacheEntryOptions, asciiEncode, baasContext, bootstrapBaasApp, checkDateObject, checkHasParentDomElement, checkLocalhost, checkSsl, checkUrlAbsolute, clearQueryStringParams, concatUrl, convertToISOString, deepClone, deleteAllCookies, deleteAllIndexedDb, deleteCookie, encodeHeaders, ensureGlobal, extractUrlOrigin, extractUrlParts, generateBaasAuthHeaders, generateRandomNumber, generateUuid, getParamNames, getStack, getStorageAccess, openUrl, parseQueryStringValue, readCookie, resolveUrlFromOrigin, resolveValue, versionCompare };
//# sourceMappingURL=lvflanders-bar.mjs.map
